import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { DxDropDownBoxModule } from 'devextreme-angular';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTextAreaComponent, DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTreeViewModule } from 'devextreme-angular/ui/tree-view';
import notify from 'devextreme/ui/notify';
import { FormsModule } from '@angular/forms';
import { map, catchError, of, forkJoin, from, Subject, debounceTime, finalize, switchMap, takeUntil, filter, firstValueFrom, Observable } from 'rxjs';
import Swal from 'sweetalert2';

import { SourceSchemaPanelComponent } from './components/source-schema-panel/source-schema-panel.component';
import { MappingRulesGridComponent } from './components/mapping-rules-grid/mapping-rules-grid.component';
import { MappingPreviewPanelComponent } from './components/mapping-preview-panel/mapping-preview-panel.component';
import { ExpressionEditorPopupComponent } from './components/expression-editor-popup/expression-editor-popup.component';
import { LookupEditorPopupComponent } from './components/lookup-editor-popup/lookup-editor-popup.component';

import { LinkGenericMapperApiService } from './services/link-generic-mapper-api.service';
import { EXPRESSION_TOOLS } from './models/expression-tools';
import {
  ChildContextBinding,
  ChildContextBindingMode,
  ChildMappingDefinition,
  ExpressionSuggestionItem,
  ExpressionTool,
  ExpressionToolCategory,
  FieldOption,
	GetSamplePayloadResponse,
	ManualLookupConfiguration,
	MapperSavedRunComparisonItem,
	MapperSavedRunDiffEntry,
	MapperExpressionFunctionDefinition,
	MapperTestRunArtifact,
	MapperTestRunResult,
	SavedMapperTestRun,
  MappingDefinition,
  MappingEntityDescriptor,
  MappingEntitySchema,
  MappingFieldSchema,
  MappingGridRow,
	MappingPreviewResponse,
  MappingRule,
	MappingRuleNormalization,
	MappingRuleMode,
	MapperRuntimeMessageTemplates,
	MappingWriteback,
	MappingWritebackMode,
  MappingValueMapConfiguration,
  MapperSampleInputDefinition,
  MapperProfileSummary,
  MappingSchemaConfiguration,
  PicklistMappingConfiguration,
  TreeNode
} from './models/link-generic-mapper.models';
import { PicklistValueMappingPopupComponent } from './components/picklist-mapper/picklist-value-mapping-popup.component';
import { MappingDebugPanelComponent } from './components/mapping-debug-panel/mapping-debug-panel.component';
import { QueueService } from '../../services/queue.service';
import { LinkStatusSignalrService } from '../../services/signalR/link-status-signalr.service';
import {
	createMapperGuideDemo,
	createMapperGuideLookup,
	createMapperGuideRowById,
	getMapperGuideLookupReferenceFieldOptions as resolveMapperGuideLookupReferenceFieldOptions,
	getMapperGuideModeDescription,
	getMapperGuideRowState,
	getMapperGuideSampleExpression,
	getMapperGuideSourceValue,
	isMapperGuideRowUnmapped,
	MapperGuideCaseDetail,
	resolveMapperGuideExpressionPreview,
	resolveMapperGuidePreviewValue
} from './link-generic-mapper-guide-demo';
import { prioritizeAndLimitArtifactTraceRows } from './artifact-trace.utils';
import {
	MapperArtifactTraceComponent,
	MapperArtifactTraceItem,
	MapperArtifactTracePanel,
	MapperArtifactTraceRow,
	MapperArtifactTraceStage,
	MapperArtifactTraceTone
} from './components/artifact-trace/mapper-artifact-trace.component';

type ToolbarFilter = 'all' | 'required' | 'mapped' | 'unmapped' | 'issues' | 'ignored';
type MapperTab = 'create' | 'update' | 'writeback' | 'messages';
type RuleTab = 'create' | 'update';
type MapperScreenMode = 'basic' | 'advanced';
type BasicTemplateMiniGridTab = 'create' | 'update' | 'writeback' | 'messages';

interface BasicTemplateActionRow {
	target: string;
	targetPath: string;
	value: string;
	mode: string;
	note: string;
	isLinkKey: boolean;
}

interface BasicTemplateSection {
	title: string;
	description: string;
	rows: BasicTemplateActionRow[];
	emptyText: string;
}

interface BasicTemplateChildRow {
	name: string;
	source: string;
	target: string;
	when: string;
	mappedFieldCount: number;
}

interface BasicTemplatePlainStep {
	title: string;
	description: string;
	tone: 'success' | 'info' | 'warning' | 'muted';
}

interface BasicTemplatePlainFieldRow {
	source: string;
	target: string;
	note: string;
	isLinkKey: boolean;
}

interface BasicTemplateMiniGridRow {
	left: string;
	middle: string;
	right: string;
	isLinkKey: boolean;
}

interface BasicTemplateMiniGridSection {
	id: BasicTemplateMiniGridTab;
	title: string;
	count: number;
	emptyText: string;
	rows: BasicTemplateMiniGridRow[];
}

interface BasicPreviewValueRow {
	label: string;
	value: string;
	tone: 'success' | 'warning' | 'danger' | 'muted';
}

interface BasicPreviewDataRouteRow {
	sourceLabel: string;
	sourceValue: string;
	ruleLabel: string;
	ruleDetail: string;
	targetLabel: string;
	targetValue: string;
	tone: 'success' | 'warning' | 'danger' | 'muted';
	isLinkKey: boolean;
}

type MappingNodeKind = 'root' | 'child';

interface MapperEditorNode {
  id: string;
  mappingId?: string | null;
  kind: MappingNodeKind;
  parentId: string | null;
  ownerTargetFieldId?: string | null;
  ownerTargetPath?: string | null;
  inlineChildObject?: boolean;
  name: string;
  sourceEntity: string;
  targetEntity: string;
  sourceSchema: MappingEntitySchema;
  targetSchema: MappingEntitySchema;
  sourceFieldOptions: FieldOption[];
  sourceTreeItems: TreeNode[];
  targetTreeItems: TreeNode[];
  createMappingRows: MappingGridRow[];
  updateMappingRows: MappingGridRow[];
  filteredCreateMappingRows: MappingGridRow[];
  filteredUpdateMappingRows: MappingGridRow[];
  runtimeMessageTemplates: MapperRuntimeMessageTemplates;
  sourceIdPath: string;
  sourceCollectionPath: string;
  targetRuntimeRoute: string;
  contextBindings: ChildContextBinding[];
  continueOnError: boolean;
  executionMode: string;
  children: MapperEditorNode[];
}

interface MappingObjectTabItem {
  id: string;
  label: string;
  description: string;
  nodeId: string;
  targetFieldId?: string | null;
  kind: MappingNodeKind;
}

interface CopyMappingPreviewItem {
	field: string;
	before: string;
	after: string;
}

interface SaveMappingReviewItem {
	mapping: string;
	tab: string;
	field: string;
	before: string;
	after: string;
}

interface ChildPreviewParentContext {
	sourceRecord: any;
	payload: any;
	targetRecord: any;
}

interface PreviewRequestContext {
	row: MappingGridRow | null;
	tab: RuleTab;
	node: MapperEditorNode | null;
}

interface ExpressionTestUsedValue {
	path: string;
	label: string;
	value: string;
}

interface MapperArtifactTraceRule {
	traceId: string;
	targetPath: string;
	sourcePath: string | null;
	label: string;
	description: string;
	tone: MapperArtifactTraceTone;
}

interface MapperArtifactTraceIndex {
	sourcePathTraceIds: Map<string, string>;
	targetPathTraceIds: Map<string, string>;
	writebackSourcePathTraceIds: Map<string, string>;
	traceLabels: Map<string, string>;
	traceDescriptions: Map<string, string>;
	traceTones: Map<string, MapperArtifactTraceTone>;
	rules: MapperArtifactTraceRule[];
}

@Component({
  selector: 'app-link-generic-mapper',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DxDataGridModule,
    DxDropDownBoxModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxTreeViewModule,
    SourceSchemaPanelComponent,
    MappingRulesGridComponent,
    MappingPreviewPanelComponent,
    ExpressionEditorPopupComponent,
    LookupEditorPopupComponent,
    PicklistValueMappingPopupComponent,
    MappingDebugPanelComponent,
		MapperArtifactTraceComponent
  ],
  templateUrl: './link-generic-mapper.component.html'
})
export class LinkGenericMapperComponent implements OnInit, OnChanges, OnDestroy {
	private static readonly metadataCachingStorageKey = 'al3.link-generic-mapper.metadata-caching-enabled';
	private static readonly screenModeStorageKey = 'al3.link-generic-mapper.screen-mode';
	private static readonly artifactCaptureStorageKey = 'al3.link-generic-mapper.artifact-capture-enabled';
	private static readonly showUnmappedArtifactFieldsStorageKey = 'al3.link-generic-mapper.show-unmapped-artifact-fields';
	private static readonly legacyArtifactCaptureSessionStorageKey = 'al3.link-generic-mapper.artifact-capture-enabled';
	private static readonly savedBaselineStorageKeyPrefix = 'al3.link-generic-mapper.saved-baseline';
	private static readonly sampleInputStorageKeyPrefix = 'al3.link-generic-mapper.sample-inputs';
	private static readonly childPreviewRecordLimit = 5;
	readonly mapperTreeDropDownOptions = {
		width: 520,
		maxWidth: 'calc(100vw - 24px)',
		maxHeight: 'calc(100vh - 48px)',
		wrapperAttr: { class: 'mapper-tree-dropdown-overlay' }
	};
	readonly writebackTargetFieldDisplayExpr = (field: FieldOption | null): string => {
		if (!field) {
			return '';
		}

		return `${this.getWritebackTargetFieldTitle(field)} [${field.path}]`;
	};
	readonly mapperRunStageFlow = [
		{ key: 'Queued', label: 'Queued' },
		{ key: 'Initializing', label: 'Prepare' },
		{ key: 'SourceLoaded', label: 'Load source' },
		{ key: 'Validating', label: 'Validate' },
		{ key: 'Upserting', label: 'Upsert' },
		{ key: 'Writeback', label: 'Writeback' },
		{ key: 'Completed', label: 'Finished' }
	] as const;
	readonly mapperGuideDemo = createMapperGuideDemo();
	readonly mapperGuideSourceSystemName = this.mapperGuideDemo.sourceSystemName;
	readonly mapperGuideTargetSystemName = this.mapperGuideDemo.targetSystemName;
	readonly mapperGuideSourceFieldOptions = this.mapperGuideDemo.sourceFieldOptions;
	readonly mapperGuideSourceTreeItems = this.mapperGuideDemo.sourceTreeItems;
	readonly mapperGuideTargetSchema = this.mapperGuideDemo.targetSchema;
	readonly mapperGuideTargetTreeItems = this.mapperGuideDemo.targetTreeItems;
	readonly mapperGuideRows = this.mapperGuideDemo.rows;
	readonly mapperGuideSections = this.mapperGuideDemo.sections;
	readonly mapperGuideCaseDetails = this.mapperGuideDemo.caseDetails;
	readonly mapperGuideMarkerLegend = this.mapperGuideDemo.markerLegend;
	readonly mapperGuideReferenceFieldOptionsByEntity = this.mapperGuideDemo.referenceFieldOptionsByEntity;
	readonly resolveMapperGuideRowState = (row: MappingGridRow): 'issue' | 'ready' | 'ignored' | 'readonly' | 'pending' | 'loading' => getMapperGuideRowState(row);
	readonly resolveMapperGuideModeDescription = (row: MappingGridRow): string => getMapperGuideModeDescription(row);
	readonly resolveMappingRowState = (row: MappingGridRow): 'issue' | 'ready' | 'ignored' | 'readonly' | 'pending' | 'loading' => this.getRowState(row);
	readonly resolveMappingModeDescription = (row: MappingGridRow): string => this.getModeDescription(row);
	readonly writebackModeOptions: Array<{ value: MappingWritebackMode; label: string }> = [
		{ value: 'TargetValue', label: 'Returned target value' },
		{ value: 'CurrentDate', label: 'Current date' },
		{ value: 'Expression', label: 'Expression' }
	];

	constructor(
    private readonly api: LinkGenericMapperApiService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly queueService: QueueService,
		private readonly linkStatusService: LinkStatusSignalrService
  ) { }

	@Input() linkId: number | null = null;
	@Input() companyId: number | null = null;
	@Input() linkTypeId: number | null = null;

  @ViewChild(DxTextAreaComponent) expressionEditor?: DxTextAreaComponent;

  referenceFieldOptionsByEntity: Record<string, FieldOption[]> = {};
  availableSourceEntities: MappingEntityDescriptor[] = [];
  availableTargetEntities: MappingEntityDescriptor[] = [];
  rootMappingNode: MapperEditorNode | null = null;
  selectedMappingNodeId: string | null = null;
  private mappingNodeIdSequence = 0;

	expressionFieldSearch = '';
	expressionSourceTreeItems: TreeNode[] = [];
	expressionTargetFieldSearch = '';
	expressionTargetTreeItems: TreeNode[] = [];

  sourceEntity: string | null = null;
  targetEntity: string | null = null;

  activeToolbarFilter: ToolbarFilter = 'all';
  activeMapperTab: MapperTab = 'create';
	activeWritebackRuleSet: RuleTab = 'create';
	mapperScreenMode: MapperScreenMode = 'advanced';

  configuration: MappingSchemaConfiguration | null = null;
	createWritebacks: MappingWriteback[] = [];
	updateWritebacks: MappingWriteback[] = [];
	get writebacks(): MappingWriteback[] {
		return this.activeWritebackRuleSet === 'create' ? this.createWritebacks : this.updateWritebacks;
	}
	private set writebacks(value: MappingWriteback[]) {
		if (this.activeWritebackRuleSet === 'create') {
			this.createWritebacks = value;
		} else {
			this.updateWritebacks = value;
		}
		this.refreshSaveMappingReviewState();
	}
  writebackSourceTreeItemsByIndex: Record<number, TreeNode[]> = {};
  writebackTargetTreeItemsByIndex: Record<number, TreeNode[]> = {};
  writebackSourceFieldSearchByIndex: Record<number, string> = {};
  writebackTargetFieldSearchByIndex: Record<number, string> = {};
  writebackSourceDropdownOpenedByIndex: Record<number, boolean> = {};
  writebackTargetDropdownOpenedByIndex: Record<number, boolean> = {};
  sourceIdPathTreeItems: TreeNode[] = [];
  sourceIdPathFieldSearch = '';
  sourceIdPathDropdownOpened = false;
  messageVariableSourceSearch = '';
  messageVariableTargetSearch = '';
  messageVariableSourceDropdownOpened = false;
  messageVariableTargetDropdownOpened = false;

  previewJson = '{}';
  previewJsonObject: any = {};
  validationSummary: Array<{ field: string; ok: boolean; message: string }> = [];
  lastActionMessage = '';
	mapperLoadErrorTitle = '';
	mapperLoadErrorMessage = '';

  sourceFieldSearch = '';
  mappingRowSearch = '';
  showOnlyRequired = false;
  showOnlyIssues = false;
  showOnlyUnmapped = false;
	hideReadOnlyRows = false;

  expressionPopupVisible = false;
  expressionDraft = '';
  expressionEditingTargetFieldId: string | null = null;
  writebackExpressionEditIndex: number | null = null;
  writebackExpressionEditTarget: 'value' | 'condition' | null = null;
  expressionTipsExpanded = false;
	expressionTestLoading = false;
	expressionTestRan = false;
	expressionTestIsValid: boolean | null = null;
	expressionTestHasResult = false;
	expressionTestResult: any = null;
	expressionTestMessage: string | null = null;
	expressionTestUsedValues: ExpressionTestUsedValue[] = [];

  expressionToolSearch = '';
  selectedExpressionToolCategory: ExpressionToolCategory | 'All' = 'Favorites';
  readonly expressionToolCategories: Array<ExpressionToolCategory | 'All'> = [
    'All',
    'Favorites',
    'Logical',
    'Comparison',
    'NullConditional',
    'String',
    'Numeric',
    'DateTime',
    'CollectionAggregate',
    'Custom',
    'Advanced'
  ];

  expressionSuggestionsVisible = false;
  expressionSuggestions: ExpressionSuggestionItem[] = [];
  expressionSuggestionIndex = 0;
	selectedExpressionSourceField: FieldOption | null = null;
	readonly runtimeMessageTemplateExamples: Array<{ key: keyof MapperRuntimeMessageTemplates; label: string; description: string; placeholder: string }> = [
		{
			key: 'createSuccess',
			label: 'Create success message',
			description: 'Used when a new target record is created successfully.',
			placeholder: 'Created {{target.Code}} - {{target.Name}} from {{meta.sourceId}}'
		},
		{
			key: 'updateSuccess',
			label: 'Update success message',
			description: 'Used when an existing target record is updated successfully.',
			placeholder: 'Updated {{target.Code}} - {{target.Name}} using {{linkKeyValue}}'
		},
		{
			key: 'validationFailure',
			label: 'Validation failure message',
			description: 'Used when a mapped field fails validation before upsert.',
			placeholder: 'Validation failed for {{meta.sourceId}} on {{fieldName}}: {{fieldMessage}}'
		},
		{
			key: 'upsertFailure',
			label: 'Upsert failure message',
			description: 'Used when the target create or update request fails.',
			placeholder: 'Failed to sync {{meta.sourceId}}: {{errorMessage}}'
		},
		{
			key: 'writebackFailure',
			label: 'Writeback failure message',
			description: 'Used when writing the result back to the source record fails.',
			placeholder: 'Writeback failed for {{meta.sourceId}}: {{errorMessage}}'
		}
	];
	readonly runtimeMessageMetaTokenExamples: string[] = [
		'{{meta.sourceId}}',
		'{{meta.profileName}}',
		'{{meta.sourceEntity}}',
		'{{meta.targetEntity}}',
		'{{meta.action}}',
		'{{errorMessage}}',
		'{{fieldName}}',
		'{{fieldMessage}}',
		'{{linkKeyValue}}'
	];
	runtimeMessageSourcePropertyExamples: string[] = [];
	runtimeMessageTargetPropertyExamples: string[] = [];
	runtimeMessageSourceTreeItems: TreeNode[] = [];
	runtimeMessageTargetTreeItems: TreeNode[] = [];

  expressionTools = EXPRESSION_TOOLS;

  private expressionSelectionStart = 0;
  private expressionSelectionEnd = 0;
  private expressionTextAreaElement: HTMLTextAreaElement | null = null;
  private readonly previewRequested$ = new Subject<PreviewRequestContext>();
  isPreviewLoading = false;
  private resolvingRowIds = new Set<string>();

  lookupPopupVisible = false;
  lookupEditingRow: MappingGridRow | null = null;
	lookupEditingDraftRow: MappingGridRow | null = null;
	lookupTestLoading = false;
	lookupTestRan = false;
	lookupTestValid = false;
	lookupTestHasResult = false;
	lookupTestResult: unknown = null;
	lookupTestMessage = '';

  lookupExpressionCriterionIndex: number | null = null;
  lookupExpressionDraft = '';

  picklistMappingPopupVisible = false;
  picklistMappingRow: MappingGridRow | null = null;
  picklistMappingSourceField: FieldOption | null = null;
	picklistMappingDraft: PicklistMappingConfiguration | null = null;

  previewResponseJson: unknown = {};
  previewSourceJson: unknown = {};
	previewResultJson: unknown = {};
	sampleInputValues: Record<string, string> = {};
	rememberSampleInputValues = false;
	sampleOptionsExpanded = false;
	targetSchemaEnrichmentInputValues: Record<string, string> = {};
  transferByIdDraftValue = '';
  isSamplePayloadLoading = false;
  samplePayloadLoadingPhase: 'sample' | 'preview' | null = null;
  isTargetSchemaEnrichmentLoading = false;
  isApplyingTransferByIdSetting = false;
  testRunMenuOpen = false;
  adminRunMenuOpen = false;
  profileName = '';
	selectedProfileName = '';
	templateName = '';
	selectedTemplateName = '';
	templateSearch = '';
	selectedTemplateMiniGridTab: BasicTemplateMiniGridTab = 'create';
	selectedTemplateDefinition: MappingDefinition | null = null;
	isLoadingSelectedTemplateDefinition = false;
	selectedTemplateDefinitionError = '';
	private savedProfileRowSignaturesValue: Map<string, string> | null = null;
	private hasPendingMappingChangesValue = false;
	private get savedProfileRowSignatures(): Map<string, string> | null {
		return this.savedProfileRowSignaturesValue;
	}
	private set savedProfileRowSignatures(value: Map<string, string> | null) {
		this.savedProfileRowSignaturesValue = value;
		this.refreshSaveMappingReviewState();
	}
	profileMenuOpen = false;
  metadataCachingEnabled = false;
  isPageLoading = false;
  pageLoadingMessage = 'Loading mapper...';
  pageLoadingDetail = 'Preparing the mapper workspace.';
  isTestRunStarting = false;
  isLinkRunning = false;
	hasRecentTestRun = false;
	testRunStatusText = 'No mapper test run started from this screen yet.';
	captureArtifactsForSession = false;
	showUnmappedArtifactFields = true;
	mapperDocumentationOpen = false;
	selectedMapperGuideRowId = 'guide-company';
	selectedMapperGuideRows: MappingGridRow[] = [];
	guideExpressionEditingRow: MappingGridRow | null = null;
	guideLookupEditorActive = false;
	guidePicklistMappingActive = false;
	expressionEditorUsesGuideFields = false;
	activeTestRunSessionId = '';
	private latestTestRunResultValue: MapperTestRunResult | null = null;
	artifactTraceItems: MapperArtifactTraceItem[] = [];
	get latestTestRunResult(): MapperTestRunResult | null {
		return this.latestTestRunResultValue;
	}
	set latestTestRunResult(value: MapperTestRunResult | null) {
		this.latestTestRunResultValue = value;
		this.rebuildArtifactTraceItems();
	}
	private savedBaselineTestRunValue: SavedMapperTestRun | null = null;
	get savedBaselineTestRun(): SavedMapperTestRun | null {
		return this.savedBaselineTestRunValue;
	}
	set savedBaselineTestRun(value: SavedMapperTestRun | null) {
		this.savedBaselineTestRunValue = value;
		this.refreshSavedRunComparisonState();
	}
	comparisonSessionId = '';
	private hasExplicitSavedRunComparisonValue = false;
	get hasExplicitSavedRunComparison(): boolean {
		return this.hasExplicitSavedRunComparisonValue;
	}
	set hasExplicitSavedRunComparison(value: boolean) {
		this.hasExplicitSavedRunComparisonValue = value;
		this.refreshSavedRunComparisonState();
	}
	isLoadingComparisonRun = false;
	private comparisonRunResultValue: MapperTestRunResult | null = null;
	get comparisonRunResult(): MapperTestRunResult | null {
		return this.comparisonRunResultValue;
	}
	set comparisonRunResult(value: MapperTestRunResult | null) {
		this.comparisonRunResultValue = value;
		this.refreshSavedRunComparisonState();
	}
	savedRunComparisonLog: MapperSavedRunComparisonItem[] = [];
  isLoadingLatestTestRunResult = false;
  mapperRunProgressStage = '';
  mapperRunProgressProcessedCount = 0;
  mapperRunProgressTotalCount = 0;
  mapperRunProgressMessage = '';
  private isInitialLoadSequence = false;
  private readonly destroy$ = new Subject<void>();
	private readonly deferredFieldLoadStates = new WeakMap<MappingFieldSchema, { state: 'loading' | 'loaded' | 'error'; error?: string | null }>();
	private readonly deferredFieldLoadPromises = new WeakMap<MappingFieldSchema, Promise<void>>();

  rawSourceJson: any = {};
  resultJson: any = {};
  validationMessages: string[] = [];

  selectedRow: MappingGridRow | null = null;
  selectedSourcePath?: string;
  selectedTargetPath?: string;
  private initializedLinkId: number | null = null;
  private loadedSamplePayloadInputSignature: string | null = null;

  private get samplePayloadRetrieved(): boolean {
    return this.configuration?.samplePayloadRetrieved === true;
  }

  get selectedMappingNode(): MapperEditorNode | null {
    if (!this.rootMappingNode) {
      return null;
    }

    if (!this.selectedMappingNodeId) {
      return this.rootMappingNode;
    }

    return this.findNodeById(this.rootMappingNode, this.selectedMappingNodeId) ?? this.rootMappingNode;
  }

  get mappingObjectTabs(): MappingObjectTabItem[] {
    const rootNode = this.rootMappingNode;
    if (!rootNode) {
      return [];
    }

    const items: MappingObjectTabItem[] = [
      {
        id: rootNode.id,
        label: rootNode.targetEntity,
        description: `${rootNode.sourceEntity} -> ${rootNode.targetEntity}`,
        nodeId: rootNode.id,
        targetFieldId: null,
        kind: 'root'
      }
    ];

    this.appendChildTabs(rootNode, items);
    return items;
  }

	get showMappingObjectTabs(): boolean {
		return this.mappingObjectTabs.length > 1;
	}

  get sourceFieldOptions(): FieldOption[] {
		return this.selectedMappingNode?.sourceFieldOptions ?? [];
  }

  get sourceTreeItems(): TreeNode[] {
		return this.selectedMappingNode?.sourceTreeItems ?? [];
  }

  get targetTreeItems(): TreeNode[] {
    return this.selectedMappingNode?.targetTreeItems ?? [];
  }

  get createMappingRows(): MappingGridRow[] {
    return this.selectedMappingNode?.createMappingRows ?? [];
  }

  set createMappingRows(value: MappingGridRow[]) {
    if (this.selectedMappingNode) {
      this.selectedMappingNode.createMappingRows = value;
    }
  }

  get updateMappingRows(): MappingGridRow[] {
    return this.selectedMappingNode?.updateMappingRows ?? [];
  }

  set updateMappingRows(value: MappingGridRow[]) {
    if (this.selectedMappingNode) {
      this.selectedMappingNode.updateMappingRows = value;
    }
  }

  get filteredCreateMappingRows(): MappingGridRow[] {
    return this.selectedMappingNode?.filteredCreateMappingRows ?? [];
  }

  set filteredCreateMappingRows(value: MappingGridRow[]) {
    if (this.selectedMappingNode) {
      this.selectedMappingNode.filteredCreateMappingRows = value;
    }
  }

  get filteredUpdateMappingRows(): MappingGridRow[] {
    return this.selectedMappingNode?.filteredUpdateMappingRows ?? [];
  }

  set filteredUpdateMappingRows(value: MappingGridRow[]) {
    if (this.selectedMappingNode) {
      this.selectedMappingNode.filteredUpdateMappingRows = value;
    }
  }

  get runtimeMessageTemplates(): MapperRuntimeMessageTemplates {
    return this.selectedMappingNode?.runtimeMessageTemplates ?? {};
  }

  set runtimeMessageTemplates(value: MapperRuntimeMessageTemplates) {
    if (this.selectedMappingNode) {
      this.selectedMappingNode.runtimeMessageTemplates = value;
			this.refreshSaveMappingReviewState();
    }
  }

	setRuntimeMessageTemplateFromEvent(event: Event): void {
		const detail = (event as CustomEvent<{ key?: string; value?: string | null }>).detail;
		const key = detail?.key?.trim();
		if (!key) {
			return;
		}

		this.runtimeMessageTemplates = {
			...this.runtimeMessageTemplates,
			[key]: detail?.value?.trim() || null
		};
	}

	onRuntimeMessageTemplateChanged(key: string, value: string | null | undefined): void {
		this.runtimeMessageTemplates = {
			...this.runtimeMessageTemplates,
			[key]: value || null
		};
	}

  private get hasSamplePayloadAttempt(): boolean {
    return !!this.loadedSamplePayloadInputSignature;
  }

	get pageLoadingProgressPercent(): number {
		switch (this.pageLoadingMessage) {
			case 'Loading mapper entities...':
				return 15;
			case 'Loading mapper configuration...':
			case 'Preparing field mapping workspace...':
				return 45;
			case 'Loading profile...':
				return 70;
			case 'Generating preview...':
				return 90;
			default:
				return 10;
		}
	}

	private stopInitialLoadSequence(): void {
		this.isInitialLoadSequence = false;
	}

  get sampleInputsComplete(): boolean {
    return !this.hasRequiredSampleInputs();
  }

	get showBasicMapperScreen(): boolean {
		return this.mapperScreenMode === 'basic';
	}

	get showAdvancedMapperScreen(): boolean {
		return this.mapperScreenMode === 'advanced';
	}

	setMapperScreenMode(mode: MapperScreenMode): void {
		this.mapperScreenMode = mode;
		this.writeMapperScreenModePreference(mode);
		this.profileMenuOpen = false;
		this.testRunMenuOpen = false;
		this.closeAdminRunMenu();
	}

  ngOnInit(): void {
    this.metadataCachingEnabled = this.readMetadataCachingPreference();
		this.captureArtifactsForSession = this.readArtifactCaptureSessionPreference();
		this.showUnmappedArtifactFields = this.readShowUnmappedArtifactFieldsPreference();
		this.selectedMapperGuideRows = [this.selectedMapperGuideRow];
		this.initializeLinkRunStatusTracking();

		this.previewRequested$
			.pipe(
				debounceTime(250),
				switchMap(context => {
					this.beginPreviewFeedback(context.row);
					return this.runPreviewRequest(context.row, context.tab, context.node).pipe(
						map(response => ({ response, context })),
						finalize(() => this.endPreviewFeedback(context.row)),
						catchError(error => {
							this.handlePreviewError(context.row, error);
							return of({ response: null, context });
						})
					);
				})
			)
			.subscribe(result => {
				const response = result?.response;
				const context = result?.context ?? null;

				if (!response) {
					return;
				}

				this.applyPreviewResponse(response, context?.row ?? null, context?.tab ?? this.currentRuleTab);
			});

    this.initializeMapperContext();
  }

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['linkId'] && !changes['linkTypeId']) {
      return;
    }

    this.initializedLinkId = null;
    this.initializeMapperContext();
  }

  private initializeMapperContext(): void {
    const linkId = this.linkId ?? null;

    if (!linkId || linkId <= 0 || this.initializedLinkId === linkId) {
      return;
    }

    this.initializedLinkId = linkId;
    this.isInitialLoadSequence = true;
    this.isPageLoading = true;
    this.pageLoadingMessage = 'Loading mapper entities...';
    this.pageLoadingDetail = 'Checking which source and target entities are available for this link.';
    this.sourceEntity = null;
    this.targetEntity = null;
    this.configuration = null;
    this.availableSourceEntities = [];
    this.availableTargetEntities = [];
    this.rootMappingNode = null;
    this.selectedMappingNodeId = null;
		this.mappingNodeIdSequence = 0;
		this.createWritebacks = [];
		this.updateWritebacks = [];
		this.activeWritebackRuleSet = 'create';
    this.writebackSourceTreeItemsByIndex = {};
    this.writebackTargetTreeItemsByIndex = {};
    this.writebackSourceFieldSearchByIndex = {};
    this.writebackTargetFieldSearchByIndex = {};
    this.writebackSourceDropdownOpenedByIndex = {};
    this.writebackTargetDropdownOpenedByIndex = {};
    this.sourceIdPathTreeItems = [];
    this.sourceIdPathFieldSearch = '';
    this.sourceIdPathDropdownOpened = false;
    this.messageVariableSourceSearch = '';
    this.messageVariableTargetSearch = '';
		this.messageVariableSourceDropdownOpened = false;
		this.messageVariableTargetDropdownOpened = false;
		this.runtimeMessageSourcePropertyExamples = [];
		this.runtimeMessageTargetPropertyExamples = [];
		this.runtimeMessageSourceTreeItems = [];
		this.runtimeMessageTargetTreeItems = [];
		this.activeTestRunSessionId = '';
		this.latestTestRunResult = null;
		this.isLoadingLatestTestRunResult = false;
		this.mapperRunProgressStage = '';
		this.mapperRunProgressProcessedCount = 0;
		this.mapperRunProgressTotalCount = 0;
		this.mapperRunProgressMessage = '';
		this.referenceFieldOptionsByEntity = {};
    this.validationSummary = [];
    this.validationMessages = [];
    this.previewResponseJson = {};
    this.previewSourceJson = {};
    this.previewResultJson = {};
    this.sampleInputValues = {};
    this.loadedSamplePayloadInputSignature = null;
		this.profileName = '';
		this.selectedProfileName = '';
		this.templateName = '';
		this.selectedTemplateName = '';
		this.templateSearch = '';
		this.selectedTemplateMiniGridTab = 'create';
		this.selectedTemplateDefinition = null;
		this.isLoadingSelectedTemplateDefinition = false;
		this.selectedTemplateDefinitionError = '';
		this.profileMenuOpen = false;
		this.loadEntitySelections();
	}

	private loadEntitySelections(): void {
    const linkId = this.linkId ?? null;
		this.clearMapperLoadError();

    if (!linkId || linkId <= 0) {
      this.lastActionMessage = 'A valid link is required before the mapper can load.';
      this.isPageLoading = false;
			this.stopInitialLoadSequence();
      return;
    }

    forkJoin({
      sourceEntities: this.api.getSourceEntities(linkId, { useCache: this.metadataCachingEnabled }),
      targetEntities: this.api.getTargetEntities(linkId, { useCache: this.metadataCachingEnabled })
		}).subscribe({
			next: ({ sourceEntities, targetEntities }) => {
				this.availableSourceEntities = sourceEntities ?? [];
				this.availableTargetEntities = targetEntities ?? [];
				this.sourceEntity = this.resolveEntitySelection(sourceEntities, this.sourceEntity, 'source');
				this.targetEntity = this.resolveEntitySelection(targetEntities, this.targetEntity, 'target');

        if (!this.sourceEntity || !this.targetEntity) {
          this.lastActionMessage = 'No generic mapper entities are available for this link.';
					this.mapperLoadErrorTitle = 'No mapper matches this link type';
					this.mapperLoadErrorMessage = !this.linkTypeId || this.linkTypeId <= 0
						? 'The link metadata did not include a valid link type ID. Rebuild and restart ApplicationLink3, then retry.'
						: `A source or target mapper entity for link type ${this.linkTypeId} is missing. Rebuild and restart the connector Function Apps, then clear the mapper cache and retry.`;
          this.isPageLoading = false;
			this.stopInitialLoadSequence();
          return;
        }

        this.pageLoadingMessage = 'Loading mapper configuration...';
        this.pageLoadingDetail = 'Fetching the resolved mapping configuration, schemas, sample payload state, and saved profiles.';
				this.loadConfiguration();
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to load mapper entities.', 'Mapper entities could not be loaded');
				this.isPageLoading = false;
				this.stopInitialLoadSequence();
			}
		});
  }

  private resolveEntitySelection(
    entities: MappingEntityDescriptor[],
    currentEntity: string | null,
    side: 'source' | 'target'
  ): string | null {
    const availableEntities = (entities ?? []).filter(entity =>
      side === 'source' ? entity.supportsSourceMapping : entity.supportsTargetMapping
    );

    if (!availableEntities.length) {
      return null;
    }

		const linkTypeId = this.linkTypeId ?? null;
		if (!linkTypeId || linkTypeId <= 0) {
			return null;
		}

		const matchingEntities = availableEntities.filter(entity => entity.linkTypeId === linkTypeId);

		if (!matchingEntities.length) {
			return null;
		}

    const selectedEntity = currentEntity?.trim();
    if (selectedEntity && matchingEntities.some(entity => entity.entityName === selectedEntity)) {
      return selectedEntity;
    }

		return matchingEntities[0].entityName;
  }

  private createEditorNode(options: {
    kind: MappingNodeKind;
    mappingId?: string | null;
    parentId: string | null;
    ownerTargetFieldId?: string | null;
    ownerTargetPath?: string | null;
    inlineChildObject?: boolean;
    name: string;
    sourceEntity: string;
    targetEntity: string;
    sourceIdPath: string;
    sourceSchema: MappingEntitySchema;
    targetSchema: MappingEntitySchema;
    sourceCollectionPath: string;
    targetRuntimeRoute: string;
    contextBindings: ChildContextBinding[];
    continueOnError: boolean;
    executionMode: string;
    runtimeMessageTemplates?: MapperRuntimeMessageTemplates | null;
  }): MapperEditorNode {
    return {
      id: `mapper-node-${++this.mappingNodeIdSequence}`,
      mappingId: options.mappingId ?? null,
      kind: options.kind,
      parentId: options.parentId,
      ownerTargetFieldId: options.ownerTargetFieldId ?? null,
      ownerTargetPath: options.ownerTargetPath ?? null,
      inlineChildObject: options.inlineChildObject === true,
      name: options.name,
      sourceEntity: options.sourceEntity,
      targetEntity: options.targetEntity,
      sourceIdPath: options.sourceIdPath,
      sourceSchema: options.sourceSchema,
      targetSchema: options.targetSchema,
      sourceFieldOptions: this.flattenSchemaToFieldOptions(
        options.sourceSchema.fields,
        '',
        '',
        field => this.isLeafField(field) && field.canRead
      ),
      sourceTreeItems: this.mapSchemaToTreeNodes(options.sourceSchema.fields),
      targetTreeItems: this.mapSchemaToTreeNodes(options.targetSchema.fields),
      createMappingRows: this.buildRowsFromTargetSchema(options.targetSchema, 'create'),
      updateMappingRows: this.buildRowsFromTargetSchema(options.targetSchema, 'update'),
      filteredCreateMappingRows: [],
      filteredUpdateMappingRows: [],
      runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates({ runtimeMessageTemplates: options.runtimeMessageTemplates ?? null }),
      sourceCollectionPath: options.sourceCollectionPath,
      targetRuntimeRoute: options.targetRuntimeRoute,
      contextBindings: (options.contextBindings ?? []).map(binding => ({
        targetPath: binding.targetPath,
        mode: binding.mode,
        sourcePath: binding.sourcePath ?? null
      })),
      continueOnError: options.continueOnError,
      executionMode: options.executionMode,
      children: []
    };
  }

  private findNodeById(node: MapperEditorNode | null, nodeId: string | null): MapperEditorNode | null {
    if (!node || !nodeId) {
      return null;
    }

    if (node.id === nodeId) {
      return node;
    }

    for (const child of node.children) {
      const match = this.findNodeById(child, nodeId);
      if (match) {
        return match;
      }
    }

    return null;
  }

  private appendChildTabs(parentNode: MapperEditorNode, items: MappingObjectTabItem[]): void {
    for (const child of parentNode.children) {
      items.push({
        id: child.id,
        label: child.name?.trim() || child.ownerTargetPath || child.targetEntity,
        description: child.ownerTargetPath || `${child.sourceEntity} -> ${child.targetEntity}`,
        nodeId: child.id,
        targetFieldId: child.ownerTargetFieldId ?? null,
        kind: child.kind
      });

      this.appendChildTabs(child, items);
    }
  }

  private findChildNodeByOwner(parentNode: MapperEditorNode, ownerTargetFieldId: string): MapperEditorNode | null {
    return parentNode.children.find(child => child.ownerTargetFieldId === ownerTargetFieldId) ?? null;
  }

  private isInlineChildObjectNode(node: MapperEditorNode | null | undefined): boolean {
    return node?.kind === 'child' && node.inlineChildObject === true;
  }

  private isInlineChildObjectRulePath(node: MapperEditorNode | null | undefined, targetPath: string | null | undefined): boolean {
    const ownerTargetPath = node?.ownerTargetPath?.trim();
    const normalizedTargetPath = targetPath?.trim();

    if (!ownerTargetPath || !normalizedTargetPath) {
      return false;
    }

    return normalizedTargetPath.startsWith(ownerTargetPath + '.');
  }

  private syncChildMappingCounts(node: MapperEditorNode): void {
    const childCounts = new Map<string, number>();
    for (const child of node.children) {
      const ownerTargetFieldId = child.ownerTargetFieldId;
      if (!ownerTargetFieldId) {
        continue;
      }

      childCounts.set(ownerTargetFieldId, (childCounts.get(ownerTargetFieldId) ?? 0) + 1);
    }

    [...node.createMappingRows, ...node.updateMappingRows].forEach(row => {
      row.childMappingCount = childCounts.get(row.targetFieldId) ?? 0;
    });

    node.children.forEach(child => this.syncChildMappingCounts(child));
  }

  isSelectedNode(node: MapperEditorNode): boolean {
    return this.selectedMappingNode?.id === node.id;
  }

  isChildMappingRow(row: MappingGridRow): boolean {
    return !!row && (!!row.isCollection || !!row.isChildObject);
  }

  private beginPreviewFeedback(row: MappingGridRow | null): void {
    this.isPreviewLoading = true;

    if (row) {
      row.isResolving = true;
      row.resolvingMessage = 'Resolving source data...';
      row.validationMessage = row.validationMessage || 'Refreshing preview...';
      this.resolvingRowIds.add(row.targetFieldId);
    } else {
      this.mappingRows.forEach(r => {
        r.isResolving = true;
        r.resolvingMessage = 'Refreshing preview...';
        this.resolvingRowIds.add(r.targetFieldId);
      });
    }
  }

  private endPreviewFeedback(row: MappingGridRow | null): void {
    this.isPreviewLoading = false;

    if (row) {
      row.isResolving = false;
      row.resolvingMessage = null;
      this.resolvingRowIds.delete(row.targetFieldId);
    } else {
      this.mappingRows.forEach(r => {
        r.isResolving = false;
        r.resolvingMessage = null;
      });
      this.resolvingRowIds.clear();
    }
  }

  private handlePreviewError(row: MappingGridRow | null, error?: unknown): void {
    const errorMessage = this.showMapperApiError(error, 'Preview failed.', 'Mapper preview failed');

    if (row) {
      row.isResolving = false;
      row.resolvingMessage = null;
      row.validationMessage = errorMessage;
      this.resolvingRowIds.delete(row.targetFieldId);
    } else {
      this.mappingRows.forEach(r => {
        r.isResolving = false;
        r.resolvingMessage = null;
      });
      this.resolvingRowIds.clear();
    }
  }

	private runPreviewRequest(row: MappingGridRow | null = null, tab: RuleTab = this.currentRuleTab, previewNode: MapperEditorNode | null = this.selectedMappingNode) {
		if (!this.configuration) {
			return of(null);
		}

    if (this.hasRequiredSampleInputs()) {
      return of(null);
    }

		return this.ensureCurrentSamplePayload().pipe(
			switchMap(loaded => {
				if (!loaded || !this.configuration) {
					return of(null);
				}

				return this.runSelectedMappingPreview(row, previewNode, tab);
			})
		);
	}

	private runSelectedMappingPreview(row: MappingGridRow | null = null, selectedNode: MapperEditorNode | null = this.selectedMappingNode, tab: RuleTab = this.currentRuleTab) {

		if (selectedNode?.kind === 'child') {
			return this.runChildMappingPreview(selectedNode, row, tab);
		}

		const request = this.buildMappingDefinition(row, tab);

		return this.api.previewMapping(
			request,
			this.configuration?.sampleSourcePayload,
			this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema,
			this.configuration?.targetSchema
		);
	}

	private runChildMappingPreview(childNode: MapperEditorNode, row: MappingGridRow | null = null, tab: RuleTab = this.currentRuleTab): Observable<MappingPreviewResponse | null> {
		const parentNode = this.findParentNode(this.rootMappingNode, childNode.id);
		if (!this.configuration || !parentNode) {
			return of(null);
		}

		return this.buildChildPreviewParentContexts(parentNode, tab).pipe(
			switchMap(parentContexts => {
				const childSourceRecords = parentContexts
					.reduce<any[]>((items, context) => items.concat(this.buildChildPreviewSourceRecords(
						childNode,
						context.sourceRecord,
						context.payload,
						context.targetRecord
					)), [])
					.slice(0, LinkGenericMapperComponent.childPreviewRecordLimit);

				if (!childSourceRecords.length) {
					this.lastActionMessage = childNode.sourceCollectionPath?.trim()
						? `No child source records found at '${childNode.sourceCollectionPath}'.`
						: 'No child source record available for preview.';
					return of(null);
				}

				const childRequest = this.buildChildPreviewMappingDefinition(childNode, row, tab);
				const previewRequests = childSourceRecords.map(childSourceRecord => this.api.previewMapping(
					childRequest,
					childSourceRecord,
					childNode.sourceSchema,
					childNode.targetSchema
				));

				return forkJoin(previewRequests).pipe(
					map((responses: MappingPreviewResponse[]) => this.mergeChildPreviewResponses(responses, childSourceRecords))
				);
			})
		);
	}

	private buildChildPreviewParentContexts(node: MapperEditorNode, tab: RuleTab = this.currentRuleTab): Observable<ChildPreviewParentContext[]> {
		if (!this.configuration) {
			return of([]);
		}

		if (node.kind === 'root') {
			const parentRequest = this.buildMappingDefinition(null, tab);
			return this.api.previewMapping(
				parentRequest,
				this.configuration.sampleSourcePayload,
				this.configuration.effectiveSourceSchema ?? this.configuration.sourceSchema,
				this.configuration.targetSchema
			).pipe(
				map(parentResponse => [{
					sourceRecord: parentResponse?.resolvedSourcePayload
						?? parentResponse?.sourcePayload
						?? this.configuration?.sampleSourcePayload
						?? {},
					payload: parentResponse?.result ?? {},
					targetRecord: parentResponse?.result ?? {}
				}])
			);
		}

		const parentNode = this.findParentNode(this.rootMappingNode, node.id);
		if (!parentNode) {
			return of([]);
		}

		return this.buildChildPreviewParentContexts(parentNode, tab).pipe(
			switchMap((ancestorContexts: ChildPreviewParentContext[]) => {
				const nodeSourceRecords = ancestorContexts
					.reduce<any[]>((items, context) => items.concat(this.buildChildPreviewSourceRecords(
						node,
						context.sourceRecord,
						context.payload,
						context.targetRecord
					)), [])
					.slice(0, LinkGenericMapperComponent.childPreviewRecordLimit);

				if (!nodeSourceRecords.length) {
					return of([]);
				}

				const nodeRequest = this.buildChildPreviewMappingDefinition(node, null, tab);
				return forkJoin(nodeSourceRecords.map(sourceRecord => this.api.previewMapping(
					nodeRequest,
					sourceRecord,
					node.sourceSchema,
					node.targetSchema
				))).pipe(
					map((responses: MappingPreviewResponse[]) => responses.map((response, index) => ({
						sourceRecord: response?.resolvedSourcePayload ?? nodeSourceRecords[index] ?? {},
						payload: response?.result ?? {},
						targetRecord: response?.result ?? {}
					})))
				);
			})
		);
	}

	private buildChildPreviewMappingDefinition(node: MapperEditorNode, previewRow: MappingGridRow | null, previewTab: RuleTab = this.currentRuleTab): MappingDefinition {
		const createRows = this.isInlineChildObjectNode(node)
			? this.getInlineChildNodeRows(node, 'create')
			: node.createMappingRows;
		const updateRows = this.isInlineChildObjectNode(node)
			? this.getInlineChildNodeRows(node, 'update')
			: node.updateMappingRows;
		const previewRows = previewRow
			? (previewTab === 'create' ? createRows : updateRows).filter(row => row.targetFieldId === previewRow.targetFieldId)
			: null;
		const activeCreateRows = previewTab === 'create' && previewRows?.length ? previewRows : createRows;
		const activeUpdateRows = previewTab === 'update' && previewRows?.length ? previewRows : updateRows;
		const inlineCreateRules = this.buildInlineChildRules(node, 'create');
		const inlineUpdateRules = this.buildInlineChildRules(node, 'update');

		return {
			linkId: this.configuration?.linkId ?? 0,
			sourceSchemaId: node.sourceSchema.id,
			targetSchemaId: node.targetSchema.id,
			sourceEntity: node.sourceEntity,
			targetEntity: node.targetEntity,
			name: node.name,
			activeRuleSet: previewTab,
			createRules: this.extractRulesFromRows(activeCreateRows).concat(inlineCreateRules),
			updateRules: this.extractRulesFromRows(activeUpdateRows).concat(inlineUpdateRules),
			rules: this.extractRulesFromRows(activeCreateRows).concat(inlineCreateRules),
			writebacks: [],
			runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates({ runtimeMessageTemplates: node.runtimeMessageTemplates }),
			children: node.children.map(child => this.buildChildMappingDefinition(child))
		};
	}

	private buildChildPreviewSourceRecords(
		childNode: MapperEditorNode,
		parentSourceRecord: any,
		parentPayload: any,
		parentTargetRecord: any
	): any[] {
		if (this.isInlineChildObjectNode(childNode)) {
			const inlineSourceRecord = this.cloneJsonValue(parentSourceRecord ?? {});

			for (const binding of childNode.contextBindings ?? []) {
				if (!binding?.targetPath || !binding.mode) {
					continue;
				}

				const source = binding.mode === 'ParentTargetValue'
					? parentTargetRecord
					: binding.mode === 'ParentPayloadValue'
						? parentPayload
						: parentSourceRecord;
				const value = this.readJsonPathCaseInsensitive(source, binding.sourcePath ?? '');
				this.setJsonPathValue(inlineSourceRecord, binding.targetPath, value);
			}

			return [inlineSourceRecord];
		}

		const collectionPath = childNode.sourceCollectionPath?.trim();
		let childSourceRecords: any[];

		if (collectionPath) {
			const collection = this.readJsonPathCaseInsensitive(parentSourceRecord, collectionPath);
			if (!Array.isArray(collection) || collection.length === 0) {
				return [];
			}

			childSourceRecords = collection
				.slice(0, LinkGenericMapperComponent.childPreviewRecordLimit)
				.map(item => this.cloneJsonValue(item));
		} else {
			childSourceRecords = [this.cloneJsonValue(parentSourceRecord ?? {})];
		}

		for (const childSourceRecord of childSourceRecords) {
			for (const binding of childNode.contextBindings ?? []) {
				if (!binding?.targetPath || !binding.mode) {
					continue;
				}

				const source = binding.mode === 'ParentTargetValue'
					? parentTargetRecord
					: binding.mode === 'ParentPayloadValue'
						? parentPayload
						: parentSourceRecord;
				const value = this.readJsonPathCaseInsensitive(source, binding.sourcePath ?? '');
				this.setJsonPathValue(childSourceRecord, binding.targetPath, value);
			}
		}

		return childSourceRecords;
	}

	private mergeChildPreviewResponses(responses: MappingPreviewResponse[], childSourceRecords: any[]): MappingPreviewResponse | null {
		if (responses.length <= 1) {
			return responses[0] ?? null;
		}

		const firstResponse = responses[0];
		if (!firstResponse) {
			return null;
		}

		return {
			...firstResponse,
			isValid: responses.every(response => response?.isValid === true),
			fieldResults: firstResponse.fieldResults ?? [],
			result: responses.map(response => response?.result ?? {}),
			resolvedSourcePayload: childSourceRecords,
			originalSourcePayload: childSourceRecords,
			childPreviewCount: responses.length,
			childPreviewLimit: LinkGenericMapperComponent.childPreviewRecordLimit
		};
	}

	private findParentNode(currentNode: MapperEditorNode | null, childNodeId: string): MapperEditorNode | null {
		if (!currentNode) {
			return null;
		}

		if (currentNode.children.some(child => child.id === childNodeId)) {
			return currentNode;
		}

		for (const child of currentNode.children) {
			const parent = this.findParentNode(child, childNodeId);
			if (parent) {
				return parent;
			}
		}

		return null;
	}

	private readJsonPathCaseInsensitive(source: any, path: string): any {
		if (source == null || !path?.trim()) {
			return source;
		}

		let current = source;
		for (const part of this.parseJsonPath(path)) {
			if (current == null || typeof current !== 'object') {
				return undefined;
			}

			const key = Object.keys(current).find(item => item.toLowerCase() === part.toLowerCase());
			if (!key) {
				return undefined;
			}

			current = current[key];
		}

		return current;
	}

	private setJsonPathValue(target: any, path: string, value: any): void {
		if (target == null || !path?.trim()) {
			return;
		}

		const parts = this.parseJsonPath(path);
		if (!parts.length) {
			return;
		}

		let current = target;

		for (let index = 0; index < parts.length - 1; index += 1) {
			const part = parts[index];
			const existingKey = Object.keys(current).find(item => item.toLowerCase() === part.toLowerCase());
			const key = existingKey ?? part;
			if (current[key] == null || typeof current[key] !== 'object' || Array.isArray(current[key])) {
				current[key] = {};
			}

			current = current[key];
		}

		current[parts[parts.length - 1]] = value;
	}

	private parseJsonPath(path: string): string[] {
		const parts: string[] = [];
		let current = '';
		let insideBracket = false;
		let insideQuote = false;
		let quoteChar = '';

		for (const char of (path ?? '').trim()) {
			if (!insideBracket && char === '.') {
				if (current) {
					parts.push(current);
					current = '';
				}
				continue;
			}

			if (char === '[' && !insideQuote) {
				insideBracket = true;
				current += char;
				continue;
			}

			if (char === ']' && !insideQuote) {
				insideBracket = false;
				current += char;
				continue;
			}

			if ((char === '\'' || char === '"') && insideBracket) {
				if (!insideQuote) {
					insideQuote = true;
					quoteChar = char;
				} else if (quoteChar === char) {
					insideQuote = false;
				}
				current += char;
				continue;
			}

			current += char;
		}

		if (current) {
			parts.push(current);
		}

		return parts
			.map(part => this.normalizeJsonPathPart(part))
			.filter(Boolean);
	}

	private normalizeJsonPathPart(part: string): string {
		const trimmed = (part ?? '').trim();
		const bracketMatch = trimmed.match(/^\[['"](.+?)['"]\]$/);
		return bracketMatch ? bracketMatch[1] ?? '' : trimmed;
	}

	private cloneJsonValue<T>(value: T): T {
		if (value == null) {
			return value;
		}

		return JSON.parse(JSON.stringify(value));
	}

	private applyPreviewResponse(response: any, row: MappingGridRow | null = null, tab: RuleTab = this.currentRuleTab): void {
    if (row) {
      const fieldResult = response?.fieldResults?.find(
        (x: any) => x.targetFieldId === row.targetFieldId
      );

      row.previewValue = fieldResult?.previewValue;
      row.isValid = fieldResult?.isValid;
      row.validationMessage = fieldResult?.message ?? null;

      this.applyRowFilters();
      this.refreshSelectedRow();
      this.lastActionMessage = row.isValid === false
        ? `${row.targetDisplayName} preview updated with validation issues.`
        : `${row.targetDisplayName} preview updated.`;
      return;
    }

    const resolvedSourcePayload =
      response?.resolvedSourcePayload ??
      response?.sourcePayload ??
      this.configuration?.sampleSourcePayload ??
      {};

    const resultPayload = response?.result ?? {};

    this.previewResponseJson = response ?? {};
    this.previewSourceJson = resolvedSourcePayload;
    this.previewResultJson = resultPayload;

    this.validationMessages = response.fieldResults
      .filter((x: any) => !x.isValid)
      .map((x: any) => `${x.targetFieldId}: ${x.message ?? 'Invalid'}`);

    this.validationSummary = [];

		this.getRowsForTab(tab).forEach(row => {
      const fieldResult = response.fieldResults.find((x: any) => x.targetFieldId === row.targetFieldId);

      row.previewValue = fieldResult?.previewValue;
      row.isValid = fieldResult?.isValid;
      row.validationMessage = fieldResult?.message ?? null;

      this.validationSummary.push({
        field: row.targetDisplayName,
        ok: row.isValid ?? false,
        message: row.validationMessage ?? 'No result.'
      });
    });

    this.applyRowFilters();
    this.refreshSelectedRow();
    this.lastActionMessage = response.isValid
      ? 'Preview generated.'
      : 'Preview generated with validation issues.';
  }

	setToolbarFilter(filter: ToolbarFilter): void {
		this.activeToolbarFilter = filter;
		this.filterMappingRows();
	}

	resetFilters(): void {
		this.activeToolbarFilter = 'all';
		this.mappingRowSearch = '';
		this.hideReadOnlyRows = false;
		this.filterMappingRows();
	}

	toggleReadOnlyRows(): void {
		this.hideReadOnlyRows = !this.hideReadOnlyRows;
		this.filterMappingRows();
	}

	get activeFilterLabel(): string {
		switch (this.activeToolbarFilter) {
			case 'required':
				return 'Required';
			case 'mapped':
				return 'Mapped';
			case 'unmapped':
				return 'Unfinished';
			case 'issues':
				return 'Issues';
			case 'ignored':
				return 'Ignored fields';
			default:
				return 'All fields';
		}
	}

	get hasActiveFilters(): boolean {
		return this.activeToolbarFilter !== 'all'
			|| !!this.mappingRowSearch.trim()
			|| this.hideReadOnlyRows;
	}

	mappingStats = {
		total: 0,
		required: 0,
		mapped: 0,
		ignored: 0,
		invalid: 0,
		mappedRequired: 0,
		unmappedRequired: 0
	};

	private refreshMappingStats(): void {
		const stats = {
			total: 0,
			required: 0,
			mapped: 0,
			ignored: 0,
			invalid: 0,
			mappedRequired: 0,
			unmappedRequired: 0
		};

		for (const row of this.mappingRows) {
			stats.total++;
			if (row.required && !row.readOnly) {
				stats.required++;
			}
			if (this.isRowMapped(row)) {
				stats.mapped++;
			}
			if (row.mode === 'Ignore') {
				stats.ignored++;
			}
			if (this.isRowValidationIssue(row)) {
				stats.invalid++;
			}
			if (row.required && !this.isRowUnmapped(row)) {
				stats.mappedRequired++;
			}
			if (row.required && this.isRowUnmapped(row)) {
				stats.unmappedRequired++;
			}
		}

		this.mappingStats = stats;
	}

  get filteredExpressionTools(): ExpressionTool[] {
    const query = this.expressionToolSearch.trim().toLowerCase();

    return this.expressionTools.filter(tool => {
      const categoryMatch =
        this.selectedExpressionToolCategory === 'All' ||
        tool.category === this.selectedExpressionToolCategory;

      if (!categoryMatch) {
        return false;
      }

      if (!query) {
        return true;
      }

      const haystack = `${tool.label} ${tool.description} ${tool.searchText} ${tool.template}`.toLowerCase();
      return haystack.includes(query);
    });
  }

  get groupedExpressionTools(): Array<{ category: string; items: ExpressionTool[] }> {
    const order: ExpressionToolCategory[] = [
      'Favorites',
      'Logical',
      'Comparison',
      'NullConditional',
      'String',
      'Numeric',
      'DateTime',
      'CollectionAggregate',
      'Custom',
      'Advanced'
    ];

    return order
      .map(category => ({
        category: this.getExpressionCategoryLabel(category),
        items: this.filteredExpressionTools.filter(x => x.category === category)
      }))
      .filter(group => group.items.length > 0);
  }

  private stringifyPreviewValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

	private buildExpressionTools(functionDefinitions: MapperExpressionFunctionDefinition[]): ExpressionTool[] {
		const backendTools = (functionDefinitions ?? [])
			.map(definition => this.mapExpressionFunctionDefinition(definition))
			.filter((tool): tool is ExpressionTool => !!tool);

		const backendToolIds = new Set(backendTools.map(tool => tool.id));
		return [
			...EXPRESSION_TOOLS.filter(tool => !backendToolIds.has(tool.id)),
			...backendTools
		];
	}

	private getConfiguredExpressionFunctions(config: MappingSchemaConfiguration | null): MapperExpressionFunctionDefinition[] {
		if (!config) {
			return [];
		}

		const rawConfig = config as MappingSchemaConfiguration & { ExpressionFunctions?: MapperExpressionFunctionDefinition[] };
		return rawConfig.expressionFunctions ?? rawConfig.ExpressionFunctions ?? [];
	}

	private loadExpressionFunctions(): void {
		this.api.getExpressionFunctions()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: functionDefinitions => {
					this.expressionTools = this.buildExpressionTools(functionDefinitions ?? []);
				},
				error: () => {
					this.expressionTools = this.buildExpressionTools(this.getConfiguredExpressionFunctions(this.configuration));
				}
			});
	}

	private mapExpressionFunctionDefinition(definition: MapperExpressionFunctionDefinition): ExpressionTool | null {
		const rawDefinition = definition as MapperExpressionFunctionDefinition & {
			Id?: string;
			Name?: string;
			Category?: string;
			Template?: string;
			Usage?: string;
			Description?: string;
			Example?: string;
			ExampleResult?: string;
			Notes?: string;
			SearchText?: string;
		};
		const name = rawDefinition.name ?? rawDefinition.Name ?? '';
		const template = rawDefinition.template ?? rawDefinition.Template ?? '';
		const description = rawDefinition.description ?? rawDefinition.Description ?? '';
		const usage = rawDefinition.usage ?? rawDefinition.Usage;
		const example = rawDefinition.example ?? rawDefinition.Example;
		const exampleResult = rawDefinition.exampleResult ?? rawDefinition.ExampleResult;
		const notes = rawDefinition.notes ?? rawDefinition.Notes;
		const searchText = rawDefinition.searchText ?? rawDefinition.SearchText;
		const category = this.normalizeExpressionToolCategory(rawDefinition.category ?? rawDefinition.Category);
		if (!name || !template || !category) {
			return null;
		}

		return {
			id: rawDefinition.id || rawDefinition.Id || name,
			category,
			label: name,
			template,
			description: description || usage || template,
			searchText: searchText || `${name} ${description} ${template}`,
			kind: 'function',
			usage,
			example,
			exampleResult,
			notes,
			backendDefined: true
		};
	}

	private normalizeExpressionToolCategory(category: string | null | undefined): ExpressionToolCategory | null {
		const normalized = category?.trim();
		const validCategories: ExpressionToolCategory[] = [
			'Favorites',
			'Logical',
			'Comparison',
			'NullConditional',
			'String',
			'Numeric',
			'DateTime',
			'CollectionAggregate',
			'Custom',
			'Advanced'
		];

		return validCategories.find(item => item === normalized) ?? 'Advanced';
	}

  private getResolvedSourcePayload(response: any): any {
    return response?.resolvedSourcePayload
      ?? response?.sourcePayload
      ?? this.configuration?.sampleSourcePayload
      ?? {};
  }

  openPicklistMappingEditor(row: MappingGridRow): void {
		void this.openPicklistMappingEditorAsync(row);
	}

	private async openPicklistMappingEditorAsync(row: MappingGridRow): Promise<void> {
    if (!row.sourceFieldPath) {
      return;
    }

		let sourceField = this.getCompatibleSourceFieldOptionsForRow(row)
      .find(x => x.path === row.sourceFieldPath);

    if (!sourceField?.isPicklist) {
      return;
    }

		if (sourceField.optionsDeferred && this.selectedMappingNode) {
			try {
				await this.loadDeferredSourceOptions(this.selectedMappingNode, sourceField.path);
			} catch {
				return;
			}

			sourceField = this.getCompatibleSourceFieldOptionsForRow(row)
				.find(option => option.path === row.sourceFieldPath);
			if (!sourceField?.isPicklist) {
				return;
			}
		}

		const shouldRebuildPicklistMapping =
			!row.picklistMapping ||
			row.picklistMapping.sourceFieldPath !== sourceField.path;

		if (shouldRebuildPicklistMapping) {
			const existingTargets = new Map(
				(row.picklistMapping?.items ?? []).map(item => [item.sourceValue, item.targetValue ?? null])
			);

			this.picklistMappingDraft = {
				sourceFieldPath: sourceField.path,
				items: (sourceField.picklistOptions ?? []).map(option => ({
					sourceValue: option.value,
					sourceLabel: option.label,
					targetValue: existingTargets.get(option.value) ?? null
				}))
			};
		} else {
			this.picklistMappingDraft = this.cloneJsonValue(row.picklistMapping);
		}

		this.picklistMappingRow = row;
		this.picklistMappingSourceField = sourceField;
		this.picklistMappingPopupVisible = true;
	}

	private async loadDeferredSourceOptions(node: MapperEditorNode, fieldPath: string): Promise<void> {
		const field = this.findSchemaFieldByPath(node.sourceSchema.fields, fieldPath);
		if (!field?.optionsDeferred || !field.deferredOptionsEntityName || !field.deferredOptionsFieldPath) {
			return;
		}

		const linkId = this.linkId ?? this.configuration?.linkId ?? 0;
		if (linkId <= 0) {
			return;
		}

		this.lastActionMessage = `Loading options for ${field.displayName}...`;
		try {
			const schema = await firstValueFrom(this.api.getEntitySchema(
				linkId,
				field.deferredOptionsEntityName,
				'source',
				{
					deferReferenceChildren: 'true',
					deferredOptionsFieldPath: field.deferredOptionsFieldPath
				}
			));
			const loadedField = this.findSchemaFieldByPath(schema.fields, field.path);
			field.picklistOptions = loadedField?.picklistOptions ?? [];
			field.optionsDeferred = false;
			this.rebuildSourceSchemaViews(node);
			this.lastActionMessage = `${field.displayName} options loaded.`;
		} catch (error) {
			const message = this.getApiErrorMessage(error, `Could not load options for ${field.displayName}.`);
			this.lastActionMessage = message;
			notify({ message, type: 'error', displayTime: 6000 });
			throw error;
		}
	}

  applyPicklistMapping(mapping: PicklistMappingConfiguration): void {
    if (!this.picklistMappingRow) {
      return;
    }

    const row = this.picklistMappingRow;

		row.picklistMapping = this.cloneJsonValue(mapping);
    this.picklistMappingPopupVisible = false;
    this.picklistMappingRow = null;
    this.picklistMappingSourceField = null;
		this.picklistMappingDraft = null;
		if (this.guidePicklistMappingActive) {
			this.guidePicklistMappingActive = false;
			this.onMapperGuidePreviewRequested(row);
			return;
		}

    this.requestPreview(row);
  }

  closePicklistMappingEditor(): void {
    this.picklistMappingPopupVisible = false;
    this.picklistMappingRow = null;
    this.picklistMappingSourceField = null;
		this.picklistMappingDraft = null;
		this.guidePicklistMappingActive = false;
  }

  openLookupCriterionExpressionEditor(index: number): void {
	if (!this.lookupEditingDraftRow?.lookup?.criteria?.[index]) {
      return;
    }

	const criterion = this.lookupEditingDraftRow.lookup.criteria[index];

    this.lookupExpressionCriterionIndex = index;
    this.expressionEditingTargetFieldId = null;
		this.guideExpressionEditingRow = null;
		this.expressionEditorUsesGuideFields = this.guideLookupEditorActive;
    this.expressionFieldSearch = '';
		if (this.expressionEditorUsesGuideFields) {
			this.expressionSourceTreeItems = this.mapperGuideSourceTreeItems;
			this.expressionTargetTreeItems = this.mapperGuideTargetTreeItems;
		} else {
			this.rebuildExpressionSourceTreeItems();
		}
		this.resetExpressionTestResult();
    this.expressionTipsExpanded = false;
    this.expressionDraft = this.normalizeExpressionForEditor(criterion.value);
    this.expressionPopupVisible = true;
    this.closeExpressionSuggestions();
  }

  getLookupReferenceFieldOptions(row: MappingGridRow | null): FieldOption[] {
    if (!row) {
      return [];
    }

    const entityName =
      row.lookup?.referenceEntityName ||
      row.referenceEntityName ||
      row.reference?.targetEntity ||
      '';

    return entityName ? (this.referenceFieldOptionsByEntity[entityName] ?? []) : [];
  }

  private ensureLookupReferenceFieldOptions(row: MappingGridRow): void {
    const linkId = this.linkId ?? null;
    const entityName =
      row.lookup?.referenceEntityName ||
      row.referenceEntityName ||
      row.reference?.targetEntity ||
      '';

    if (!linkId || linkId <= 0 || !entityName || this.referenceFieldOptionsByEntity[entityName]?.length) {
      return;
    }

		this.api.getEntitySchema(linkId, entityName, 'target').subscribe({
			next: schema => {
				this.referenceFieldOptionsByEntity = {
					...this.referenceFieldOptionsByEntity,
          [entityName]: this.flattenSchemaToFieldOptions(
            schema.fields,
            '',
            '',
            field => this.isLeafField(field) && (field.canRead || field.canUseInLookup)
          )
				};
			},
			error: error => {
				this.showMapperApiError(error, `Failed to load lookup fields for ${entityName}.`, 'Lookup fields could not be loaded');
			}
		});
	}

  private getRowsForTab(tab: 'create' | 'update'): MappingGridRow[] {
    return tab === 'update' ? this.updateMappingRows : this.createMappingRows;
  }

  private setRowsForTab(tab: 'create' | 'update', rows: MappingGridRow[]): void {
    if (tab === 'update') {
      this.updateMappingRows = rows;
      return;
    }

    this.createMappingRows = rows;
  }

  private getFilteredRowsForTab(tab: 'create' | 'update'): MappingGridRow[] {
    return tab === 'update' ? this.filteredUpdateMappingRows : this.filteredCreateMappingRows;
  }

  private setFilteredRowsForTab(tab: 'create' | 'update', rows: MappingGridRow[]): void {
    if (tab === 'update') {
      this.filteredUpdateMappingRows = rows;
      return;
    }

    this.filteredCreateMappingRows = rows;
  }

  private getOtherRuleTab(tab: 'create' | 'update'): 'create' | 'update' {
    return tab === 'create' ? 'update' : 'create';
  }

  async copyMappingsFromOtherTemplate(): Promise<void> {
    if (this.activeMapperTab !== 'create' && this.activeMapperTab !== 'update') {
      return;
    }

    const targetTab = this.currentRuleTab;
    const sourceTab = this.getOtherRuleTab(targetTab);
    const sourceRowsByTargetFieldId = new Map(
      this.getRowsForTab(sourceTab).map(row => [row.targetFieldId, row])
    );

		const copyCandidates: Array<{ sourceRow: MappingGridRow; targetRow: MappingGridRow; preview: CopyMappingPreviewItem }> = [];

    for (const targetRow of this.getRowsForTab(targetTab)) {
      const sourceRow = sourceRowsByTargetFieldId.get(targetRow.targetFieldId);
		if (!sourceRow || (targetRow.readOnly && !targetRow.matchOnlyLinkKey)) {
        continue;
      }

			if (this.getRowConfigurationSignature(targetRow) === this.getRowConfigurationSignature(sourceRow)) {
				continue;
			}

			copyCandidates.push({
				sourceRow,
				targetRow,
				preview: {
					field: targetRow.targetDisplayName || targetRow.targetPath || targetRow.targetFieldId,
					before: this.formatRowConfigurationForReview(targetRow),
					after: this.formatRowConfigurationForReview(sourceRow)
				}
			});
    }

		if (!copyCandidates.length) {
			this.lastActionMessage = `No ${targetTab} rules would change when copying from ${sourceTab}.`;
			return;
		}

		const visibleItems = copyCandidates.slice(0, 40);
		const previewItems = visibleItems.map(item => `
			<div style="border: 1px solid #dbe4ef; border-radius: 10px; padding: 12px; margin-bottom: 10px; background: #fff;">
				<div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 10px; color: #42526e; font-size: 12px;">
					<div><strong style="color: #172b4d;">${this.escapeHtml(item.preview.field)}</strong></div>
					<div>${this.escapeHtml(sourceTab)} -> ${this.escapeHtml(targetTab)}</div>
				</div>
				<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; text-align: left;">
					<div>
						<div style="font-size: 11px; font-weight: 700; color: #6b778c; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;">Current</div>
						<div style="font-family: Consolas, 'Courier New', monospace; font-size: 12px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; background: #f7f9fc; border-radius: 6px; padding: 8px;">${this.escapeHtml(item.preview.before)}</div>
					</div>
					<div>
						<div style="font-size: 11px; font-weight: 700; color: #6b778c; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;">Copied rule</div>
						<div style="font-family: Consolas, 'Courier New', monospace; font-size: 12px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; background: #f2fff7; border: 1px solid #c6f6d5; border-radius: 6px; padding: 8px;">${this.escapeHtml(item.preview.after)}</div>
					</div>
				</div>
			</div>`).join('');
		const hiddenCount = Math.max(0, copyCandidates.length - visibleItems.length);
		const confirmation = await Swal.fire({
			icon: 'question',
			title: `Copy ${sourceTab} rules to ${targetTab}?`,
			width: 760,
			html: `
				<p>This will update ${copyCandidates.length} matching ${targetTab} field${copyCandidates.length === 1 ? '' : 's'} by target field id.</p>
				<div style="max-height: 460px; overflow: auto; text-align: left; padding-right: 4px;">
					${previewItems}
				</div>
				${hiddenCount > 0 ? `<p>${hiddenCount} more change${hiddenCount === 1 ? '' : 's'} not shown.</p>` : ''}
			`,
			showCancelButton: true,
			confirmButtonText: 'Apply copy',
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		for (const candidate of copyCandidates) {
			this.copyRowConfiguration(candidate.sourceRow, candidate.targetRow);
		}

		this.applyRowFilters();
		this.refreshSelectedRow();
		this.lastActionMessage = `${copyCandidates.length} ${targetTab === 'create' ? 'create' : 'update'} rule${copyCandidates.length === 1 ? '' : 's'} copied from ${sourceTab}.`;
		this.previewMappings(false);
	}

	applyRowFilters(): void {
		this.refreshMappingStats();
		this.refreshSaveMappingReviewState();
		this.filterMappingRows();
	}

	private filterMappingRows(): void {
		const query = this.mappingRowSearch.trim().toLowerCase();

		const nextRows = this.mappingRows.filter(row => {
      const haystack = [
        row.targetDisplayName,
        row.targetPath,
        row.targetDataType,
        row.description ?? '',
        row.referenceEntityName ?? '',
        row.format ?? ''
      ]
        .join(' ')
        .toLowerCase();

		const matchesSearch = !query || haystack.includes(query);
		const matchesReadOnlyFilter = !this.hideReadOnlyRows || !row.readOnly;
		return matchesSearch && matchesReadOnlyFilter && this.matchesToolbarFilter(row);
		}).sort((left, right) => this.compareMappingRowsByTargetField(left, right));
		const currentRows = this.filteredMappingRows;
		const hasSameRows = currentRows.length === nextRows.length && currentRows.every((row, index) => row === nextRows[index]);
		if (!hasSameRows) {
			this.filteredMappingRows = nextRows;
		}
	}

	private compareMappingRowsByTargetField(left: MappingGridRow, right: MappingGridRow): number {
		return this.getTargetFieldSortValue(left).localeCompare(this.getTargetFieldSortValue(right), undefined, { sensitivity: 'base', numeric: true }) ||
			(left.targetPath || '').localeCompare(right.targetPath || '', undefined, { sensitivity: 'base', numeric: true }) ||
			left.targetFieldId.localeCompare(right.targetFieldId, undefined, { sensitivity: 'base', numeric: true });
	}

	private getTargetFieldSortValue(row: MappingGridRow): string {
		return row.targetDisplayName?.trim() || row.targetPath?.trim() || row.targetFieldId;
	}

	private matchesToolbarFilter(row: MappingGridRow): boolean {
		switch (this.activeToolbarFilter) {
			case 'required':
				return row.required;
			case 'mapped':
				return this.isRowMapped(row);
			case 'unmapped':
				return this.isRowUnmapped(row);
			case 'issues':
				return this.isRowValidationIssue(row);
			case 'ignored':
				return this.getRowState(row) === 'ignored';
			default:
				return true;
		}
	}

  private getRowPriority(row: MappingGridRow): number {
    const state = this.getRowState(row);

	if (row.readOnly && !row.matchOnlyLinkKey) return 80;
    if (state === 'issue' && row.required) return 0;
    if (state === 'pending' && row.required) return 1;
    if (state === 'ready' && row.required) return 2;
    if (state === 'issue') return 3;
    if (state === 'pending') return 4;
    if (state === 'ready') return 5;
    if (state === 'ignored') return 6;

    return 7;
  }

  openLookupEditor(row: MappingGridRow): void {
		const draftLookup = this.cloneJsonValue(row.lookup) ?? {
        referenceEntityName:
          row.referenceEntityName ||
          row.reference?.targetEntity ||
          '',
        returnFieldPath:
          row.reference?.defaultReturnFieldPath || 'ID',
        matchMode: 'All',
        criteria: [
          {
            targetFieldPath: '',
            mode: 'SourceField',
            sourceFieldPath: null,
            value: null
          }
        ]
      };

		this.lookupEditingDraftRow = {
			...row,
			lookup: draftLookup
		};
	this.ensureLookupReferenceFieldOptions(this.lookupEditingDraftRow);
		this.lookupEditingRow = row;
		this.resetLookupTestState();
    this.lookupPopupVisible = true;
  }

  closeLookupEditor(): void {
    this.lookupPopupVisible = false;
    this.lookupEditingRow = null;
		this.lookupEditingDraftRow = null;
		this.resetLookupTestState();
		this.guideLookupEditorActive = false;
  }

	@HostListener('mapper-configure-active-lookup-draft', ['$event'])
	configureActiveLookupDraftFromEvent(event: CustomEvent<ManualLookupConfiguration>): void {
		if (!this.lookupEditingDraftRow || !event.detail) {
			return;
		}

		event.stopPropagation();
		this.lookupEditingDraftRow.lookup = this.cloneJsonValue(event.detail);
		this.resetLookupTestState();
	}

	resetLookupTestState(): void {
		this.lookupTestLoading = false;
		this.lookupTestRan = false;
		this.lookupTestValid = false;
		this.lookupTestHasResult = false;
		this.lookupTestResult = null;
		this.lookupTestMessage = '';
	}

	testLookupEditor(lookup: ManualLookupConfiguration): void {
		const draftRow = this.lookupEditingDraftRow;
		if (!this.configuration || !draftRow || !lookup || this.guideLookupEditorActive || this.hasRequiredSampleInputs()) {
			return;
		}

		const previewTab = this.currentRuleTab;
		const request = this.buildMappingDefinition(null, previewTab);
		const draftRule = this.extractRulesFromRows([{
			...draftRow,
			mode: 'Lookup',
			lookup: this.cloneJsonValue(lookup)
		}])[0];
		const activeRules = previewTab === 'create' ? request.createRules : request.updateRules;
		const ruleIndex = activeRules.findIndex(rule => rule.targetFieldId === draftRule.targetFieldId);
		if (ruleIndex >= 0) {
			activeRules[ruleIndex] = draftRule;
		}
		request.rules = request.createRules;

		this.lookupTestLoading = true;
		this.lookupTestRan = true;
		this.lookupTestMessage = '';
		this.api.previewMapping(
			request,
			this.configuration.sampleSourcePayload,
			this.configuration.effectiveSourceSchema ?? this.configuration.sourceSchema,
			this.configuration.targetSchema
		).pipe(takeUntil(this.destroy$)).subscribe({
			next: response => {
				const fieldResult = response?.fieldResults?.find(result => result.targetFieldId === draftRow.targetFieldId);
				this.lookupTestLoading = false;
				this.lookupTestValid = fieldResult?.isValid === true;
				this.lookupTestResult = fieldResult?.previewValue ?? null;
				this.lookupTestHasResult = fieldResult?.previewValue !== null && fieldResult?.previewValue !== undefined;
				this.lookupTestMessage = fieldResult?.message
					?? (this.lookupTestValid ? 'Lookup configuration is valid.' : 'No lookup result was returned.');
			},
			error: error => {
				this.lookupTestLoading = false;
				this.lookupTestValid = false;
				this.lookupTestHasResult = false;
				this.lookupTestMessage = this.getApiErrorMessage(error, 'Lookup test failed.');
			}
		});
	}

	applyLookupEditor(lookup: ManualLookupConfiguration): void {
    const row = this.lookupEditingRow;
		if (!row || !lookup) {
			this.closeLookupEditor();
			return;
		}

		row.lookup = this.cloneJsonValue(lookup);
		row.mode = 'Lookup';

    this.lookupPopupVisible = false;
    this.lookupEditingRow = null;
		this.lookupEditingDraftRow = null;
		this.resetLookupTestState();
		if (this.guideLookupEditorActive) {
			this.guideLookupEditorActive = false;
			this.onMapperGuidePreviewRequested(row);
			return;
		}

    this.requestPreview(row);
  }

  loadConfiguration(forceRefresh = false): void {
    const linkId = this.linkId ?? null;
    const sourceEntity = this.sourceEntity;
    const targetEntity = this.targetEntity;
		this.clearMapperLoadError();

    if (!linkId || linkId <= 0) {
      this.lastActionMessage = 'A valid link is required before the mapper can load.';
      this.isPageLoading = false;
      return;
    }

    if (!sourceEntity || !targetEntity) {
      this.pageLoadingMessage = 'Loading mapper entities...';
      this.pageLoadingDetail = 'Checking which source and target entities are available for this link.';
      this.loadEntitySelections();
      return;
    }

    this.isPageLoading = true;
    this.profileMenuOpen = false;
    this.pageLoadingMessage = 'Loading mapper configuration...';
    this.pageLoadingDetail = 'Fetching the resolved mapping configuration, schemas, sample payload state, and saved profiles.';
    this.api.getConfiguration(linkId, sourceEntity, targetEntity, {
      useCache: this.metadataCachingEnabled,
      forceRefresh
		}).subscribe({
			next: config => {
				void this.applyLoadedConfigurationAsync(config);
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to load mapping configuration.', 'Mapper configuration could not be loaded');
				this.isPageLoading = false;
				this.stopInitialLoadSequence();
			}
    });
  }

	private async applyLoadedConfigurationAsync(config: MappingSchemaConfiguration): Promise<void> {
		this.pageLoadingMessage = 'Loading mapper configuration...';
		this.pageLoadingDetail = 'Preparing field trees, mapping rows, preview data, and saved profile state.';

		this.configuration = config;
		this.expressionTools = this.buildExpressionTools(this.getConfiguredExpressionFunctions(config));
		this.loadExpressionFunctions();
		this.savedBaselineTestRun = this.readSavedBaselineTestRun(config.linkId, config.sourceEntity, config.targetEntity);
		this.comparisonSessionId = '';
		this.comparisonRunResult = null;
		this.hasExplicitSavedRunComparison = false;
		const storedSampleInputs = this.readStoredSampleInputs(config.linkId, config.sourceEntity, config.targetEntity);
		this.rememberSampleInputValues = storedSampleInputs.enabled;
		this.sampleInputValues = this.buildInputValues(config.sampleInputDefinitions ?? [], storedSampleInputs.values ?? this.sampleInputValues);
		this.targetSchemaEnrichmentInputValues = this.buildInputValues(this.getTargetSchemaEnrichmentInputDefinitions(config.targetSchema?.targetEnrichment), this.targetSchemaEnrichmentInputValues);
		this.transferByIdDraftValue = (config.transferByKeySetting?.appliedValue ?? '').trim();
		if (!this.metadataCachingEnabled) {
			this.loadTransferByKeySettingState(config.linkId, config.sourceEntity);
		}
		this.loadedSamplePayloadInputSignature = (!this.hasSampleInputs || config.samplePayloadRetrieved)
			? this.getCurrentSamplePayloadInputSignature()
			: null;
		this.selectedProfileName = config.activeProfileName?.trim() || '';
		this.profileName = this.selectedProfileName;
		this.selectedTemplateName = config.availableTemplates?.[0]?.name ?? '';
		this.templateName = this.selectedTemplateName;
		this.selectedTemplateMiniGridTab = 'create';
		this.selectedTemplateDefinition = null;
		this.selectedTemplateDefinitionError = '';
		this.savedProfileRowSignatures = null;
		this.initializeMapperScreenMode(config);
		if (this.selectedTemplateName) {
			this.loadSelectedTemplateDefinition(this.selectedTemplateName);
		}

		const effectiveSourceSchema = config.effectiveSourceSchema ?? config.sourceSchema;
		this.rootMappingNode = this.createEditorNode({
			kind: 'root',
			mappingId: null,
			parentId: null,
			name: config.mappingName || `${config.sourceEntity} -> ${config.targetEntity}`,
			sourceEntity: config.sourceEntity,
			targetEntity: config.targetEntity,
			sourceSchema: effectiveSourceSchema,
			targetSchema: config.targetSchema,
			sourceIdPath: this.resolveSourceIdPath(effectiveSourceSchema),
			sourceCollectionPath: '',
			targetRuntimeRoute: '',
			contextBindings: [],
			continueOnError: true,
			executionMode: 'AfterParentUpsert',
			runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates(config as { runtimeMessageTemplates?: MapperRuntimeMessageTemplates | null; })
		});
		this.selectedMappingNodeId = this.rootMappingNode.id;

		this.refreshRuntimeMessagePropertyExamples();
		this.createWritebacks = [];
		this.updateWritebacks = [];
		this.activeWritebackRuleSet = 'create';
		this.referenceFieldOptionsByEntity = config.lookupFieldOptionsByEntity ?? {};

		this.applyRowFilters();

		if (this.selectedProfileName.trim()) {
			this.pageLoadingMessage = 'Loading profile...';
			this.pageLoadingDetail = 'Applying the active saved profile before showing the mapper.';
			this.loadSelectedProfile();
			return;
		}

		if (this.hasRequiredSampleInputs()) {
			this.previewResponseJson = {};
			this.previewSourceJson = {};
			this.previewResultJson = {};
			this.lastActionMessage = 'Enter source sample input values to load a preview payload.';
			this.isPageLoading = false;
			this.stopInitialLoadSequence();
			return;
		}

		this.pageLoadingMessage = 'Loading mapper configuration...';
		this.pageLoadingDetail = 'Running the initial preview so field validation and sample output are ready immediately.';
		this.previewMappings();
	}

	  onSampleInputChanged(key: string, value: string): void {
	    this.sampleInputValues = {
	      ...this.sampleInputValues,
	      [key]: value ?? ''
	    };

		if (this.rememberSampleInputValues) {
			this.writeStoredSampleInputs();
		}

    if (this.loadedSamplePayloadInputSignature !== this.getCurrentSamplePayloadInputSignature()) {
      this.loadedSamplePayloadInputSignature = null;

      if (this.configuration) {
        this.configuration = {
          ...this.configuration,
          samplePayloadRetrieved: false,
          samplePayloadStatusMessage: null
        };
      }
    }
	  }

	onRememberSampleInputValuesChanged(checked: boolean): void {
		this.rememberSampleInputValues = checked;

		if (checked) {
			this.writeStoredSampleInputs();
			return;
		}

		this.removeStoredSampleInputs();
	}

	  loadSampleSourcePayload(): void {
    if (!this.configuration) {
      return;
    }

    if (this.hasRequiredSampleInputs()) {
      this.lastActionMessage = 'Required sample input values are missing.';
      return;
    }

		this.isSamplePayloadLoading = true;
		this.samplePayloadLoadingPhase = 'sample';

		this.ensureCurrentSamplePayload(true).subscribe({
			next: loaded => {
				if (!loaded) {
					this.isSamplePayloadLoading = false;
					this.samplePayloadLoadingPhase = null;
					return;
				}

				this.samplePayloadLoadingPhase = 'preview';
				this.previewMappings(false, () => {
					this.isSamplePayloadLoading = false;
					this.samplePayloadLoadingPhase = null;
				});
			},
			error: error => {
				this.isSamplePayloadLoading = false;
				this.samplePayloadLoadingPhase = null;
				this.showMapperApiError(error, 'Failed to load sample source payload.', 'Sample source payload could not be loaded');
				this.isPageLoading = false;
			}
    });
  }

	async applyTransferByKeySetting(): Promise<void> {
		if (!this.configuration || !this.transferByKeySetting) {
			return;
		}

		const transferSetting = this.transferByKeySetting;
		const appliedValue = this.transferByIdDraftValue.trim();
		if (!appliedValue) {
			this.lastActionMessage = `Enter ${transferSetting.inputKey} before applying the transfer override.`;
			return;
		}

		const confirmation = await Swal.fire({
			icon: 'warning',
			title: 'Apply transfer-by-key override?',
			text: transferSetting.warningMessage,
			showCancelButton: true,
			confirmButtonText: 'Apply setting',
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.isApplyingTransferByIdSetting = true;

		this.api.applyTransferByKeySetting(
			this.configuration.linkId,
			this.configuration.sourceEntity,
			this.buildTransferByKeyInputs(appliedValue)
		).subscribe({
				next: response => {
					if (this.configuration?.transferByKeySetting) {
						this.configuration = {
							...this.configuration,
							transferByKeySetting: {
								...this.configuration.transferByKeySetting,
								appliedValue: response.appliedValue
							}
						};
					}
					this.cacheCurrentConfiguration();

					this.transferByIdDraftValue = response.appliedValue ?? this.transferByIdDraftValue;
					this.testRunMenuOpen = false;

					this.lastActionMessage = response.message;
					notify({
						message: response.message,
						type: 'success',
						displayTime: 2000,
					});
					this.isApplyingTransferByIdSetting = false;
				},
				error: error => {
					this.showMapperApiError(error, 'Failed to apply transfer-by-key setting.', 'Transfer-by-key setting could not be applied');
					this.isApplyingTransferByIdSetting = false;
				}
			});
	}

  get sampleInputDefinitions(): MapperSampleInputDefinition[] {
    return this.configuration?.sampleInputDefinitions ?? [];
  }

  get hasSampleInputs(): boolean {
    return this.sampleInputDefinitions.length > 0;
  }

  get canLoadSamplePayload(): boolean {
    return !!this.configuration && !this.isSamplePayloadLoading && !this.hasRequiredSampleInputs();
  }

	get transferByKeySetting() {
		return this.configuration?.transferByKeySetting ?? null;
	}

	get targetSchemaEnrichmentInputDefinitions(): MapperSampleInputDefinition[] {
		return this.getTargetSchemaEnrichmentInputDefinitions(this.targetSchemaEnrichmentConfig);
	}

	get canApplyTransferByKeySetting(): boolean {
		if (!this.configuration || !this.transferByKeySetting || this.isApplyingTransferByIdSetting || this.isPageLoading) {
			return false;
		}

		return !!this.transferByIdDraftValue.trim();
	}

	get applyTransferByKeyButtonLabel(): string {
		return this.isApplyingTransferByIdSetting ? 'Applying...' : 'Use for next link run';
	}

	get transferByKeyAppliedValue(): string {
		return (this.transferByKeySetting?.appliedValue ?? '').trim();
	}

	get transferByIdPlaceholder(): string {
		return this.transferByKeySetting?.inputKey || 'Transfer by ID';
	}

	get hasAppliedTransferByKeySetting(): boolean {
		if (!this.transferByKeySetting) {
			return true;
		}

		return !!this.transferByKeyAppliedValue;
	}

	get loadSamplePayloadButtonLabel(): string {
    if (this.samplePayloadLoadingPhase === 'preview') {
      return 'Generating preview...';
    }

    if (this.isSamplePayloadLoading) {
      return 'Loading...';
    }

    return 'Load sample payload';
  }

  get metadataCacheStatusLabel(): string {
    return this.metadataCachingEnabled ? 'Enabled' : 'Disabled';
  }

  get hasCachedMetadata(): boolean {
    return this.api.hasCachedMetadata();
  }

  get showInitialLoader(): boolean {
    return this.isPageLoading && this.isInitialLoadSequence;
  }

	get showLoadingBanner(): boolean {
    return this.isPageLoading && !!this.configuration && !this.isInitialLoadSequence;
  }

	get canRefreshTargetSchemaEnrichment(): boolean {
		return this.hasTargetSchemaEnrichment
			&& !this.isTargetSchemaEnrichmentLoading
			&& !this.isPageLoading
			&& !this.hasMissingRequiredInputValues(this.targetSchemaEnrichmentInputDefinitions, this.targetSchemaEnrichmentInputValues);
	}

	get visibleConfiguration(): MappingSchemaConfiguration | null {
		return this.showInitialLoader ? null : this.configuration;
	}

	get showMapperLoadError(): boolean {
		return !this.showInitialLoader && !this.configuration && !!this.mapperLoadErrorMessage.trim();
	}

  get requiredSampleInputCount(): number {
    return this.sampleInputDefinitions.filter(definition => definition.required).length;
  }

  get completedRequiredSampleInputCount(): number {
    return this.sampleInputDefinitions.filter(definition =>
      !definition.required || !!(this.sampleInputValues[definition.key] ?? '').trim()
    ).filter(definition => definition.required).length;
  }

  get sampleInputStatusLabel(): string {
    if (!this.hasSampleInputs) {
      return 'Ready';
    }

    if (this.hasRequiredSampleInputs()) {
      return 'Setup required';
    }

    if (this.samplePayloadRetrieved) {
      return 'Sample retrieved';
    }

    if (this.hasSamplePayloadAttempt) {
      return 'Sample not retrieved';
    }

    return 'Load sample';
  }

  get sampleInputStatusTone(): 'ready' | 'attention' | 'danger' | 'muted' {
    if (this.hasSampleInputs && this.hasRequiredSampleInputs()) {
      return 'attention';
    }

    if (this.samplePayloadRetrieved) {
      return 'ready';
    }

    if (this.hasSamplePayloadAttempt) {
      return 'danger';
    }

    return 'muted';
  }

  get sampleInputHelperText(): string {
    if (!this.hasSampleInputs) {
      return `${this.sourceSystemName} already provides a ${this.sourceEntityLabel} sample, so preview and validation are ready immediately.`;
    }

    if (this.hasRequiredSampleInputs()) {
      return `Connect the ${this.sourceSystemName} ${this.sourceEntityLabel.toLowerCase()} first so preview and validation use a real sample payload.`;
    }

		if (this.samplePayloadRetrieved) {
			return `Use this sample to preview, validate, and refine the ${this.targetSystemName} mapping.`;
		}

    if (this.hasSamplePayloadAttempt) {
      return `The ${this.sourceSystemName} ${this.sourceEntityLabel.toLowerCase()} sample could not be retrieved. Update the details and try again.`;
    }

    return `Load a ${this.sourceSystemName} ${this.sourceEntityLabel.toLowerCase()} sample to unlock preview, validation, and field mapping.`;
  }

	get showSampleLoadSuccessIndicator(): boolean {
		return this.hasSampleInputs
			&& this.sampleInputsComplete
			&& this.samplePayloadRetrieved;
	}

	get showSampleLoadStatusText(): boolean {
		return this.hasSampleInputs
			&& this.sampleInputsComplete
			&& !!this.configuration?.samplePayloadStatusMessage
			&& !this.samplePayloadRetrieved;
	}

	get sampleLoadSuccessLabel(): string {
		return 'Sample loaded';
	}

  get isGridLocked(): boolean {
    return this.hasSampleInputs && (this.hasRequiredSampleInputs() || !this.samplePayloadRetrieved);
  }

  get gridLockTitle(): string {
    if (this.hasRequiredSampleInputs()) {
      return `Enter the required ${this.sourceSystemName} ${this.sourceEntityLabel.toLowerCase()} details to unlock this ${this.targetSystemName} mapping.`;
    }

    return `Load a valid ${this.sourceSystemName} ${this.sourceEntityLabel.toLowerCase()} sample to unlock this ${this.targetSystemName} mapping.`;
  }

  get gridLockMessage(): string {
    if (!this.requiredSampleInputCount) {
      return `Complete the required ${this.sourceSystemName} setup details to continue.`;
    }

    if (this.hasRequiredSampleInputs()) {
      return `Fill in the required ${this.sourceSystemName} sample details first. ${this.completedRequiredSampleInputCount}/${this.requiredSampleInputCount} required value${this.requiredSampleInputCount === 1 ? '' : 's'} ready.`;
    }

    return this.configuration?.samplePayloadStatusMessage
      || `The ${this.sourceSystemName} sample has not been retrieved yet. Load a valid sample before mapping ${this.targetSystemName} fields.`;
  }

  get workflowTone(): 'info' | 'warning' | 'danger' | 'success' {
    if (this.isPageLoading) {
      return 'info';
    }

    if (this.hasSampleInputs && this.hasRequiredSampleInputs()) {
      return 'warning';
    }

    if (this.hasSampleInputs && !this.samplePayloadRetrieved) {
      return 'warning';
    }

    if (this.mappingStats.invalid > 0) {
      return 'danger';
    }

    if (this.mappingStats.unmappedRequired > 0) {
      return 'warning';
    }

    if (this.validationSummary.length > 0 && this.mappingStats.invalid === 0) {
      return 'success';
    }

    return 'info';
  }

  get workflowTitle(): string {
    if (this.isPageLoading) {
      return this.pageLoadingMessage;
    }

    if (this.hasSampleInputs && this.hasRequiredSampleInputs()) {
      return 'Load a source sample first';
    }

    if (this.hasSampleInputs && !this.samplePayloadRetrieved) {
      return 'Retrieve a valid source sample';
    }

    if (this.mappingStats.invalid > 0) {
      return 'Validation issues need review';
    }

    if (this.mappingStats.unmappedRequired > 0) {
      return 'Required fields are still unfinished';
    }

    if (this.validationSummary.length > 0 && this.mappingStats.invalid === 0) {
      return 'Mapping is ready to save';
    }

    return 'Build the mapping step by step';
  }

  get workflowMessage(): string {
    if (this.isPageLoading) {
      return this.pageLoadingMessage;
    }

    if (this.hasSampleInputs && this.hasRequiredSampleInputs()) {
      return 'Complete the required sample values to unlock a realistic preview, validation, and result inspection flow.';
    }

    if (this.hasSampleInputs && !this.samplePayloadRetrieved) {
      return this.configuration?.samplePayloadStatusMessage
        || 'Retrieve a valid sample payload before mapping so preview and validation use real source data.';
    }

    if (this.mappingStats.invalid > 0) {
      return `${this.mappingStats.invalid} field${this.mappingStats.invalid === 1 ? '' : 's'} currently report validation issues. Review the status column and inspector output before saving.`;
    }

    if (this.mappingStats.unmappedRequired > 0) {
      return `${this.mappingStats.unmappedRequired} required field${this.mappingStats.unmappedRequired === 1 ? '' : 's'} still need a usable value. Use the Required and Unfinished filters to work through them quickly.`;
    }

    if (this.validationSummary.length > 0 && this.mappingStats.invalid === 0) {
      return 'Preview and validation currently look healthy. Save this as a profile once you are satisfied with the rules.';
    }

    return this.lastActionMessage || 'Use the filters and inspector to work through the target fields in a predictable order.';
  }

  get workflowSecondaryMessage(): string | null {
    if (!this.lastActionMessage || this.lastActionMessage === this.workflowMessage) {
      return null;
    }

    return this.lastActionMessage;
  }

  get currentWorkflowStep(): 1 | 2 | 3 {
    if (this.hasSampleInputs && (this.hasRequiredSampleInputs() || !this.samplePayloadRetrieved)) {
      return 1;
    }

    if (this.mappingStats.unmappedRequired > 0 || this.mappingStats.invalid > 0) {
      return 2;
    }

    return 3;
  }

  get mappingWorkflowMessage(): string {
    if (this.mappingStats.invalid > 0) {
      return `${this.mappingStats.invalid} field${this.mappingStats.invalid === 1 ? '' : 's'} still need attention.`;
    }

    if (this.mappingStats.unmappedRequired > 0) {
      return `${this.mappingStats.unmappedRequired} required field${this.mappingStats.unmappedRequired === 1 ? '' : 's'} are still unfinished.`;
    }

    return 'Required fields are covered.';
  }

  get finalizeWorkflowMessage(): string {
    if (this.mappingStats.invalid > 0) {
      return 'Resolve validation issues before saving a profile.';
    }

    if (this.validationSummary.length > 0) {
      return 'Preview looks healthy. Save when you are satisfied with the rules.';
    }

    return 'Run Preview or Validate, then save to a named profile.';
  }

	get basicReadinessItems(): Array<{ label: string; value: string; tone: 'success' | 'warning' | 'danger' | 'muted' | 'info' }> {
		const requiredReady = this.mappingStats.required > 0
			? `${this.mappingStats.mappedRequired}/${this.mappingStats.required}`
			: 'No required fields';

		return [
			{
				label: 'Required fields',
				value: requiredReady,
				tone: this.mappingStats.unmappedRequired > 0 ? 'warning' : 'success'
			},
			{
				label: 'Sample',
				value: this.sampleInputStatusLabel,
				tone: this.sampleInputStatusTone === 'ready' ? 'success' : this.sampleInputStatusTone === 'attention' ? 'warning' : this.sampleInputStatusTone === 'danger' ? 'danger' : 'muted'
			},
			{
				label: 'Validation',
				value: this.validationSummary.length ? (this.mappingStats.invalid > 0 ? `${this.mappingStats.invalid} issue${this.mappingStats.invalid === 1 ? '' : 's'}` : 'Validated') : 'Not run yet',
				tone: this.mappingStats.invalid > 0 ? 'danger' : this.validationSummary.length ? 'success' : 'muted'
			},
			{
				label: 'Test run',
				value: this.latestTestRunStateLabel,
				tone: this.hasLatestTestRunResult ? this.latestTestRunStateTone : 'muted'
			}
		];
	}

	get basicPreviewItems(): Array<{ label: string; value: string }> {
		const payload = this.previewResultJsonObject;
		if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
			return [];
		}

		return Object.keys(payload)
			.slice(0, 8)
			.map(key => ({
				label: key,
				value: this.stringifyPreviewValue(payload[key]) || 'Empty'
			}));
	}

	get hasBasicPreviewItems(): boolean {
		return this.basicPreviewItems.length > 0;
	}

	get basicActiveProfileSummary(): string {
		if (this.selectedProfileIsActive) {
			return 'This is the profile currently used when the link runs.';
		}

		if (this.activeProfileName) {
			return `Runtime profile is still ${this.activeProfileName}. Set this profile active when you are ready.`;
		}

		return 'No active runtime profile is selected yet.';
	}

	private get previewResultJsonObject(): any {
		return this.safeParseJson(this.previewResultJson, null);
	}

  get currentProfileLabel(): string {
    return this.profileName.trim() || this.selectedProfileName.trim() || 'Unsaved draft';
  }

	get activeProfileName(): string {
		return this.configuration?.activeProfileName?.trim() || '';
	}

	get hasSelectedProfile(): boolean {
		return !!this.selectedProfileName.trim();
	}

	get selectedProfileIsActive(): boolean {
		const selected = this.selectedProfileName.trim();
		return !!selected && selected === this.activeProfileName;
	}

	get canSetSelectedProfileActive(): boolean {
		return !!this.configuration && !!this.selectedProfileName.trim() && !this.selectedProfileIsActive && !this.isPageLoading;
	}

	get hasPendingMappingChanges(): boolean {
		return this.hasPendingMappingChangesValue;
	}

	get canSaveMapping(): boolean {
		return !this.isPageLoading && this.hasPendingMappingChanges;
	}

	get canDiscardMappingChanges(): boolean {
		return !this.isPageLoading && this.hasPendingMappingChanges;
	}

	get saveMappingButtonLabel(): string {
		return this.hasPendingMappingChanges ? 'Save' : 'No changes to save';
	}

	get saveMappingProfileButtonLabel(): string {
		return this.hasPendingMappingChanges ? 'Save profile' : 'No changes to save';
	}

	get discardMappingChangesButtonLabel(): string {
		return this.hasPendingMappingChanges ? 'Discard changes' : 'No changes to discard';
	}

	get resetUnsavedMappingsButtonLabel(): string {
		return this.hasPendingMappingChanges ? 'Reset unsaved' : 'No unsaved changes';
	}

	hasUnsavedMappingChanges(): boolean {
		return !this.isPageLoading && this.hasPendingMappingChanges;
	}

	async confirmDiscardPendingMappingChanges(): Promise<boolean> {
		if (!this.hasUnsavedMappingChanges()) {
			return true;
		}

		const result = await Swal.fire({
			icon: 'warning',
			title: 'Leave with unsaved mapper changes?',
			html: '<div style="text-align:left;line-height:1.5">You have mapper changes that have not been saved to the profile.<br><br>If you leave now, those changes will be lost.</div>',
			showCancelButton: true,
			confirmButtonText: 'Leave without saving',
			cancelButtonText: 'Stay on mapper',
			confirmButtonColor: '#d33',
			focusCancel: true
		});

		return result.isConfirmed;
	}

  get currentProfileSummaryText(): string {
    const meta = this.currentProfileMeta;

    if (!meta) {
      return 'Unsaved draft';
    }

		const timestamp = this.formatTimestamp(meta.lastModificationDate || meta.creationDate);
		return meta.isActive
			? `Active profile, updated ${timestamp}`
			: `Loaded for review, updated ${timestamp}`;
  }

	get currentProfileMeta(): MapperProfileSummary | null {
		const profileName = this.profileName.trim() || this.selectedProfileName.trim();
		if (!profileName) {
			return null;
		}

		return this.availableProfiles.find(profile => profile.name === profileName) ?? null;
	}

  get sourceWritableFieldOptions(): FieldOption[] {
    const sourceSchema = this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema;

    return this.flattenSchemaToFieldOptions(
      sourceSchema?.fields ?? [],
      '',
      '',
      field => this.isLeafField(field) && field.canUpdate
    );
  }

  get targetReadableFieldOptions(): FieldOption[] {
    return this.flattenSchemaToFieldOptions(
      this.configuration?.targetSchema?.fields ?? [],
      '',
      '',
      field => this.isLeafField(field) && field.canRead
    );
  }

	get targetWritebackFieldOptions(): FieldOption[] {
		return this.flattenSchemaToFieldOptions(
			this.configuration?.targetSchema?.fields ?? [],
			'',
			'',
			field => this.isLeafField(field) && (field.canRead || field.isKey)
		).sort((left, right) => {
			if (left.isKey !== right.isKey) {
				return left.isKey ? -1 : 1;
			}

			return left.label.localeCompare(right.label);
		});
	}

  get writebackDateFormatOptions(): string[] {
    return ['yyyy-MM-dd', 'yyyy-MM-ddTHH:mm:ss', 'O'];
  }

	get filteredRuntimeMessageSourceTreeItems(): TreeNode[] {
		return this.filterTreeNodes(this.runtimeMessageSourceTreeItems, this.messageVariableSourceSearch);
	}

	get filteredRuntimeMessageTargetTreeItems(): TreeNode[] {
		return this.filterTreeNodes(this.runtimeMessageTargetTreeItems, this.messageVariableTargetSearch);
	}

	get runtimeMessageSourceVariableDisplayValue(): string {
		return 'Select source property';
	}

	get runtimeMessageTargetVariableDisplayValue(): string {
		return 'Select target property';
	}

	get canRunTestLink(): boolean {
		return !!this.linkId
			&& this.linkId > 0
			&& !this.isTestRunStarting
			&& !this.isLinkRunning
			&& this.hasAppliedTransferByKeySetting;
	}

	get testRunButtonLabel(): string {
		if (this.isTestRunStarting) {
			return 'Starting...';
		}

		if (this.isLinkRunning) {
			return 'Running...';
		}

		return this.transferByKeySetting ? 'Run transfer test' : 'Test run';
	}

	get testRunButtonTitle(): string | null {
		if (!this.hasAppliedTransferByKeySetting && this.transferByKeySetting) {
			return `Use '${this.applyTransferByKeyButtonLabel}' in Source sample first.`;
		}

		return null;
	}

	get showTransferByIdTestRunFlow(): boolean {
		return !!this.transferByKeySetting;
	}

	get testRunMenuLabel(): string {
		if (!this.transferByKeySetting) {
			return 'Test run';
		}

		return 'Transfer test';
	}

	get testRunMenuHint(): string {
		if (!this.transferByKeySetting) {
			return this.testRunMenuOpen ? 'Close' : 'Run';
		}

		if (this.testRunMenuOpen) {
			return 'Close';
		}

		return this.hasAppliedTransferByKeySetting ? 'Ready' : 'Set ID';
	}

	toggleTestRunMenu(): void {
		if (this.isPageLoading || this.isTestRunStarting || this.isLinkRunning) {
			return;
		}

		const willOpen = !this.testRunMenuOpen;
		if (willOpen) {
			this.profileMenuOpen = false;
			this.closeAdminRunMenu();
		}

		if (willOpen && this.transferByKeySetting) {
			this.transferByIdDraftValue = this.transferByKeyAppliedValue;
		}

		this.testRunMenuOpen = willOpen;
	}

	toggleAdminRunMenu(): void {
		if (this.isPageLoading) {
			return;
		}

		const willOpen = !this.adminRunMenuOpen;
		if (willOpen) {
			this.profileMenuOpen = false;
			this.testRunMenuOpen = false;
			this.adminRunMenuOpen = true;
			return;
		}

		this.closeAdminRunMenu();
	}

	private closeAdminRunMenu(): void {
		if (!this.adminRunMenuOpen) {
			return;
		}

		this.adminRunMenuOpen = false;
		this.comparisonSessionId = '';
		this.comparisonRunResult = null;
		this.hasExplicitSavedRunComparison = false;
	}

	onComparisonSessionIdChanged(value: string): void {
		this.comparisonSessionId = value;
		this.comparisonRunResult = null;
		this.hasExplicitSavedRunComparison = false;
	}

	get hasLatestTestRunResult(): boolean {
		return !!this.latestTestRunResult;
	}

	get showLatestTestRunCard(): boolean {
		return this.isLoadingLatestTestRunResult
			|| !!this.activeTestRunSessionId
			|| this.isLinkRunning
			|| this.hasMapperRunProgress
			|| this.hasLatestTestRunResult;
	}

	get latestTestRunSummaryItems(): Array<{ label: string; value: number; tone: 'success' | 'danger' | 'muted' }> {
		const result = this.latestTestRunResult;
		if (!result) {
			return [];
		}

		return [
			{ label: 'Created', value: result.createdCount ?? 0, tone: (result.createdCount ?? 0) > 0 ? 'success' : 'muted' },
			{ label: 'Updated', value: result.updatedCount ?? 0, tone: (result.updatedCount ?? 0) > 0 ? 'success' : 'muted' },
			{ label: 'Validation', value: result.validationFailures ?? 0, tone: (result.validationFailures ?? 0) > 0 ? 'danger' : 'muted' },
			{ label: 'Upsert', value: result.upsertFailures ?? 0, tone: (result.upsertFailures ?? 0) > 0 ? 'danger' : 'muted' },
			{ label: 'Writeback', value: result.writebackFailures ?? 0, tone: (result.writebackFailures ?? 0) > 0 ? 'danger' : 'muted' }
		];
	}

	get latestTestRunMessages(): NonNullable<MapperTestRunResult['messages']> {
		return this.latestTestRunResult?.messages ?? [];
	}

	get canUseAdminRunTools(): boolean {
		return true;
	}

	get captureArtifactsButtonLabel(): string {
		return this.captureArtifactsForSession ? 'Artifact capture enabled' : 'Enable artifact capture';
	}

	get captureArtifactsSummaryText(): string {
		return this.captureArtifactsForSession
			? 'Mapper test runs will store source and target payload artifacts until you disable capture in this browser.'
			: 'Artifact payload capture is off by default. Enable it only when you explicitly need source and target payloads stored for mapper test runs.';
	}

	get adminRunToolsToggleLabel(): string {
		return this.adminRunMenuOpen ? 'Close' : 'Run tools';
	}

	get canSaveLatestRunAsBaseline(): boolean {
		return this.canUseAdminRunTools && !!this.latestTestRunResult?.isCompleted && !this.isLoadingLatestTestRunResult;
	}

	get saveLatestRunAsBaselineButtonLabel(): string {
		return this.savedBaselineTestRun ? 'Replace saved run' : 'Save as test run';
	}

	get hasSavedBaselineTestRun(): boolean {
		return this.canUseAdminRunTools && !!this.savedBaselineTestRun;
	}

	get savedBaselineSummaryText(): string {
		const baseline = this.savedBaselineTestRun;
		if (!baseline) {
			return 'No saved test run baseline yet.';
		}

		const savedAt = baseline.savedAt ? this.formatTimestamp(baseline.savedAt) : 'unknown time';
		const profile = baseline.profileName?.trim();
		return profile
			? `Saved from session ${baseline.sessionId} using profile ${profile} on ${savedAt}`
			: `Saved from session ${baseline.sessionId} on ${savedAt}`;
	}

	get canCompareAnotherRunAgainstSavedBaseline(): boolean {
		const sessionId = this.comparisonSessionId.trim();
		return this.canUseAdminRunTools
			&& !!this.savedBaselineTestRun
			&& !!this.linkId
			&& !!this.configuration?.sourceEntity
			&& !!this.configuration?.targetEntity
			&& !this.isLoadingComparisonRun
			&& !!sessionId
			&& !this.isComparingSavedBaselineToSameSession(sessionId);
	}

	get compareRunButtonLabel(): string {
		return this.isLoadingComparisonRun ? 'Comparing...' : 'Compare run';
	}

	get hasSavedRunComparisonLog(): boolean {
		return this.canUseAdminRunTools && this.hasExplicitSavedRunComparison && this.savedRunComparisonLog.length > 0;
	}

	private refreshSavedRunComparisonState(): void {
		this.savedRunComparisonLog = this.hasExplicitSavedRunComparisonValue
			? this.buildSavedRunComparisonLog(this.savedBaselineTestRunValue?.result ?? null, this.comparisonRunResultValue)
			: [];
	}

	get savedRunComparisonSummaryText(): string {
		if (!this.savedBaselineTestRun) {
			return 'Save a completed transfer-single run first.';
		}

		if (this.isLoadingComparisonRun) {
			return 'Loading the selected session for comparison.';
		}

		if (!this.comparisonRunResult) {
			return 'Enter a different completed mapper run session id to compare its actual run data with the saved baseline.';
		}

		return `Comparing saved session ${this.savedBaselineTestRun.sessionId} with session ${this.comparisonRunResult.sessionId}.`;
	}

	get hasMapperRunProgress(): boolean {
		return !!this.mapperRunProgressStage || !!this.mapperRunProgressMessage;
	}

	get mapperRunProgressPercent(): number {
		if (!this.mapperRunProgressTotalCount || this.mapperRunProgressTotalCount <= 0) {
			return this.mapperRunProgressStage ? 10 : 0;
		}

		const rawPercent = Math.round((this.mapperRunProgressProcessedCount / this.mapperRunProgressTotalCount) * 100);
		return Math.max(0, Math.min(100, rawPercent));
	}

	get mapperRunStageFlowIndex(): number {
		const flowKey = this.getMapperRunStageFlowKey(this.mapperRunProgressStage);
		return this.mapperRunStageFlow.findIndex(stage => stage.key === flowKey);
	}

	get mapperRunStageFlowPositionLabel(): string {
		const currentIndex = this.mapperRunStageFlowIndex;
		if (currentIndex < 0) {
			return 'Waiting to start';
		}

		return `Stage ${currentIndex + 1} of ${this.mapperRunStageFlow.length}`;
	}

	get mapperRunProgressCountLabel(): string {
		switch (this.getMapperRunStageFlowKey(this.mapperRunProgressStage)) {
			case 'Upserting':
				return 'Upsert items';
			case 'Writeback':
				return 'Writeback items';
			default:
				return 'Source records';
		}
	}

	get mapperRunProgressDisplayStage(): string {
		switch (this.mapperRunProgressStage) {
			case 'Queued':
				return 'Queued';
			case 'Initializing':
				return 'Preparing mapper run';
			case 'SourceLoaded':
				return 'Loading source records';
			case 'Validating':
				return 'Validating mappings';
			case 'ValidationCompleted':
				return 'Validation finished';
			case 'Upserting':
				return 'Upserting target records';
			case 'Writeback':
				return 'Applying writeback';
			case 'WritebackCompleted':
				return 'Writeback finished';
			case 'Completed':
				return 'Finished';
			default:
				return this.mapperRunProgressStage || 'Running';
		}
	}

	get mapperRunProgressDisplayMessage(): string {
		const message = this.mapperRunProgressMessage?.trim();
		if (message) {
			return message;
		}

		switch (this.mapperRunProgressStage) {
			case 'Queued':
				return 'Waiting for the mapper runtime to start.';
			case 'Initializing':
				return 'Preparing the generic mapper runtime for this session.';
			case 'SourceLoaded':
				return 'Loading the source records selected for this mapper run.';
			case 'Validating':
				return 'Checking mapped fields before target upsert begins.';
			case 'ValidationCompleted':
				return 'Validation is done and valid records are ready to sync.';
			case 'Upserting':
				return 'Creating or updating target records using this mapper profile.';
			case 'Writeback':
				return 'Writing mapper results back to the source system where configured.';
			case 'WritebackCompleted':
				return 'Writeback has finished. Finalizing mapper results.';
			case 'Completed':
				return 'The mapper session finished. Loading final results.';
			default:
				return '';
		}
	}

	private getMapperRunStageFlowKey(stage: string): string {
		switch (stage) {
			case 'Queued':
				return 'Queued';
			case 'Initializing':
				return 'Initializing';
			case 'SourceLoaded':
				return 'SourceLoaded';
			case 'Validating':
			case 'ValidationCompleted':
				return 'Validating';
			case 'Upserting':
				return 'Upserting';
			case 'Writeback':
			case 'WritebackCompleted':
				return 'Writeback';
			case 'Completed':
				return 'Completed';
			default:
				return '';
		}
	}

	get latestTestRunStateLabel(): string {
		if (this.isLoadingLatestTestRunResult) {
			return 'Loading results';
		}

		if (this.isLinkRunning) {
			return 'Running';
		}

		if (this.hasLatestTestRunFailure) {
			return 'Failed';
		}

		if (this.latestTestRunResult?.isCompleted) {
			return 'Completed';
		}

		if (this.activeTestRunSessionId) {
			return 'Queued';
		}

		return 'Idle';
	}

	get latestTestRunStateTone(): 'info' | 'success' | 'danger' | 'muted' {
		if (this.isLoadingLatestTestRunResult || this.isLinkRunning || this.activeTestRunSessionId) {
			return 'info';
		}

		if (this.hasLatestTestRunFailure) {
			return 'danger';
		}

		if (this.latestTestRunResult?.isCompleted) {
			return 'success';
		}

		return 'muted';
	}

	get latestTestRunHeadline(): string {
		if (this.isLoadingLatestTestRunResult) {
			return 'Loading mapper run results...';
		}

		if (this.isLinkRunning) {
			return 'Mapper test run is in progress.';
		}

		if (this.hasLatestTestRunFailure) {
			return 'Latest mapper test run failed';
		}

		if (this.latestTestRunResult?.isCompleted) {
			return 'Latest mapper test run results';
		}

		if (this.activeTestRunSessionId) {
			return 'Mapper test run queued for execution.';
		}

		return 'Start a test run to inspect mapper results here.';
	}

  get sourceSystemName(): string {
    return this.selectedMappingNode?.sourceSchema?.systemName ?? this.configuration?.sourceSchema?.systemName ?? 'Source';
  }

  get targetSystemName(): string {
    return this.selectedMappingNode?.targetSchema?.systemName ?? this.configuration?.targetSchema?.systemName ?? 'Target';
  }

  get sourceEntityLabel(): string {
    return this.selectedMappingNode?.sourceSchema?.entityName ?? this.configuration?.sourceSchema?.entityName ?? 'source record';
  }

  get targetEntityLabel(): string {
    return this.selectedMappingNode?.targetSchema?.entityName ?? this.configuration?.targetSchema?.entityName ?? 'target record';
  }

  get canShowUpdateTab(): boolean {
    return !!this.selectedMappingNode?.targetSchema?.canUpdate;
  }

  get currentRuleTab(): 'create' | 'update' {
    return this.activeMapperTab === 'update' ? 'update' : 'create';
  }

	get mappingGridLayoutStorageKey(): string {
		const node = this.selectedMappingNode;
		const parts = [
			'application-link',
			'generic-mapper-grid-layout',
			String(this.linkId ?? this.configuration?.linkId ?? 0),
			node?.sourceSchema?.id ?? this.configuration?.sourceSchema?.id ?? this.sourceEntity ?? 'source',
			node?.targetSchema?.id ?? this.configuration?.targetSchema?.id ?? this.targetEntity ?? 'target',
			node?.sourceEntity ?? this.sourceEntity ?? 'source-entity',
			node?.targetEntity ?? this.targetEntity ?? 'target-entity',
			node?.ownerTargetPath ?? 'root',
			this.currentRuleTab
		];

		return parts
			.map(part => String(part).trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, '-'))
			.join(':');
	}

  get mappingRows(): MappingGridRow[] {
    return this.getRowsForTab(this.currentRuleTab);
  }

  set mappingRows(value: MappingGridRow[]) {
    this.setRowsForTab(this.currentRuleTab, value);
  }

	get filteredMappingRows(): MappingGridRow[] {
		return this.getFilteredRowsForTab(this.currentRuleTab);
	}

  set filteredMappingRows(value: MappingGridRow[]) {
    this.setFilteredRowsForTab(this.currentRuleTab, value);
  }

  get canCopyFromOtherTemplate(): boolean {
    return this.activeMapperTab === 'create' || this.activeMapperTab === 'update';
  }

  get copyFromTemplateLabel(): string {
    return this.currentRuleTab === 'create' ? 'Copy from Update' : 'Copy from Create';
  }

  get currentTabTitle(): string {
    switch (this.activeMapperTab) {
      case 'update':
        return `Update ${this.targetEntityLabel}`;
      case 'messages':
        return 'Runtime messages';
      case 'writeback':
        return 'What happens here?';
      default:
        return `Create ${this.targetEntityLabel}`;
    }
  }

  get currentTabDescription(): string {
    switch (this.activeMapperTab) {
      case 'update':
        return `Configure how existing ${this.targetSystemName} ${this.targetEntityLabel.toLowerCase()} records should be updated when the integration supports updates.`;
      case 'messages':
        return 'Configure optional profile-level success and failure messages written while the generic mapper runs.';
      case 'writeback':
		return `Configure separate ${this.sourceSystemName} writebacks for newly created and existing ${this.targetSystemName} records.`;
      default:
        return `Configure how ${this.sourceSystemName} values are mapped into new ${this.targetSystemName} ${this.targetEntityLabel.toLowerCase()} records.`;
    }
  }

  get selectedNodeTitle(): string {
    const node = this.selectedMappingNode;
    if (!node) {
      return 'Mapping node';
    }

    return node.name?.trim() || `${node.sourceEntity} -> ${node.targetEntity}`;
  }

  get selectedNodeSubtitle(): string {
    const node = this.selectedMappingNode;
    if (!node) {
      return '';
    }

    if (this.isInlineChildObjectNode(node) && node.ownerTargetPath?.trim()) {
      return `${node.targetEntity} inline object at ${node.ownerTargetPath}`;
    }

    return node.sourceCollectionPath?.trim()
      ? `${node.sourceEntity} -> ${node.targetEntity} via ${node.sourceCollectionPath}`
      : `${node.sourceEntity} -> ${node.targetEntity} from parent source record`;
  }

  get selectedNodeIsRoot(): boolean {
    return this.selectedMappingNode?.kind === 'root';
  }

	readonly childContextBindingModeOptions: Array<{ value: ChildContextBindingMode; label: string }> = [
	  { value: 'ParentSourceValue', label: 'Parent source' },
	  { value: 'ParentPayloadValue', label: 'Parent mapped payload' },
	  { value: 'ParentTargetValue', label: 'Parent target result' }
	];

	readonly childExecutionModeOptions: Array<{ value: string; label: string }> = [
		{ value: 'AfterParentUpsert', label: 'After parent upsert' },
		{ value: 'BeforeParentUpsert', label: 'Before parent upsert' }
	];

  get selectedRowStatusLabel(): string {
    if (!this.selectedRow) {
      return 'No field selected';
    }

    switch (this.getRowState(this.selectedRow)) {
      case 'ready':
        return 'Ready';
      case 'issue':
        return 'Needs attention';
      case 'ignored':
        return 'Ignored';
      case 'readonly':
        return 'Read-only';
      case 'loading':
        return 'Refreshing';
      default:
        return 'Needs input';
    }
  }

  get selectedRowStatusTone(): 'info' | 'warning' | 'danger' | 'success' | 'muted' {
    if (!this.selectedRow) {
      return 'muted';
    }

    switch (this.getRowState(this.selectedRow)) {
      case 'ready':
        return 'success';
      case 'issue':
        return 'danger';
      case 'ignored':
        return 'muted';
      case 'readonly':
        return 'muted';
      case 'loading':
        return 'info';
      default:
        return 'warning';
    }
  }

  get selectedRowGuidance(): string {
    if (!this.selectedRow) {
      return '';
    }

    if (this.selectedRow.mode === 'Lookup') {
      const criterionCount = this.selectedRow.lookup?.criteria?.length ?? 0;

      if (!criterionCount) {
        return 'Add one or more match rules to find the related record.';
      }

      return criterionCount === 1
        ? 'This field uses 1 lookup condition to find the related record.'
        : `This field uses ${criterionCount} lookup conditions to find the related record.`;
    }

    if (this.selectedRow.validationMessage) {
      return this.selectedRow.validationMessage;
    }

    if (this.selectedRow.description?.trim()) {
      return this.selectedRow.description.trim();
    }

    return this.getModeDescription(this.selectedRow);
  }

  get selectedRowLinkKeyLabel(): string {
    if (!this.selectedRow) {
      return '';
    }

    return this.selectedRow.isLinkKey ? 'Used to match existing target records.' : 'Not used for record matching.';
  }

  formatTimestamp(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) {
      return value;
    }

    return timestamp.toLocaleString();
  }

  formatPreviewValue(value: any): string {
    return this.stringifyPreviewValue(value) || 'No preview yet';
  }

  getSelectedRowSourceSummary(): string {
    if (!this.selectedRow) {
      return '';
    }

    if (this.selectedRow.mode === 'Lookup') {
      const referenceEntity =
        this.selectedRow.lookup?.referenceEntityName ||
        this.selectedRow.referenceEntityName ||
        this.selectedRow.reference?.targetEntity ||
        'related entity';

      const returnField = this.selectedRow.lookup?.returnFieldPath;
      if (returnField) {
        return `${referenceEntity} -> ${returnField}`;
      }

      return `Find record in ${referenceEntity}`;
    }

    return this.selectedRow.sourceFieldPath || 'Not selected';
  }

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.profileMenuOpen && !this.testRunMenuOpen && !this.adminRunMenuOpen && !this.sampleOptionsExpanded) {
			return;
		}

    const target = event.target as HTMLElement | null;
    if (target?.closest('.profile-menu')) {
      return;
    }

		if (target?.closest('.test-run-menu')) {
			return;
		}

		if (target?.closest('.admin-run-menu')) {
			return;
		}

		if (target?.closest('.sample-load-menu')) {
			return;
		}

		if (!this.elementRef.nativeElement.contains(target as Node)) {
			this.profileMenuOpen = false;
			this.testRunMenuOpen = false;
			this.adminRunMenuOpen = false;
			this.sampleOptionsExpanded = false;
			return;
		}

		this.profileMenuOpen = false;
		this.testRunMenuOpen = false;
		this.adminRunMenuOpen = false;
		this.sampleOptionsExpanded = false;
	}

	@HostListener('document:keydown.escape')
	onEscapeKey(): void {
		this.profileMenuOpen = false;
		this.testRunMenuOpen = false;
		this.adminRunMenuOpen = false;
		this.sampleOptionsExpanded = false;
		this.mapperDocumentationOpen = false;
	}

	get selectedMapperGuideRow(): MappingGridRow {
		return this.mapperGuideRows.find(row => row.targetFieldId === this.selectedMapperGuideRowId) ?? this.mapperGuideRows[0];
	}

	get selectedMapperGuideCaseDetail(): MapperGuideCaseDetail {
		return this.mapperGuideCaseDetails[this.selectedMapperGuideRow.targetFieldId] ?? {
			tabs: ['Create', 'Update'],
			title: this.selectedMapperGuideRow.targetDisplayName,
			summary: this.selectedMapperGuideRow.description || 'Try this mapper grid feature with demo-only data.',
			steps: ['Change the grid row and click Preview.', 'The example updates only this guide row.'],
			exampleInput: 'Demo source value',
			exampleResult: this.formatPreviewValue(this.selectedMapperGuideRow.previewValue)
		};
	}

	isMapperGuideHeaderActive(header: 'Create' | 'Update' | 'Writeback' | 'Messages'): boolean {
		return this.selectedMapperGuideCaseDetail.tabs.includes(header);
	}

	selectMapperGuideRow(row: MappingGridRow): void {
		this.selectedMapperGuideRowId = row.targetFieldId;
		this.selectedMapperGuideRows = [row];
	}

	resetSelectedMapperGuideRow(): void {
		const resetRow = createMapperGuideRowById(
			this.selectedMapperGuideRowId,
			this.mapperGuideTargetSystemName,
			this.mapperGuideSourceFieldOptions
		);

		if (!resetRow) {
			return;
		}

		const index = this.mapperGuideRows.findIndex(row => row.targetFieldId === resetRow.targetFieldId);
		if (index >= 0) {
			this.mapperGuideRows.splice(index, 1, resetRow);
		}

		this.selectedMapperGuideRows = [resetRow];
	}

	onMapperGuideModeChanged(row: MappingGridRow): void {
		row.previewValue = undefined;
		row.isValid = undefined;
		row.validationMessage = null;

		if (row.mode === 'Ignore') {
			row.isLinkKey = false;
			row.sourceFieldPath = null;
			row.value = null;
			row.lookup = null;
			row.picklistMapping = null;
		} else if (row.mode === 'SourceField') {
			row.value = null;
			row.lookup = null;
			row.picklistMapping = null;
			row.sourceFieldPath ||= this.mapperGuideSourceFieldOptions[0]?.path ?? null;
		} else if (row.mode === 'Constant') {
			row.sourceFieldPath = null;
			row.lookup = null;
			row.picklistMapping = null;
			row.value ||= row.isPicklist ? row.targetPicklistOptions?.[0]?.value ?? '' : 'Sample value';
		} else if (row.mode === 'Expression') {
			row.sourceFieldPath = null;
			row.lookup = null;
			row.picklistMapping = null;
			row.value ||= getMapperGuideSampleExpression(row);
		} else if (row.mode === 'Lookup') {
			row.sourceFieldPath = null;
			row.value = null;
			row.picklistMapping = null;
			row.lookup = createMapperGuideLookup(row);
		}

		this.onMapperGuidePreviewRequested(row);
	}

	onMapperGuidePreviewRequested(row: MappingGridRow | null = null): void {
		const rows = row ? [row] : this.mapperGuideRows;
		for (const guideRow of rows) {
			guideRow.previewValue = resolveMapperGuidePreviewValue(guideRow);
			guideRow.isValid = !isMapperGuideRowUnmapped(guideRow);
			guideRow.validationMessage = guideRow.isValid ? null : 'Try mapping this row first.';
		}
	}

	onMapperGuideExpressionEditRequested(row: MappingGridRow): void {
		row.mode = 'Expression';
		row.value ||= getMapperGuideSampleExpression(row);
		this.guideExpressionEditingRow = row;
		this.expressionEditorUsesGuideFields = true;
		this.expressionEditingTargetFieldId = row.targetFieldId;
		this.lookupExpressionCriterionIndex = null;
		this.expressionFieldSearch = '';
		this.expressionTargetFieldSearch = '';
		this.expressionSourceTreeItems = this.mapperGuideSourceTreeItems;
		this.expressionTargetTreeItems = this.mapperGuideTargetTreeItems;
		this.resetExpressionTestResult();
		this.expressionTipsExpanded = false;
		this.expressionDraft = this.normalizeExpressionForEditor(row.value);
		this.selectedExpressionSourceField = null;
		this.expressionPopupVisible = true;
		this.closeExpressionSuggestions();
	}

	onMapperGuideLookupEditRequested(row: MappingGridRow): void {
		this.lookupEditingDraftRow = {
			...row,
			mode: 'Lookup',
			lookup: this.cloneJsonValue(row.lookup ?? createMapperGuideLookup(row))
		};
		this.lookupEditingRow = row;
		this.guideLookupEditorActive = true;
		this.resetLookupTestState();
		this.lookupPopupVisible = true;
	}

	openMapperGuidePicklistMappingEditor(row: MappingGridRow): void {
		if (!row.sourceFieldPath) {
			return;
		}

		const sourceField = this.mapperGuideSourceFieldOptions.find(x => x.path === row.sourceFieldPath);
		if (!sourceField?.isPicklist) {
			return;
		}

		this.picklistMappingDraft = this.cloneJsonValue(row.picklistMapping) ?? {
			sourceFieldPath: sourceField.path,
			items: (sourceField.picklistOptions ?? []).map(option => ({
				sourceValue: option.value,
				sourceLabel: option.label,
				targetValue: null
			}))
		};

		this.picklistMappingRow = row;
		this.picklistMappingSourceField = sourceField;
		this.guidePicklistMappingActive = true;
		this.picklistMappingPopupVisible = true;
	}

	onMapperGuideLinkKeyChanged(changedRow: MappingGridRow): void {
		if (changedRow.isLinkKey) {
			for (const row of this.mapperGuideRows) {
				if (row !== changedRow) {
					row.isLinkKey = false;
				}
			}
		}

		this.selectedMapperGuideRowId = changedRow.targetFieldId;
		this.onMapperGuidePreviewRequested(changedRow);
	}

	getMapperGuideLookupReferenceFieldOptions(row: MappingGridRow | null): FieldOption[] {
		return resolveMapperGuideLookupReferenceFieldOptions(row, this.mapperGuideReferenceFieldOptionsByEntity);
	}

  private getCompatibleSourceFieldOptionsForRow(row: MappingGridRow): FieldOption[] {
    return [...this.sourceFieldOptions]
      .filter(option => this.isSourceFieldCompatibleForRow(row, option))
      .sort((a, b) => {
        const scoreDiff = this.getSourceFieldScoreForRow(row, a) - this.getSourceFieldScoreForRow(row, b);
        if (scoreDiff !== 0) {
          return scoreDiff;
        }

        const exactPathA = a.path.toLowerCase() === row.targetPath.toLowerCase() ? 0 : 1;
        const exactPathB = b.path.toLowerCase() === row.targetPath.toLowerCase() ? 0 : 1;
        if (exactPathA !== exactPathB) {
          return exactPathA - exactPathB;
        }

        const exactNameA = a.label.toLowerCase().includes(row.targetDisplayName.toLowerCase()) ? 0 : 1;
        const exactNameB = b.label.toLowerCase().includes(row.targetDisplayName.toLowerCase()) ? 0 : 1;
        if (exactNameA !== exactNameB) {
          return exactNameA - exactNameB;
        }

        return a.label.localeCompare(b.label);
      });
  }

  private isSourceFieldCompatibleForRow(row: MappingGridRow, option: FieldOption): boolean {
    if (row.isReference) {
      return option.isReference || option.isKey || option.isUniqueKey || option.dataType === 'String';
    }

    if (!row.isPicklist) {
      return true;
    }

    if (option.isPicklist) {
      return true;
    }

    return ['String', 'Integer', 'Decimal', 'Boolean'].includes(option.dataType);
  }

  insertExpressionExample(example: string): void {
    this.applyExpressionInsertion(example, false);
  }

  private getSourceFieldScoreForRow(row: MappingGridRow, option: FieldOption): number {
    if (row.isReference) {
      if (option.isReference) {
        return 0;
      }

      if (option.isKey || option.isUniqueKey) {
        return 1;
      }

      if (option.dataType === 'String') {
        return 2;
      }

      return 10;
    }

    if (!row.isPicklist) {
      if (option.dataType === row.targetDataType) {
        return 0;
      }

      if (option.isPicklist) {
        return 3;
      }

      return 10;
    }

    if (option.isPicklist) {
      return 0;
    }

    if (option.dataType === 'String') {
      return 1;
    }

    if (option.dataType === 'Integer' || option.dataType === 'Decimal') {
      return 2;
    }

    if (option.dataType === 'Boolean') {
      return 3;
    }

    return 10;
  }

  onRowSelected(row: MappingGridRow): void {
    this.selectedRow = row;
    this.selectedSourcePath = row.sourceFieldPath ?? undefined;
    this.selectedTargetPath = row.targetPath ?? undefined;
  }

  selectMappingNode(nodeId: string): void {
    if (!this.rootMappingNode) {
      return;
    }

    if (this.rootMappingNode.id === nodeId) {
      this.selectedMappingNodeId = this.rootMappingNode.id;
      this.selectedRow = null;
      this.selectedSourcePath = undefined;
      this.selectedTargetPath = undefined;
      this.applyRowFilters();
      this.refreshRuntimeMessagePropertyExamples();
      return;
    }

    const nextNode = this.findNodeById(this.rootMappingNode, nodeId);
    if (!nextNode) {
      return;
    }

    this.selectedMappingNodeId = nextNode.id;
    this.selectedRow = null;
    this.selectedSourcePath = undefined;
    this.selectedTargetPath = undefined;
    this.applyRowFilters();
    this.refreshRuntimeMessagePropertyExamples();
  }

  selectRootMappingNode(): void {
    if (!this.rootMappingNode) {
      return;
    }

    this.selectMappingNode(this.rootMappingNode.id);
  }

  onObjectTabPointerDown(event: PointerEvent, nodeId: string): void {
    event.preventDefault();
    this.selectMappingNode(nodeId);
  }

  openChildMappingFromRow(row: MappingGridRow): void {
    const parentNode = this.selectedMappingNode;
    if (!parentNode || !this.isChildMappingRow(row)) {
      return;
    }

    const existingChild = this.findChildNodeByOwner(parentNode, row.targetFieldId);
    if (existingChild) {
      this.selectMappingNode(existingChild.id);
      return;
    }

    const defaultSourceEntity = this.availableSourceEntities.find(entity => entity.supportsSourceMapping)?.entityName
      || this.configuration?.sourceEntity
      || parentNode.sourceEntity;
    const defaultTargetEntity = row.referenceEntityName
      || this.availableTargetEntities.find(entity => entity.supportsTargetMapping)?.entityName
      || this.configuration?.targetEntity
      || parentNode.targetEntity;

    if (row.isChildObject && !row.isCollection) {
      const inlineTargetSchema = this.buildInlineChildTargetSchema(parentNode.targetSchema, row.targetPath, row.targetDisplayName);
      const childNode = this.createEditorNode({
        kind: 'child',
        mappingId: null,
        parentId: parentNode.id,
        ownerTargetFieldId: row.targetFieldId,
        ownerTargetPath: row.targetPath,
        inlineChildObject: true,
        name: row.targetDisplayName,
        sourceEntity: parentNode.sourceEntity,
        targetEntity: parentNode.targetEntity,
        sourceIdPath: parentNode.sourceIdPath,
        sourceSchema: parentNode.sourceSchema,
        targetSchema: inlineTargetSchema,
        sourceCollectionPath: '',
        targetRuntimeRoute: parentNode.targetRuntimeRoute,
        contextBindings: [],
        continueOnError: true,
        executionMode: 'AfterParentUpsert',
        runtimeMessageTemplates: null
      });

      parentNode.children = [...parentNode.children, childNode];
      this.syncChildMappingCounts(parentNode);
      this.selectMappingNode(childNode.id);
      return;
    }

    void this.createChildNodeAsync(parentNode, row, defaultSourceEntity, defaultTargetEntity);
  }

  removeSelectedMappingNode(): void {
    const selectedNode = this.selectedMappingNode;
    if (!selectedNode || selectedNode.kind === 'root' || !this.rootMappingNode) {
      return;
    }

    const parentNode = this.findNodeById(this.rootMappingNode, selectedNode.parentId);
    if (!parentNode) {
      return;
    }

    parentNode.children = parentNode.children.filter(child => child.id !== selectedNode.id);
    this.syncChildMappingCounts(parentNode);
    this.selectMappingNode(parentNode.id);
  }

  addContextBinding(): void {
    const node = this.selectedMappingNode;
    if (!node || node.kind === 'root') {
      return;
    }

    node.contextBindings = [
      ...node.contextBindings,
      {
        targetPath: '',
        mode: 'ParentSourceValue',
        sourcePath: ''
      }
    ];
  }

  removeContextBinding(index: number): void {
    const node = this.selectedMappingNode;
    if (!node || node.kind === 'root') {
      return;
    }

    node.contextBindings = node.contextBindings.filter((_, currentIndex) => currentIndex !== index);
  }

  async onSelectedNodeSourceEntityChanged(entityName: string | null | undefined): Promise<void> {
    const node = this.selectedMappingNode;
    const linkId = this.linkId ?? 0;
    if (!node || node.kind === 'root' || !entityName?.trim()) {
      return;
    }

    if (this.isInlineChildObjectNode(node)) {
      node.sourceEntity = entityName;
      return;
    }

    if (linkId <= 0) {
      return;
    }

    const sourceSchema = await this.loadEntitySchemaAsync(linkId, entityName, 'source');
    this.applyNodeSourceSchema(node, entityName, sourceSchema);
  }

  async onSelectedNodeTargetEntityChanged(entityName: string | null | undefined): Promise<void> {
    const node = this.selectedMappingNode;
    const linkId = this.linkId ?? 0;
    if (!node || node.kind === 'root' || !entityName?.trim()) {
      return;
    }

    if (this.isInlineChildObjectNode(node)) {
      node.targetEntity = entityName;
      return;
    }

    if (linkId <= 0) {
      return;
    }

    const targetSchema = await this.loadEntitySchemaAsync(linkId, entityName, 'target');
    this.applyNodeTargetSchema(node, entityName, targetSchema);
  }

  private async createChildNodeAsync(parentNode: MapperEditorNode, ownerRow: MappingGridRow, sourceEntity: string, targetEntity: string): Promise<void> {
    const linkId = this.linkId ?? 0;
    if (linkId <= 0) {
      return;
    }

    try {
      const [sourceSchema, targetSchema] = await Promise.all([
        this.loadEntitySchemaAsync(linkId, sourceEntity, 'source'),
        this.loadEntitySchemaAsync(linkId, targetEntity, 'target')
      ]);

			const targetRuntimeRoute = this.getDefaultTargetRuntimeRoute(targetEntity);

      const childNode = this.createEditorNode({
        kind: 'child',
        mappingId: null,
        parentId: parentNode.id,
        ownerTargetFieldId: ownerRow.targetFieldId,
        ownerTargetPath: ownerRow.targetPath,
        name: ownerRow.targetDisplayName,
        sourceEntity,
        targetEntity,
        sourceIdPath: 'id',
        sourceSchema,
        targetSchema,
        sourceCollectionPath: ownerRow.targetPath,
			targetRuntimeRoute: targetRuntimeRoute,
        contextBindings: [],
        continueOnError: true,
        executionMode: 'AfterParentUpsert',
        runtimeMessageTemplates: null
      });

      parentNode.children = [...parentNode.children, childNode];
      this.syncChildMappingCounts(parentNode);
      this.selectMappingNode(childNode.id);
    } catch (error) {
      this.showMapperApiError(error, 'Failed to load child mapping schemas.', 'Child mapping schemas could not be loaded');
    }
  }

  private async loadEntitySchemaAsync(linkId: number, entityName: string, side: 'source' | 'target'): Promise<MappingEntitySchema> {
    return await firstValueFrom(this.api.getEntitySchema(linkId, entityName, side));
  }

	private applyNodeSourceSchema(node: MapperEditorNode, entityName: string, sourceSchema: MappingEntitySchema): void {
    node.sourceEntity = entityName;
    node.sourceSchema = sourceSchema;
    node.sourceFieldOptions = this.flattenSchemaToFieldOptions(
      sourceSchema.fields,
      '',
      '',
      field => this.isLeafField(field) && field.canRead
    );
    node.sourceTreeItems = this.mapSchemaToTreeNodes(sourceSchema.fields);
    this.applyRowFiltersToNode(node);

		if (node.kind === 'root') {
			node.sourceIdPath = this.resolveSourceIdPath(sourceSchema, node.sourceIdPath);

			this.rebuildSourceIdPathTree();
		}

    this.refreshRuntimeMessagePropertyExamples();
  }

  private applyNodeTargetSchema(node: MapperEditorNode, entityName: string, targetSchema: MappingEntitySchema): void {
    const previousDefaultRuntimeRoute = this.getDefaultTargetRuntimeRoute(node.targetEntity);
    const nextDefaultRuntimeRoute = this.getDefaultTargetRuntimeRoute(entityName);
    node.targetEntity = entityName;
    if (!node.targetRuntimeRoute?.trim() || node.targetRuntimeRoute === previousDefaultRuntimeRoute) {
      node.targetRuntimeRoute = nextDefaultRuntimeRoute;
    }
    node.targetSchema = targetSchema;
    node.targetTreeItems = this.mapSchemaToTreeNodes(targetSchema.fields);
    node.createMappingRows = this.buildRowsFromTargetSchema(targetSchema, 'create');
    node.updateMappingRows = this.buildRowsFromTargetSchema(targetSchema, 'update');
    node.filteredCreateMappingRows = [];
    node.filteredUpdateMappingRows = [];
    this.applyRowFiltersToNode(node);
    this.selectedRow = null;
    this.selectedSourcePath = undefined;
    this.selectedTargetPath = undefined;
    this.refreshRuntimeMessagePropertyExamples();
  }


  previewMappings(showGlobalLoading = true, onComplete?: () => void): void {
    if (!this.configuration) {
      onComplete?.();
      return;
    }

		const previewNode = this.selectedMappingNode;
		const previewTab = this.currentRuleTab;

    if (this.hasRequiredSampleInputs()) {
      this.lastActionMessage = 'Enter the required sample input values before previewing mappings.';
      onComplete?.();
      return;
    }

      if (showGlobalLoading) {
        this.isPageLoading = true;
      }

    this.ensureCurrentSamplePayload().pipe(
      switchMap(loaded => {
        if (!loaded || !this.configuration) {
          return of(null);
        }

			if (showGlobalLoading) {
				this.pageLoadingMessage = 'Generating preview...';
				this.pageLoadingDetail = 'Resolving the sample payload through the current mapping rules.';
			}

			return this.runSelectedMappingPreview(null, previewNode, previewTab);
		})
    ).subscribe({
      next: async response => {
        if (!response) {
          if (showGlobalLoading) {
            this.isPageLoading = false;
          }
          onComplete?.();
          return;
        }

        const resolvedSourcePayload =
          response?.resolvedSourcePayload ??
          response?.sourcePayload ??
          this.configuration?.sampleSourcePayload ??
          {};

        const resultPayload = response?.result ?? {};

    this.previewResponseJson = response ?? {};
    this.previewSourceJson = resolvedSourcePayload;
    this.previewResultJson = resultPayload;

        this.validationMessages = response.fieldResults
          .filter((x: any) => !x.isValid)
          .map((x: any) => `${x.targetFieldId}: ${x.message ?? 'Invalid'}`);

        this.validationSummary = [];

		this.getRowsForTab(previewTab).forEach(row => {
          const fieldResult = response.fieldResults.find((x: any) => x.targetFieldId === row.targetFieldId);

          row.previewValue = fieldResult?.previewValue;
          row.isValid = fieldResult?.isValid;
          row.validationMessage = fieldResult?.message ?? null;

          this.validationSummary.push({
            field: row.targetDisplayName,
            ok: row.isValid ?? false,
            message: row.validationMessage ?? 'No result.'
          });
        });

        this.applyRowFilters();
        this.refreshSelectedRow();

        this.lastActionMessage = response.isValid
          ? 'Preview generated.'
          : 'Preview generated with validation issues.';

        if (showGlobalLoading) {
			this.stopInitialLoadSequence();
          this.isPageLoading = false;
        }

			onComplete?.();
		},
			error: async error => {
				this.showMapperApiError(error, 'Preview failed.', 'Mapper preview failed');

				if (showGlobalLoading) {
					this.stopInitialLoadSequence();
          this.isPageLoading = false;
        }

        onComplete?.();
      }
    });
  }

  private refreshSelectedRow(): void {
    if (!this.selectedRow) {
      return;
    }

    const updated = this.mappingRows.find(x => x.targetFieldId === this.selectedRow?.targetFieldId);
    if (!updated) {
      return;
    }

    this.selectedRow = updated;
    this.selectedSourcePath = updated.sourceFieldPath ?? undefined;
    this.selectedTargetPath = updated.targetPath ?? undefined;
  }

	requestPreview(row: MappingGridRow | null = null): void {
		if (row?.matchOnlyLinkKey && row.mode !== 'Ignore') {
			row.isLinkKey = true;
		}

		if (row?.isLinkKey) {
			this.synchronizeSharedLinkKeyFromRow(row);
		}
		this.refreshMappingStats();
		this.refreshSaveMappingReviewState();

		const node = this.selectedMappingNode;
		const sourceField = row?.isPicklist && row.sourceFieldPath
			? node?.sourceFieldOptions.find(field => field.path === row.sourceFieldPath)
			: null;
		if (row && node && sourceField?.optionsDeferred) {
			void this.loadDeferredSourceOptions(node, sourceField.path)
				.then(() => this.enqueuePreview(row, node))
				.catch(() => undefined);
			return;
		}

		this.enqueuePreview(row, node);
	}

	private enqueuePreview(row: MappingGridRow | null, node: MapperEditorNode | null): void {
		this.previewRequested$.next({
			row,
			tab: this.currentRuleTab,
			node
		});
	}

  setActiveMapperTab(tab: MapperTab): void {
    if (tab === 'update' && !this.canShowUpdateTab) {
      return;
    }

		this.activeMapperTab = tab;
		this.refreshRuntimeMessagePropertyExamples();
		this.applyRowFilters();
		this.refreshSelectedRow();

		if ((tab === 'create' || tab === 'update') && this.shouldPreviewRuleTab(tab)) {
			this.previewMappings(false);
		}
	}

	private shouldPreviewRuleTab(tab: RuleTab): boolean {
		return this.getRowsForTab(tab).some(row => !this.isRowUnmapped(row) && row.previewValue === undefined);
	}

  addWriteback(): void {
    const sourceField = this.sourceWritableFieldOptions[0]?.path ?? '';
		const targetField = this.getDefaultTargetWritebackFieldPath();
    const writebackIndex = this.writebacks.length;

    this.writebacks = [
      ...this.writebacks,
      {
        sourceFieldPath: sourceField,
        mode: 'TargetValue',
        targetFieldPath: targetField,
        dateFormat: null,
        expression: null,
        conditionExpression: null
      }
    ];

    this.rebuildWritebackSourceTree(writebackIndex, this.writebacks[writebackIndex]);
    this.rebuildWritebackTargetTree(writebackIndex, this.writebacks[writebackIndex]);
  }

	configureWritebackFromEvent(event: Event): void {
		const detail = (event as CustomEvent<{ writebacks?: MappingWriteback[] }>).detail;
		const writebacks = detail?.writebacks;
		if (!Array.isArray(writebacks)) {
			return;
		}

		this.writebacks = writebacks.map(writeback => ({
			sourceFieldPath: writeback.sourceFieldPath ?? '',
			mode: writeback.mode ?? 'TargetValue',
			targetFieldPath: writeback.targetFieldPath ?? null,
			dateFormat: writeback.dateFormat ?? null,
			expression: writeback.expression ?? null,
			conditionExpression: writeback.conditionExpression ?? null
		}));

		this.writebacks.forEach((writeback, index) => {
			this.rebuildWritebackSourceTree(index, writeback);
			this.rebuildWritebackTargetTree(index, writeback);
		});
	}

  async removeWriteback(index: number): Promise<void> {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: `Remove writeback ${index + 1}?`,
      text: 'This writeback will be removed from the mapper profile. Save the profile to persist the change.',
      showCancelButton: true,
      confirmButtonText: 'Remove writeback',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626'
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    this.writebacks = this.writebacks.filter((_, currentIndex) => currentIndex !== index);
    delete this.writebackSourceTreeItemsByIndex[index];
    delete this.writebackTargetTreeItemsByIndex[index];
    delete this.writebackSourceFieldSearchByIndex[index];
    delete this.writebackTargetFieldSearchByIndex[index];
    delete this.writebackSourceDropdownOpenedByIndex[index];
    delete this.writebackTargetDropdownOpenedByIndex[index];
  }

  onWritebackModeChanged(writeback: MappingWriteback, mode: MappingWritebackMode): void {
    writeback.mode = mode;

    if (mode === 'CurrentDate') {
      writeback.targetFieldPath = null;
      writeback.dateFormat = writeback.dateFormat || 'yyyy-MM-dd';
      writeback.expression = null;
		} else if (mode === 'Expression') {
      writeback.targetFieldPath = null;
      writeback.dateFormat = null;
      writeback.expression = writeback.expression ?? '';
		} else {
			writeback.targetFieldPath = writeback.targetFieldPath || this.getDefaultTargetWritebackFieldPath();
			writeback.dateFormat = null;
			writeback.expression = null;
		}

		this.refreshSaveMappingReviewState();
  }

	onWritebackTargetFieldChanged(writeback: MappingWriteback, value: string | null | undefined): void {
		writeback.targetFieldPath = value || null;
		this.refreshSaveMappingReviewState();
	}

	onWritebackDateFormatChanged(writeback: MappingWriteback, value: string | null | undefined): void {
		writeback.dateFormat = value || null;
		this.refreshSaveMappingReviewState();
	}

	onWritebackExpressionChanged(writeback: MappingWriteback, value: string | null | undefined): void {
		writeback.expression = value || null;
		this.refreshSaveMappingReviewState();
	}

	onWritebackConditionChanged(writeback: MappingWriteback, value: string | null | undefined): void {
		writeback.conditionExpression = value || null;
		this.refreshSaveMappingReviewState();
	}

  openWritebackExpressionEditor(index: number, target: 'value' | 'condition'): void {
    const writeback = this.writebacks[index];
    if (!writeback) {
      return;
    }

		this.guideExpressionEditingRow = null;
		this.expressionEditorUsesGuideFields = false;
    this.writebackExpressionEditIndex = index;
    this.writebackExpressionEditTarget = target;
    this.lookupExpressionCriterionIndex = null;
    this.expressionEditingTargetFieldId = null;
    this.expressionFieldSearch = '';
    this.expressionTargetFieldSearch = '';
    this.rebuildExpressionSourceTreeItems();
    this.rebuildExpressionTargetTreeItems();
    this.resetExpressionTestResult();
    this.expressionTipsExpanded = false;
    this.expressionDraft = this.normalizeExpressionForEditor(
      target === 'condition'
        ? writeback.conditionExpression
        : writeback.expression
    );
    this.selectedExpressionSourceField = null;
    this.expressionPopupVisible = true;
    this.closeExpressionSuggestions();
  }

  getWritebackTargetDisplayValue(writeback: MappingWriteback): string {
		const selected = this.targetWritebackFieldOptions.find(option => option.path === writeback.targetFieldPath);
		return selected?.label || writeback.targetFieldPath || '';
  }

	setActiveWritebackRuleSet(ruleSet: RuleTab): void {
		if (this.activeWritebackRuleSet === ruleSet) {
			return;
		}

		this.activeWritebackRuleSet = ruleSet;
		this.writebackSourceTreeItemsByIndex = {};
		this.writebackTargetTreeItemsByIndex = {};
		this.writebackSourceFieldSearchByIndex = {};
		this.writebackTargetFieldSearchByIndex = {};
		this.writebackSourceDropdownOpenedByIndex = {};
		this.writebackTargetDropdownOpenedByIndex = {};
		this.writebacks.forEach((writeback, index) => {
			this.rebuildWritebackSourceTree(index, writeback);
			this.rebuildWritebackTargetTree(index, writeback);
		});
	}

	getWritebackTargetFieldTitle(field: FieldOption | null | undefined): string {
		if (!field) {
			return '';
		}

		const pathSuffix = ` [${field.path}]`;
		const pathIndex = field.label.lastIndexOf(pathSuffix);
		return pathIndex > 0 ? field.label.substring(0, pathIndex) : field.label;
	}

	private getDefaultTargetWritebackFieldPath(): string | null {
		const options = this.targetWritebackFieldOptions;
		const primaryKeyPath = this.configuration?.targetSchema?.primaryKeyPath?.trim();
		if (primaryKeyPath) {
			const primaryKey = options.find(option => option.path.localeCompare(primaryKeyPath, undefined, { sensitivity: 'accent' }) === 0);
			if (primaryKey) {
				return primaryKey.path;
			}
		}

		return options.find(option => option.isKey && !option.path.includes('.'))?.path
			?? options.find(option => option.isKey)?.path
			?? options[0]?.path
			?? null;
	}

  getWritebackSourceDisplayValue(writeback: MappingWriteback): string {
    const selected = this.sourceWritableFieldOptions.find(option => option.path === writeback.sourceFieldPath);
    return selected?.label || writeback.sourceFieldPath || '';
  }

  getCachedSourceIdPathTreeItems(): TreeNode[] {
    return this.sourceIdPathTreeItems;
  }

  setSourceIdPathFieldSearchValue(value: string | null | undefined): void {
    this.sourceIdPathFieldSearch = value ?? '';
    this.rebuildSourceIdPathTree();
  }

  getWritebackSourceFieldSearchValue(index: number): string {
    return this.writebackSourceFieldSearchByIndex[index] ?? '';
  }

  setWritebackSourceFieldSearchValue(index: number, value: string | null | undefined): void {
    this.writebackSourceFieldSearchByIndex[index] = value ?? '';
    this.rebuildWritebackSourceTree(index, this.writebacks[index]);
  }

  getWritebackTargetFieldSearchValue(index: number): string {
    return this.writebackTargetFieldSearchByIndex[index] ?? '';
  }

  setWritebackTargetFieldSearchValue(index: number, value: string | null | undefined): void {
    this.writebackTargetFieldSearchByIndex[index] = value ?? '';
    this.rebuildWritebackTargetTree(index, this.writebacks[index]);
  }

  getCachedWritebackSourceTreeItems(index: number): TreeNode[] {
    return this.writebackSourceTreeItemsByIndex[index] ?? [];
  }

  getCachedWritebackTargetTreeItems(index: number): TreeNode[] {
    return this.writebackTargetTreeItemsByIndex[index] ?? [];
  }

	onRuntimeMessageSourceItemClick(e: any): void {
		const item = e?.itemData as TreeNode | undefined;
		if (!item || item.items?.length || item.disabled || !item.isMappable || !item.path) {
			return;
		}

		void this.copyRuntimeMessageToken(`{{source.${item.path}}}`);
		this.messageVariableSourceDropdownOpened = false;
	}

	onRuntimeMessageTargetItemClick(e: any): void {
		const item = e?.itemData as TreeNode | undefined;
		if (!item || item.items?.length || item.disabled || !item.isMappable || !item.path) {
			return;
		}

		void this.copyRuntimeMessageToken(`{{target.${item.path}}}`);
		this.messageVariableTargetDropdownOpened = false;
	}

	async copyRuntimeMessageToken(token: string): Promise<void> {
		try {
			if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(token);
			}
		} catch {
			// Ignore clipboard failures; the token is still shown in the notification.
		}

		notify({
			message: `${token} copied`,
			type: 'success',
			displayTime: 1800
		});
	}

	async runMapperTestLink(): Promise<void> {
		const linkId = this.linkId ?? 0;
		if (linkId <= 0 || this.isTestRunStarting || this.isLinkRunning) {
			return;
		}

		const transferByKeyValue = this.transferByKeyAppliedValue;
		const confirmation = await Swal.fire({
			icon: 'warning',
			title: 'Start mapper test run?',
			text: transferByKeyValue
				? `This will queue a mapper test run using transfer-by-ID '${transferByKeyValue}'. That value also remains active for the next normal link run until you change it in Setup.`
				: 'This will queue a mapper test run against the current mapper configuration and store the results in the link logs for this session.',
			showCancelButton: true,
			confirmButtonText: 'Start test run',
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.isTestRunStarting = true;
		this.hasRecentTestRun = false;
		this.activeTestRunSessionId = '';
		this.latestTestRunResult = null;
		this.mapperRunProgressStage = 'Queued';
		this.mapperRunProgressProcessedCount = 0;
		this.mapperRunProgressTotalCount = 0;
		this.mapperRunProgressMessage = 'Waiting for mapper runtime to start.';
		this.testRunStatusText = 'Link enqueued, waiting for execution to start.';

		this.api.startTestRun({
			linkId,
			sourceEntity: this.configuration?.sourceEntity ?? '',
			targetEntity: this.configuration?.targetEntity ?? '',
			captureArtifacts: this.captureArtifactsForSession
		}).subscribe({
				next: response => {
					this.isTestRunStarting = false;
					this.testRunMenuOpen = false;
					this.activeTestRunSessionId = response?.sessionId ?? '';
					this.mapperRunProgressStage = 'Queued';
					this.testRunStatusText = response?.message || 'Link enqueued, waiting for execution to start.';
					if (this.isE2eAuthBypassEnabled && this.activeTestRunSessionId) {
						this.loadLatestTestRunResult(this.activeTestRunSessionId);
					}
				},
				error: error => {
					this.isTestRunStarting = false;
					this.activeTestRunSessionId = '';
					this.mapperRunProgressStage = '';
					this.mapperRunProgressProcessedCount = 0;
					this.mapperRunProgressTotalCount = 0;
					this.mapperRunProgressMessage = '';
					this.testRunStatusText = 'Failed to start mapper test run.';
					this.showMapperApiError(error, 'Failed to start mapper test run.', 'Mapper test run could not be started');
				}
		});
	}

	private get isE2eAuthBypassEnabled(): boolean {
		try {
			return typeof window !== 'undefined'
				&& window.localStorage?.getItem('application-link:e2e-auth-bypass') === 'true';
		} catch {
			return false;
		}
	}

	private initializeMapperScreenMode(config: MappingSchemaConfiguration): void {
		if (this.isE2eAuthBypassEnabled) {
			this.mapperScreenMode = 'advanced';
			return;
		}

		const preferredMode = this.readMapperScreenModePreference();
		if (preferredMode) {
			this.mapperScreenMode = preferredMode;
			return;
		}

		const hasReusableSetup = !!config.activeProfileName?.trim()
			|| (config.availableProfiles ?? []).length > 0
			|| (config.availableTemplates ?? []).length > 0;
		this.mapperScreenMode = hasReusableSetup ? 'basic' : 'advanced';
	}

	private readMapperScreenModePreference(): MapperScreenMode | null {
		try {
			if (typeof localStorage === 'undefined') {
				return null;
			}

			const value = localStorage.getItem(LinkGenericMapperComponent.screenModeStorageKey);
			return value === 'basic' || value === 'advanced' ? value : null;
		} catch {
			return null;
		}
	}

	private writeMapperScreenModePreference(mode: MapperScreenMode): void {
		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.setItem(LinkGenericMapperComponent.screenModeStorageKey, mode);
		} catch {
			// Ignore storage failures and keep the in-memory mode.
		}
	}

  getFilteredTargetTreeItemsForWriteback(index: number): TreeNode[] {
    return this.filterTreeNodes(this.targetTreeItems, this.getWritebackTargetFieldSearchValue(index));
  }

  onWritebackSourceTreeItemClick(e: any, writeback: MappingWriteback, index: number): void {
    const item = e?.itemData as TreeNode | undefined;
    if (!item || item.items?.length || item.disabled || !item.isMappable) {
      return;
    }

    writeback.sourceFieldPath = item.path;
    this.rebuildWritebackSourceTree(index, writeback);
    this.writebackSourceDropdownOpenedByIndex[index] = false;
		this.refreshSaveMappingReviewState();
  }

  onSourceIdPathTreeItemClick(e: any): void {
    const item = e?.itemData as TreeNode | undefined;
    if (!item || item.items?.length || item.disabled || !item.isMappable || !item.path || !this.rootMappingNode) {
      return;
    }

    this.rootMappingNode.sourceIdPath = item.path;
    this.rebuildSourceIdPathTree();
    this.sourceIdPathDropdownOpened = false;
  }

  onWritebackTargetTreeItemClick(e: any, writeback: MappingWriteback, index: number): void {
    const item = e?.itemData as TreeNode | undefined;
    if (!item || !item.isMappable || !item.path) {
      return;
    }

    writeback.targetFieldPath = item.path;
    this.rebuildWritebackTargetTree(index, writeback);
    this.writebackTargetDropdownOpenedByIndex[index] = false;
		this.refreshSaveMappingReviewState();
  }

  validateMappings(): void {
    if (!this.configuration) {
      return;
    }

    if (this.hasRequiredSampleInputs()) {
      this.lastActionMessage = 'Enter the required sample input values before validating mappings.';
      return;
    }

    this.isPageLoading = true;
    this.pageLoadingDetail = 'Resolving the sample payload and checking every mapped field for issues.';
    this.ensureCurrentSamplePayload().pipe(
      switchMap(loaded => {
        if (!loaded || !this.configuration) {
          return of(null);
        }

        const request = this.buildMappingDefinition();
        this.pageLoadingMessage = 'Validating mappings...';
        this.pageLoadingDetail = 'Checking all mapped fields against the current sample data.';

        return this.api.validateMapping(
          request,
          this.configuration.sampleSourcePayload,
          this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema,
          this.configuration.targetSchema
        );
      })
    ).subscribe({
      next: response => {
        if (!response) {
          this.isPageLoading = false;
          return;
        }

        this.validationSummary = [];

        this.mappingRows.forEach(row => {
          const fieldResult = response.fieldResults.find((x: any) => x.targetFieldId === row.targetFieldId);
          row.isValid = fieldResult?.isValid;
          row.validationMessage = fieldResult?.message ?? null;

          this.validationSummary.push({
            field: row.targetDisplayName,
            ok: row.isValid ?? false,
            message: row.validationMessage ?? 'No result.'
          });
        });

        this.applyRowFilters();

        this.lastActionMessage = response.isValid
          ? 'Validation succeeded.'
          : 'Validation completed with issues.';
        this.isPageLoading = false;
		},
		error: error => {
			this.showMapperApiError(error, 'Validation failed.', 'Mapper validation failed');
			this.isPageLoading = false;
		}
    });
  }

	async saveMapping(): Promise<void> {
		if (!this.configuration) {
			return;
		}

    const trimmedProfileName = this.profileName.trim() || this.selectedProfileName.trim();
    if (!trimmedProfileName) {
      this.lastActionMessage = 'Enter a profile name before saving.';
      return;
    }

		this.enforceSharedLinkKeysForAllNodes();
		this.refreshSaveMappingReviewState();

		if (!this.hasPendingMappingChanges) {
			this.lastActionMessage = 'There are no mapping changes to save.';
			return;
		}

		const reviewConfirmed = await this.confirmSaveMappingChanges(trimmedProfileName);
		if (!reviewConfirmed) {
			return;
		}

		this.isPageLoading = true;
		this.pageLoadingMessage = 'Saving mapping...';
		this.pageLoadingDetail = 'Persisting this mapper profile for the current company and link scope.';

		const mapping = {
			...this.buildMappingDefinition(),
      activeRuleSet: null,
      name: trimmedProfileName
    };

		this.api.saveMapping(mapping).subscribe({
			next: () => {
        this.profileName = trimmedProfileName;
        this.selectedProfileName = trimmedProfileName;
        this.profileMenuOpen = false;
		if (this.configuration) {
		  const currentProfiles = this.configuration.availableProfiles ?? [];
		  const existing = currentProfiles.find(profile => profile.name === trimmedProfileName);
			const isFirstProfile = currentProfiles.length === 0;
			const isSavedProfileActive = existing?.isActive === true || isFirstProfile;
			const now = new Date().toISOString();
			const updatedProfiles = existing
				? currentProfiles.map(profile => profile.name === trimmedProfileName
					? { ...profile, isActive: isSavedProfileActive, lastModificationDate: now }
					: profile
				)
				: [
					...currentProfiles,
					{ name: trimmedProfileName, creationDate: now, lastModificationDate: now, isActive: isSavedProfileActive }
				];

		  this.configuration = {
			...this.configuration,
				activeProfileName: isSavedProfileActive ? trimmedProfileName : this.configuration.activeProfileName,
            availableProfiles: updatedProfiles.sort((a, b) => a.name.localeCompare(b.name))
          };
				this.cacheCurrentConfiguration();
			}
			this.savedProfileRowSignatures = this.captureCurrentRowSignatures();
			this.lastActionMessage = this.selectedProfileIsActive
				? 'Mapping saved. This profile remains active for runtime.'
				: 'Mapping saved. Use Set active if this profile should run next.';
        this.isPageLoading = false;
		},
			error: error => {
				this.showMapperApiError(error, 'Save failed.', 'Mapper profile could not be saved');
				this.isPageLoading = false;
			}
    });
  }

  loadSelectedProfile(): void {
    if (!this.configuration || !this.selectedProfileName.trim()) {
      this.lastActionMessage = 'Select a profile to load.';
      return;
    }

    this.isPageLoading = true;
    this.pageLoadingMessage = 'Loading profile...';
    this.pageLoadingDetail = 'Loading the saved profile and reapplying its create, update, and writeback rules.';

    this.api.getMappingProfile(
      this.configuration.linkId,
      this.configuration.sourceEntity,
      this.configuration.targetEntity,
      this.selectedProfileName.trim()
    ).subscribe({
      next: async mapping => {
        this.rebuildRootNodeFromConfiguration();
        await this.applySavedMappingDefinition(mapping);
			this.applyRowFilters();
			this.refreshSelectedRow();
			this.profileName = mapping.name ?? this.selectedProfileName.trim();
			this.savedProfileRowSignatures = this.captureCurrentRowSignatures();
			this.profileMenuOpen = false;
			this.lastActionMessage = this.selectedProfileIsActive
				? `Active profile '${this.selectedProfileName.trim()}' loaded.`
				: `Profile '${this.selectedProfileName.trim()}' loaded for review. Use Set active to make it the runtime profile.`;

			if (!this.hasRequiredSampleInputs()) {
				this.pageLoadingMessage = 'Generating preview...';
				this.pageLoadingDetail = 'Resolving the sample payload through the active profile.';
				this.previewMappings();
				return;
			}

			this.isPageLoading = false;
			this.stopInitialLoadSequence();
		},
			error: error => {
				this.showMapperApiError(error, 'Failed to load selected profile.', 'Mapper profile could not be loaded');
				this.isPageLoading = false;
				this.stopInitialLoadSequence();
		}
    });
  }

	async discardMappingChanges(): Promise<void> {
		if (!this.configuration || !this.hasPendingMappingChanges) {
			return;
		}

		const profileName = this.selectedProfileName.trim();
		const reviewItems = this.buildSaveMappingReviewItems();
		const visibleItems = reviewItems.slice(0, 40);
		const reviewRows = this.buildMappingReviewRows(visibleItems, 'discard');
		const hiddenCount = Math.max(0, reviewItems.length - visibleItems.length);
		const confirmation = await Swal.fire({
			icon: 'warning',
			title: 'Discard unsaved mapper changes?',
			width: 760,
			html: `
				<p>${profileName
					? `This will reload profile '${this.escapeHtml(profileName)}' and discard ${reviewItems.length} unsaved mapper change${reviewItems.length === 1 ? '' : 's'}.`
					: `This will reset the mapper draft and discard ${reviewItems.length} unsaved mapper change${reviewItems.length === 1 ? '' : 's'}.`}</p>
				<div style="max-height: 460px; overflow: auto; text-align: left; padding-right: 4px;">
					${reviewRows}
				</div>
				${hiddenCount > 0 ? `<p>${hiddenCount} more change${hiddenCount === 1 ? '' : 's'} not shown.</p>` : ''}
			`,
			showCancelButton: true,
			confirmButtonText: 'Discard changes',
			cancelButtonText: 'Keep editing',
			confirmButtonColor: '#d33',
			focusCancel: true
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.closeExpressionEditor();
		this.closeLookupEditor();
		this.closePicklistMappingEditor();

		if (profileName) {
			this.loadSelectedProfile();
			return;
		}

		this.resetMapperDraftFromConfiguration();
		this.profileName = '';
		this.profileMenuOpen = false;
		this.lastActionMessage = 'Unsaved mapper changes discarded.';

		if (!this.hasRequiredSampleInputs()) {
			this.previewMappings();
		}
	}

	private showMapperApiError(error: unknown, fallback: string, title = 'Mapper error'): string {
		const message = this.getApiErrorMessage(error, fallback);
		this.lastActionMessage = message;
		this.mapperLoadErrorTitle = title;
		this.mapperLoadErrorMessage = message;

		notify({
			message,
			type: 'error',
			displayTime: 8000,
		});

		setTimeout(() => void Swal.fire({
			icon: 'error',
			title,
			text: message,
			confirmButtonText: 'OK'
		}), 0);

		return message;
	}

	private clearMapperLoadError(): void {
		this.mapperLoadErrorTitle = '';
		this.mapperLoadErrorMessage = '';
	}

	private getApiErrorMessage(error: unknown, fallback: string): string {
		if (error instanceof HttpErrorResponse) {
			const responseBody = error.error;
			const statusSuffix = error.status
				? ` (${error.status}${error.statusText ? ` ${error.statusText}` : ''})`
				: '';

			if (typeof responseBody === 'string' && responseBody.trim()) {
				return responseBody.trim();
			}

			if (responseBody && typeof responseBody === 'object') {
				const applicationErrorMessage = this.getApplicationErrorMessage((responseBody as any).errors);
				if (applicationErrorMessage) {
					return applicationErrorMessage;
				}

				const message =
					(responseBody as any).message ??
					(responseBody as any).detail ??
					(responseBody as any).title ??
					null;
				if (typeof message === 'string' && message.trim()) {
					return message.trim();
				}

				const errors = (responseBody as any).errors;
				if (errors && typeof errors === 'object') {
					const firstError = Object.values(errors).find(value => {
						if (Array.isArray(value)) {
							return value.some(entry => typeof entry === 'string' && entry.trim());
						}

						return typeof value === 'string' && value.trim();
					});

					const firstErrorMessage = Array.isArray(firstError)
						? firstError.find(entry => typeof entry === 'string' && entry.trim())
						: firstError;

					if (typeof firstErrorMessage === 'string' && firstErrorMessage.trim()) {
						return firstErrorMessage.trim();
					}
				}
			}

			if (statusSuffix) {
				return `${fallback}${statusSuffix}`;
			}

			if (typeof error.message === 'string' && error.message.trim()) {
				return error.message.trim();
			}
		}

		if (error instanceof Error && error.message.trim()) {
			return error.message.trim();
		}

		return fallback;
	}

	private getApplicationErrorMessage(errors: unknown): string | null {
		if (!Array.isArray(errors)) {
			return null;
		}

		const messages = errors
			.map(error => {
				if (typeof error === 'string') {
					return error.trim();
				}

				if (error && typeof error === 'object') {
					return String((error as any).message ?? '').trim();
				}

				return '';
			})
			.filter(Boolean);

		return messages.length ? messages.join('\n') : null;
	}

  get availableProfiles() {
    return this.configuration?.availableProfiles ?? [];
  }

	get availableTemplates(): MapperProfileSummary[] {
		return this.configuration?.availableTemplates ?? [];
	}

	get filteredAvailableTemplates(): MapperProfileSummary[] {
		const search = this.templateSearch.trim().toLowerCase();
		if (!search) {
			return this.availableTemplates;
		}

		return this.availableTemplates.filter(template =>
			template.name.toLowerCase().includes(search)
			|| (template.summary ?? '').toLowerCase().includes(search)
			|| (template.highlights ?? []).some(highlight => highlight.toLowerCase().includes(search))
		);
	}

	get hasTemplateSearchResults(): boolean {
		return this.filteredAvailableTemplates.length > 0;
	}

	get selectedTemplate(): MapperProfileSummary | null {
		return this.availableTemplates.find(template => template.name === this.selectedTemplateName) ?? null;
	}

	get selectedTemplateSummary(): string {
		return this.selectedTemplate?.summary || 'Select a template to see what it configures before applying it.';
	}

	get selectedTemplateHighlights(): string[] {
		return this.selectedTemplate?.highlights ?? [];
	}

	get hasSelectedTemplateHighlights(): boolean {
		return this.selectedTemplateHighlights.length > 0;
	}

	selectTemplate(templateName: string): void {
		this.selectedTemplateName = templateName;
		this.templateName = templateName;
		this.selectedTemplateMiniGridTab = 'create';
		this.loadSelectedTemplateDefinition(templateName);
	}

	applySelectedTemplate(): void {
		if (!this.selectedTemplateName) {
			return;
		}

		void this.applyTemplate(this.selectedTemplateName);
	}

	async applySelectedTemplateForBasicSetup(): Promise<void> {
		if (!this.configuration || !this.selectedTemplateName.trim() || !this.canApplySelectedTemplate) {
			return;
		}

		const templateName = this.selectedTemplateName.trim();
		const profileName = templateName;
		const existingProfile = this.availableProfiles.find(profile => profile.name === profileName);
		const confirmation = await Swal.fire({
			icon: 'question',
			title: existingProfile ? `Update profile '${profileName}'?` : `Use template '${templateName}'?`,
			text: existingProfile
				? 'This will replace that saved profile with the selected template and make it active for mapper runs.'
				: 'This will save the selected template as an active profile for this link so you can test it.',
			showCancelButton: true,
			confirmButtonText: 'Apply setup',
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.isPageLoading = true;
		this.pageLoadingMessage = 'Applying setup...';
		this.pageLoadingDetail = 'Saving the selected template as the active mapper profile for this link.';

		this.api.applyTemplateForBasicSetup({
			linkId: this.configuration.linkId,
			sourceEntity: this.configuration.sourceEntity,
			targetEntity: this.configuration.targetEntity,
			templateName,
			profileName,
			setActive: true
		}).subscribe({
			next: async response => {
				const mapping = response.mapping;
				this.rebuildRootNodeFromConfiguration();
				await this.applySavedMappingDefinition(mapping);
				this.applyRowFilters();
				this.refreshSelectedRow();

				const appliedProfileName = response.profileName?.trim() || mapping.name || profileName;
				this.selectedProfileName = appliedProfileName;
				this.profileName = appliedProfileName;
				this.templateName = templateName;
				this.profileMenuOpen = false;

				if (this.configuration) {
					this.configuration = {
						...this.configuration,
						activeProfileName: response.activeProfileName ?? appliedProfileName,
						availableProfiles: response.availableProfiles ?? this.configuration.availableProfiles ?? []
					};
					this.cacheCurrentConfiguration();
				}

				this.savedProfileRowSignatures = this.captureCurrentRowSignatures();
				this.lastActionMessage = response.message || `Template '${templateName}' applied as active profile '${appliedProfileName}'.`;
				this.isPageLoading = false;

				if (!this.hasRequiredSampleInputs()) {
					this.previewMappings();
				}
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to apply selected template.', 'Basic mapper setup could not be applied');
				this.isPageLoading = false;
			}
		});
	}

	get selectedTemplateExactSections(): BasicTemplateSection[] {
		const definition = this.selectedTemplateDefinition;
		return [
			{
				title: 'When a target record is created',
				description: `These values will be sent to ${this.targetSystemName} for a new ${this.targetEntityLabel}.`,
				rows: definition ? this.buildTemplateRuleRows(this.getTemplateCreateRules(definition)) : [],
				emptyText: 'This template does not create any target fields.'
			},
			{
				title: 'When an existing target record is updated',
				description: `These values will be updated on an existing ${this.targetEntityLabel}.`,
				rows: definition ? this.buildTemplateRuleRows(definition.updateRules ?? []) : [],
				emptyText: 'This template does not update existing target fields.'
			}
		];
	}

	get selectedTemplateWritebackRows(): BasicTemplateActionRow[] {
		const definition = this.selectedTemplateDefinition;
		if (!definition) {
			return [];
		}

		const hasSplitWritebacks = definition.createWritebacks != null || definition.updateWritebacks != null;
		const scopedWritebacks = hasSplitWritebacks
			? [
				...(definition.createWritebacks ?? []).map(writeback => ({ writeback, scope: 'After create' })),
				...(definition.updateWritebacks ?? []).map(writeback => ({ writeback, scope: 'After update' }))
			]
			: (definition.writebacks ?? []).map(writeback => ({ writeback, scope: 'After create and update' }));

		return scopedWritebacks
			.filter(item => !!item.writeback.sourceFieldPath)
			.map(({ writeback, scope }) => ({
				target: this.getSourceFieldDisplay(writeback.sourceFieldPath),
				targetPath: writeback.sourceFieldPath,
				value: this.describeWritebackValue(writeback),
				mode: this.getWritebackModeLabel(writeback.mode),
				note: writeback.conditionExpression?.trim()
					? `${scope}, only when ${writeback.conditionExpression.trim()}`
					: `${scope}.`,
				isLinkKey: false
			}));
	}

	get hasSelectedTemplateWritebackRows(): boolean {
		return this.selectedTemplateWritebackRows.length > 0;
	}

	get selectedTemplateChildRows(): BasicTemplateChildRow[] {
		return this.flattenTemplateChildren(this.selectedTemplateDefinition?.children ?? []);
	}

	get hasSelectedTemplateChildRows(): boolean {
		return this.selectedTemplateChildRows.length > 0;
	}

	get selectedTemplateActionSummary(): string {
		if (this.isLoadingSelectedTemplateDefinition) {
			return 'Loading exact template actions...';
		}

		if (this.selectedTemplateDefinitionError) {
			return this.selectedTemplateDefinitionError;
		}

		if (!this.selectedTemplateDefinition) {
			return this.availableTemplates.length
				? 'Select a template to see the exact changes it will make.'
				: 'No template is available for this link type.';
		}

		const createCount = this.getTemplateCreateRules(this.selectedTemplateDefinition).filter(rule => this.templateRuleIsActive(rule)).length;
		const updateCount = (this.selectedTemplateDefinition.updateRules ?? []).filter(rule => this.templateRuleIsActive(rule)).length;
		const writebackCount = this.selectedTemplateWritebackRows.length;
		const childCount = this.selectedTemplateChildRows.length;
		const parts = [`${createCount} create field${createCount === 1 ? '' : 's'}`];

		if (updateCount > 0) {
			parts.push(`${updateCount} update field${updateCount === 1 ? '' : 's'}`);
		}

		if (writebackCount > 0) {
			parts.push(`${writebackCount} writeback${writebackCount === 1 ? '' : 's'}`);
		}

		if (childCount > 0) {
			parts.push(`${childCount} child mapping${childCount === 1 ? '' : 's'}`);
		}

		return `Applying this template will configure ${parts.join(', ')}.`;
	}

	get selectedTemplatePlainSteps(): BasicTemplatePlainStep[] {
		const definition = this.selectedTemplateDefinition;
		if (!definition) {
			return [];
		}

		const createRows = this.selectedTemplateCreateFieldRows;
		const updateRows = this.selectedTemplateUpdateFieldRows;
		const writebackRows = this.selectedTemplateWritebackRows;
		const childRows = this.selectedTemplateChildRows;
		const linkKey = createRows.concat(updateRows).find(row => row.isLinkKey);
		const steps: BasicTemplatePlainStep[] = [];

		if (linkKey) {
			steps.push({
				title: 'Find the right existing record',
				description: `Matches ${this.sourceSystemName} records to ${this.targetSystemName} records using ${linkKey.source} and ${linkKey.target}.`,
				tone: 'success'
			});
		}

		if (createRows.length) {
			steps.push({
				title: `Fill ${createRows.length} field${createRows.length === 1 ? '' : 's'} when creating a record`,
				description: `Copies the listed ${this.sourceSystemName} values into ${this.targetSystemName}.`,
				tone: 'info'
			});
		}

		if (updateRows.length) {
			steps.push({
				title: `Update ${updateRows.length} field${updateRows.length === 1 ? '' : 's'} when the record already exists`,
				description: 'The same setup is used to keep existing records up to date.',
				tone: 'info'
			});
		}

		if (writebackRows.length) {
			steps.push({
				title: `Write ${writebackRows.length} value${writebackRows.length === 1 ? '' : 's'} back afterwards`,
				description: `After ${this.targetSystemName} saves the record, selected returned values are stored back in ${this.sourceSystemName}.`,
				tone: 'warning'
			});
		}

		if (childRows.length) {
			steps.push({
				title: `Also map ${childRows.length} related object${childRows.length === 1 ? '' : 's'}`,
				description: 'Related records or child payload sections are included in this setup.',
				tone: 'muted'
			});
		}

		return steps;
	}

	get selectedTemplateCreateFieldRows(): BasicTemplatePlainFieldRow[] {
		if (!this.selectedTemplateDefinition) {
			return [];
		}

		return this.buildTemplatePlainFieldRows(this.getTemplateCreateRules(this.selectedTemplateDefinition));
	}

	get selectedTemplateUpdateFieldRows(): BasicTemplatePlainFieldRow[] {
		return this.buildTemplatePlainFieldRows(this.selectedTemplateDefinition?.updateRules ?? []);
	}

	get selectedTemplateVisibleFieldRows(): BasicTemplatePlainFieldRow[] {
		return this.selectedTemplateCreateFieldRows.length
			? this.selectedTemplateCreateFieldRows
			: this.selectedTemplateUpdateFieldRows;
	}

	get selectedTemplatePreviewFieldRows(): BasicTemplatePlainFieldRow[] {
		return this.selectedTemplateVisibleFieldRows.slice(0, 8);
	}

	get selectedTemplateMiniGridSections(): BasicTemplateMiniGridSection[] {
		return [
			{
				id: 'create',
				title: 'Create',
				count: this.selectedTemplateCreateFieldRows.length,
				emptyText: 'No create fields.',
				rows: this.selectedTemplateCreateFieldRows.map(row => ({
					left: row.source,
					middle: row.isLinkKey ? 'Match key' : row.note || 'Map',
					right: row.target,
					isLinkKey: row.isLinkKey
				}))
			},
			{
				id: 'update',
				title: 'Update',
				count: this.selectedTemplateUpdateFieldRows.length,
				emptyText: 'No update fields.',
				rows: this.selectedTemplateUpdateFieldRows.map(row => ({
					left: row.source,
					middle: row.isLinkKey ? 'Match key' : row.note || 'Map',
					right: row.target,
					isLinkKey: row.isLinkKey
				}))
			},
			{
				id: 'writeback',
				title: 'Writeback',
				count: this.selectedTemplateWritebackRows.length,
				emptyText: 'No writebacks.',
				rows: this.selectedTemplateWritebackRows.map(row => ({
					left: row.value,
					middle: row.mode,
					right: row.target,
					isLinkKey: false
				}))
			},
			{
				id: 'messages',
				title: 'Messages',
				count: this.selectedTemplateRuntimeMessageRows.length,
				emptyText: 'Default messages.',
				rows: this.selectedTemplateRuntimeMessageRows
			}
		];
	}

	get selectedTemplateActiveMiniGridSection(): BasicTemplateMiniGridSection | null {
		return this.selectedTemplateMiniGridSections.find(section => section.id === this.selectedTemplateMiniGridTab) ?? null;
	}

	setSelectedTemplateMiniGridTab(tab: BasicTemplateMiniGridTab): void {
		this.selectedTemplateMiniGridTab = tab;
	}

	private get selectedTemplateRuntimeMessageRows(): BasicTemplateMiniGridRow[] {
		const templates = this.selectedTemplateDefinition?.runtimeMessageTemplates;
		if (!templates) {
			return [];
		}

		return [
			{ label: 'Create success', value: templates.createSuccess },
			{ label: 'Update success', value: templates.updateSuccess },
			{ label: 'Validation failure', value: templates.validationFailure },
			{ label: 'Upsert failure', value: templates.upsertFailure },
			{ label: 'Writeback failure', value: templates.writebackFailure }
		]
			.filter(item => !!item.value?.trim())
			.map(item => ({
				left: item.label,
				middle: 'Message',
				right: item.value?.trim() || '',
				isLinkKey: false
			}));
	}

	get selectedTemplateRemainingFieldCount(): number {
		return Math.max(0, this.selectedTemplateVisibleFieldRows.length - this.selectedTemplatePreviewFieldRows.length);
	}

	get selectedTemplateFieldCountLabel(): string {
		const count = this.selectedTemplate?.mappedFieldCount ?? this.selectedTemplateVisibleFieldRows.length;
		return `${count} field${count === 1 ? '' : 's'}`;
	}

	get basicTemplatePreviewJsonLines(): string[] {
		const rows = this.selectedTemplatePreviewFieldRows;
		if (!rows.length) {
			return ['{', '  // Apply or preview a template to see sample output.', '}'];
		}

		const lines = rows.map(row => `  "${row.target}": "${row.source}",`);
		if (this.selectedTemplateRemainingFieldCount > 0) {
			lines.push(`  // ... ${this.selectedTemplateRemainingFieldCount} more fields`);
		}

		return ['{', ...lines, '}'];
	}

	get hasBasicLoadedSampleValues(): boolean {
		return !!this.configuration?.samplePayloadRetrieved && this.basicSampleValueRows.length > 0;
	}

	get basicSampleValueRows(): BasicPreviewValueRow[] {
		const samplePayload = this.getBasicPreviewSourcePayload();
		const sourcePaths = new Set<string>();

		this.getRowsForTab(this.currentRuleTab)
			.filter(row => !this.isRowUnmapped(row) && row.mode !== 'Ignore')
			.forEach(row => this.getArtifactRuleSourcePaths(row).forEach(path => sourcePaths.add(path)));

		return Array.from(sourcePaths)
			.slice(0, 8)
			.map(path => ({
				label: this.getSourceFieldSimpleDisplay(path),
				value: this.formatBasicSampleValue(this.getBasicValueAtPath(samplePayload, path)),
				tone: 'muted'
			}));
	}

	get basicSampleValueEmptyText(): string {
		if (!this.configuration?.samplePayloadRetrieved) {
			return 'Load a sample source record to show source values used by this mapping.';
		}

		return 'This mapping does not read any direct source fields for the current tab.';
	}

	get hasBasicPreviewDataRouteRows(): boolean {
		return this.basicPreviewDataRouteRows.length > 0;
	}

	get basicPreviewDataRouteRows(): BasicPreviewDataRouteRow[] {
		return this.getRowsForTab(this.currentRuleTab)
			.filter(row => !this.isRowUnmapped(row) && row.mode !== 'Ignore')
			.slice(0, 12)
			.map(row => {
				const sourcePaths = this.getArtifactRuleSourcePaths(row);
				return {
					sourceLabel: this.getBasicRouteSourceLabel(row, sourcePaths),
					sourceValue: this.getBasicRouteSourceValue(row, sourcePaths),
					ruleLabel: this.getBasicRouteRuleLabel(row),
					ruleDetail: this.getBasicRouteRuleDetail(row, sourcePaths),
					targetLabel: row.targetDisplayName || row.targetPath || row.targetFieldId,
					targetValue: this.getBasicRouteTargetValue(row),
					tone: this.getBasicRouteTone(row),
					isLinkKey: !!row.isLinkKey
				};
			});
	}

	get basicPreviewDataRouteEmptyText(): string {
		return this.configuration?.samplePayloadRetrieved
			? 'No mapped fields are configured for the current tab.'
			: 'Load a sample source record to show the data route for this mapping.';
	}

	get hasBasicMappedValueRows(): boolean {
		return this.basicMappedValueRows.length > 0;
	}

	get basicMappedValueRows(): BasicPreviewValueRow[] {
		const rows = this.getRowsForTab(this.currentRuleTab)
			.filter(row => !this.isRowUnmapped(row) && row.mode !== 'Ignore')
			.filter(row => row.previewValue !== undefined || row.validationMessage)
			.slice(0, 8)
			.map(row => ({
				label: row.targetDisplayName || row.targetPath || row.targetFieldId,
				value: row.previewValue === undefined
					? row.validationMessage || 'No preview value yet'
					: this.formatPreviewValue(row.previewValue),
				tone: row.isValid === false ? 'danger' as const : row.previewValue === undefined ? 'warning' as const : 'success' as const
			}));

		if (rows.length) {
			return rows;
		}

		return this.flattenBasicPreviewObject(this.safeParseJson(this.previewResultJson))
			.slice(0, 8)
			.map(row => ({
				label: row.path,
				value: row.value,
				tone: 'success'
			}));
	}

	private flattenBasicPreviewObject(value: any, parentPath = '', depth = 0): Array<{ path: string; value: string }> {
		if (value === null || value === undefined || depth > 4) {
			return [];
		}

		if (typeof value !== 'object') {
			return parentPath
				? [{ path: parentPath, value: this.formatPreviewValue(value) }]
				: [];
		}

		if (Array.isArray(value)) {
			const rows: Array<{ path: string; value: string }> = [];
			value.forEach((item, index) => {
				rows.push(...this.flattenBasicPreviewObject(item, `${parentPath}[${index}]`, depth + 1));
			});
			return rows;
		}

		const rows: Array<{ path: string; value: string }> = [];
		Object.keys(value).forEach(key => {
			const nextPath = parentPath ? `${parentPath}.${key}` : key;
			const item = value[key];
			if (item === null || item === undefined || typeof item !== 'object') {
				rows.push({ path: nextPath, value: this.formatPreviewValue(item) });
				return;
			}

			rows.push(...this.flattenBasicPreviewObject(item, nextPath, depth + 1));
		});

		return rows;
	}

	private getBasicValueAtPath(value: any, path: string): any {
		if (value === null || value === undefined || !path?.trim()) {
			return undefined;
		}

		if (Object.prototype.hasOwnProperty.call(value, path)) {
			return value[path];
		}

		const exactKey = this.findBasicObjectKey(value, path);
		if (exactKey) {
			return value[exactKey];
		}

		return path.split('.').reduce((current, part) => {
			if (current === null || current === undefined) {
				return undefined;
			}

			const match = part.match(/^([^\[]+)(?:\[(\d+)])?$/);
			if (!match) {
				return current[part];
			}

			const key = match[1];
			const index = match[2] !== undefined ? Number(match[2]) : null;
			const actualKey = this.findBasicObjectKey(current, key) ?? key;
			const next = current[actualKey];
			return index === null ? next : Array.isArray(next) ? next[index] : undefined;
		}, value);
	}

	private getBasicPreviewSourcePayload(): any {
		const previewSource = this.safeParseJson(this.previewSourceJson);
		if (previewSource && typeof previewSource === 'object' && Object.keys(previewSource).length > 0) {
			return previewSource;
		}

		return this.configuration?.sampleSourcePayload;
	}

	private findBasicObjectKey(value: any, key: string): string | null {
		if (!value || typeof value !== 'object' || Array.isArray(value)) {
			return null;
		}

		const normalizedKey = key.trim().toLowerCase();
		return Object.keys(value).find(currentKey => currentKey.toLowerCase() === normalizedKey) ?? null;
	}

	private formatBasicSampleValue(value: any): string {
		if (value === undefined) {
			return 'Not found in sample';
		}

		if (value === null || value === '') {
			return 'Empty';
		}

		return this.formatPreviewValue(value);
	}

	private getBasicRouteSourceLabel(row: MappingGridRow, sourcePaths: string[]): string {
		if (row.mode === 'Constant') {
			return 'Fixed value';
		}

		if (row.mode === 'Lookup') {
			return sourcePaths.length
				? sourcePaths.map(path => this.getSourceFieldSimpleDisplay(path)).join(', ')
				: row.lookup?.referenceEntityName || 'Lookup criteria';
		}

		if (row.mode === 'Expression') {
			return sourcePaths.length
				? sourcePaths.map(path => this.getSourceFieldSimpleDisplay(path)).join(', ')
				: 'Expression';
		}

		return sourcePaths.length
			? sourcePaths.map(path => this.getSourceFieldSimpleDisplay(path)).join(', ')
			: 'No source field';
	}

	private getBasicRouteSourceValue(row: MappingGridRow, sourcePaths: string[]): string {
		if (row.mode === 'Constant') {
			return this.formatBasicSampleValue(row.value);
		}

		if (!sourcePaths.length) {
			return row.mode === 'Expression'
				? row.value?.trim() || 'Calculated value'
				: row.mode === 'Lookup'
					? 'Lookup result'
					: 'No source value';
		}

		if (!this.configuration?.samplePayloadRetrieved) {
			return 'Load sample to show values';
		}

		const sourcePayload = this.getBasicPreviewSourcePayload();
		const values = sourcePaths.map(path => {
			const label = this.getSourceFieldSimpleDisplay(path);
			const value = this.formatBasicSampleValue(this.getBasicValueAtPath(sourcePayload, path));
			return sourcePaths.length === 1 ? value : `${label}: ${value}`;
		});

		return values.join(' | ');
	}

	private getBasicRouteRuleLabel(row: MappingGridRow): string {
		switch (row.mode) {
			case 'SourceField':
				return row.picklistMapping ? 'Copy + translate' : 'Copy';
			case 'Constant':
				return 'Fixed';
			case 'Expression':
				return 'Calculate';
			case 'Lookup':
				return 'Lookup';
			default:
				return row.mode || 'Map';
		}
	}

	private getBasicRouteRuleDetail(row: MappingGridRow, sourcePaths: string[]): string {
		if (row.isLinkKey) {
			return 'Used to match existing target records';
		}

		if (row.mode === 'Lookup') {
			return `Find ${row.lookup?.referenceEntityName || 'related record'} and return ${row.lookup?.returnFieldPath || 'ID'}`;
		}

		if (row.mode === 'Expression') {
			return row.value?.trim() || 'Expression result';
		}

		if (row.mode === 'Constant') {
			return 'Always sends the configured value';
		}

		if (row.picklistMapping) {
			return 'Translates source value before sending';
		}

		return sourcePaths.length ? 'Direct source-to-target copy' : 'Configured mapping rule';
	}

	private getBasicRouteTargetValue(row: MappingGridRow): string {
		if (row.isValid === false) {
			return row.validationMessage || 'Invalid preview value';
		}

		if (row.previewValue !== undefined) {
			return this.formatPreviewValue(row.previewValue) || 'Empty';
		}

		return this.configuration?.samplePayloadRetrieved ? 'Preview not loaded' : 'Load sample to preview';
	}

	private getBasicRouteTone(row: MappingGridRow): 'success' | 'warning' | 'danger' | 'muted' {
		if (row.isValid === false) {
			return 'danger';
		}

		if (row.previewValue !== undefined) {
			return 'success';
		}

		return this.configuration?.samplePayloadRetrieved ? 'warning' : 'muted';
	}

	private safeParseJson(value: unknown, fallback: any = {}): any {
		if (typeof value !== 'string') {
			return value ?? fallback;
		}

		try {
			return JSON.parse(value || '{}');
		} catch {
			return fallback;
		}
	}

	get selectedTemplateFieldListTitle(): string {
		return this.selectedTemplateCreateFieldRows.length
			? 'Fields filled when creating a record'
			: 'Fields updated on existing records';
	}

	get selectedTemplateFieldListDescription(): string {
		return 'Read each row as: source value on the left is copied into target field on the right.';
	}

	get canApplySelectedTemplate(): boolean {
		return !!this.selectedTemplateName && !!this.selectedTemplateDefinition && !this.isLoadingSelectedTemplateDefinition && !this.isPageLoading;
	}

	private loadSelectedTemplateDefinition(templateName: string): void {
		const trimmedName = templateName.trim();
		if (!this.configuration || !trimmedName) {
			this.selectedTemplateDefinition = null;
			this.selectedTemplateDefinitionError = '';
			this.isLoadingSelectedTemplateDefinition = false;
			return;
		}

		this.selectedTemplateDefinition = null;
		this.selectedTemplateDefinitionError = '';
		this.isLoadingSelectedTemplateDefinition = true;

		this.api.getMappingTemplate(
			this.configuration.linkId,
			this.configuration.sourceEntity,
			this.configuration.targetEntity,
			trimmedName
		).subscribe({
			next: mapping => {
				if (this.selectedTemplateName === trimmedName) {
					this.selectedTemplateDefinition = mapping;
					this.selectedTemplateDefinitionError = '';
					this.isLoadingSelectedTemplateDefinition = false;
				}
			},
			error: () => {
				if (this.selectedTemplateName === trimmedName) {
					this.selectedTemplateDefinition = null;
					this.selectedTemplateDefinitionError = 'Template details could not be loaded. Do not apply this template until details are visible.';
					this.isLoadingSelectedTemplateDefinition = false;
				}
			}
		});
	}

	private getTemplateCreateRules(definition: MappingDefinition): MappingRule[] {
		return definition.createRules?.length ? definition.createRules : definition.rules ?? [];
	}

	private buildTemplateRuleRows(rules: MappingRule[]): BasicTemplateActionRow[] {
		return (rules ?? [])
			.filter(rule => this.templateRuleIsActive(rule))
			.map(rule => ({
				target: this.getTargetFieldDisplay(rule.targetPath || rule.targetFieldId),
				targetPath: rule.targetPath || rule.targetFieldId,
				value: this.describeTemplateRuleValue(rule),
				mode: this.getTemplateRuleModeLabel(rule),
				note: this.describeTemplateRuleNote(rule),
				isLinkKey: rule.isLinkKey === true
			}));
	}

	private buildTemplatePlainFieldRows(rules: MappingRule[]): BasicTemplatePlainFieldRow[] {
		return (rules ?? [])
			.filter(rule => this.templateRuleIsActive(rule))
			.map(rule => ({
				source: this.describeTemplateRuleSimpleValue(rule),
				target: this.getTargetFieldSimpleDisplay(rule.targetPath || rule.targetFieldId),
				note: this.describeTemplateRuleSimpleNote(rule),
				isLinkKey: rule.isLinkKey === true
			}))
			.sort((left, right) => this.compareTemplatePlainFieldRows(left, right));
	}

	private compareTemplatePlainFieldRows(left: BasicTemplatePlainFieldRow, right: BasicTemplatePlainFieldRow): number {
		if (left.isLinkKey !== right.isLinkKey) {
			return left.isLinkKey ? -1 : 1;
		}

		return left.target.localeCompare(right.target, undefined, { sensitivity: 'base', numeric: true })
			|| left.source.localeCompare(right.source, undefined, { sensitivity: 'base', numeric: true })
			|| left.note.localeCompare(right.note, undefined, { sensitivity: 'base', numeric: true });
	}

	private templateRuleIsActive(rule: MappingRule | null | undefined): boolean {
		return !!rule
			&& !this.isIgnoreMode(rule.mode)
			&& !!(rule.targetPath || rule.targetFieldId);
	}

	private isIgnoreMode(mode: string | null | undefined): boolean {
		return String(mode ?? '').toLowerCase() === 'ignore';
	}

	private getTemplateRuleModeLabel(rule: MappingRule): string {
		switch (rule.mode) {
			case 'SourceField':
				return 'Copy source field';
			case 'Constant':
				return 'Use fixed value';
			case 'Expression':
				return 'Calculate value';
			case 'Lookup':
				return 'Find related record';
			default:
				return rule.mode || 'Mapping';
		}
	}

	private describeTemplateRuleValue(rule: MappingRule): string {
		if (rule.mode === 'SourceField') {
			return this.getSourceFieldDisplay(rule.sourceFieldPath);
		}

		if (rule.mode === 'Constant') {
			return this.stringifyPreviewValue(rule.value ?? '') || 'Empty fixed value';
		}

		if (rule.mode === 'Expression') {
			return rule.value?.trim() || 'Expression is empty';
		}

		if (rule.mode === 'Lookup') {
			const lookup = rule.lookup;
			if (!lookup) {
				return 'Lookup configuration is missing';
			}

			const criteria = lookup.criteria ?? [];
			const criteriaText = criteria.length
				? criteria.map(criterion => `${criterion.targetFieldPath} from ${criterion.sourceFieldPath || criterion.value || 'value'}`).join(', ')
				: 'no lookup criteria';
			return `${lookup.referenceEntityName || 'reference'} by ${criteriaText}`;
		}

		return rule.value?.trim() || rule.sourceFieldPath || 'Configured mapping';
	}

	private describeTemplateRuleSimpleValue(rule: MappingRule): string {
		if (rule.mode === 'SourceField') {
			return this.getSourceFieldSimpleDisplay(rule.sourceFieldPath);
		}

		if (rule.mode === 'Constant') {
			return `Fixed value: ${this.stringifyPreviewValue(rule.value ?? '') || 'empty'}`;
		}

		if (rule.mode === 'Expression') {
			return 'Calculated value';
		}

		if (rule.mode === 'Lookup') {
			return rule.lookup?.referenceEntityName
				? `Lookup in ${rule.lookup.referenceEntityName}`
				: 'Lookup value';
		}

		return rule.value?.trim() || rule.sourceFieldPath || 'Configured value';
	}

	private describeTemplateRuleSimpleNote(rule: MappingRule): string {
		const notes: string[] = [];

		if (rule.isLinkKey) {
			notes.push('Used to find whether the target record already exists.');
		}

		if (rule.mode === 'Expression' && rule.value?.trim()) {
			notes.push(`Calculation: ${rule.value.trim()}`);
		}

		if (rule.mode === 'Lookup' && rule.lookup?.criteria?.length) {
			notes.push(`Lookup uses ${rule.lookup.criteria.length} condition${rule.lookup.criteria.length === 1 ? '' : 's'}.`);
		}

		if (rule.valueMap?.items?.length) {
			notes.push(`Translates ${rule.valueMap.items.length} value${rule.valueMap.items.length === 1 ? '' : 's'}.`);
		}

		return notes.join(' ');
	}

	private describeTemplateRuleNote(rule: MappingRule): string {
		const notes: string[] = [];

		if (rule.isLinkKey) {
			notes.push('Used to match source records to existing target records.');
		}

		if (rule.valueMap?.items?.length) {
			notes.push(`Translates ${rule.valueMap.items.length} source value${rule.valueMap.items.length === 1 ? '' : 's'} to target values.`);
		}

		const normalization = this.describeTemplateNormalization(rule.normalization);
		if (normalization) {
			notes.push(normalization);
		}

		return notes.join(' ');
	}

	private describeTemplateNormalization(normalization: MappingRuleNormalization | null | undefined): string {
		if (!normalization) {
			return '';
		}

		const items: string[] = [];
		if (normalization.trimWhitespace) {
			items.push('trims spaces');
		}

		if (normalization.emptyStringToNull) {
			items.push('turns empty text into null');
		}

		if (normalization.truncateToMaxLength && normalization.maxLength) {
			items.push(`limits text to ${normalization.maxLength} characters`);
		}

		if (normalization.numberFormat) {
			items.push(`formats numbers as ${normalization.numberFormat}`);
		}

		if (normalization.dateFormat) {
			items.push(`formats dates as ${normalization.dateFormat}`);
		}

		return items.length ? `Also ${items.join(', ')}.` : '';
	}

	private describeWritebackValue(writeback: MappingWriteback): string {
		if (writeback.mode === 'TargetValue') {
			return this.getTargetFieldDisplay(writeback.targetFieldPath);
		}

		if (writeback.mode === 'CurrentDate') {
			return `Current date (${writeback.dateFormat || 'yyyy-MM-dd'})`;
		}

		if (writeback.mode === 'Expression') {
			return writeback.expression?.trim() || 'Expression is empty';
		}

		return 'Configured writeback value';
	}

	private getWritebackModeLabel(mode: MappingWritebackMode | string): string {
		switch (mode) {
			case 'TargetValue':
				return 'Copy target result';
			case 'CurrentDate':
				return 'Write current date';
			case 'Expression':
				return 'Calculate writeback';
			default:
				return mode || 'Writeback';
		}
	}

	private flattenTemplateChildren(children: ChildMappingDefinition[], depth = 0): BasicTemplateChildRow[] {
		const rows: BasicTemplateChildRow[] = [];

		for (const child of children ?? []) {
			const createRules = child.createRules?.length ? child.createRules : child.rules ?? [];
			const row: BasicTemplateChildRow = {
				name: child.name || child.targetEntity || 'Child mapping',
				source: child.sourceCollectionPath?.trim()
					? `${child.sourceEntity || this.sourceEntityLabel}.${child.sourceCollectionPath}`
					: child.sourceEntity || this.sourceEntityLabel,
				target: child.inlineChildObject && child.ownerTargetPath
					? `${this.targetEntityLabel}.${child.ownerTargetPath}`
					: child.targetEntity || this.targetEntityLabel,
				when: this.describeChildExecution(child, depth),
				mappedFieldCount: createRules.concat(child.updateRules ?? [])
					.filter(rule => this.templateRuleIsActive(rule))
					.length
			};

			rows.push(row, ...this.flattenTemplateChildren(child.children ?? [], depth + 1));
		}

		return rows;
	}

	private describeChildExecution(child: ChildMappingDefinition, depth: number): string {
		const execution = child.executionMode === 'BeforeParentUpsert'
			? 'before the parent record is saved'
			: 'after the parent record is saved';
		const prefix = depth > 0 ? 'Nested child, ' : '';
		return `${prefix}${execution}${child.continueOnError === false ? '; stops on error' : '; continues on child errors'}`;
	}

	private getSourceFieldDisplay(path: string | null | undefined): string {
		const normalizedPath = path?.trim();
		if (!normalizedPath) {
			return 'No source field selected';
		}

		const field = this.rootMappingNode?.sourceFieldOptions?.find(option => option.path.toLowerCase() === normalizedPath.toLowerCase())
			?? this.sourceFieldOptions.find(option => option.path.toLowerCase() === normalizedPath.toLowerCase());
		return field ? `${field.label} (${field.path})` : normalizedPath;
	}

	private getSourceFieldSimpleDisplay(path: string | null | undefined): string {
		const normalizedPath = path?.trim();
		if (!normalizedPath) {
			return 'No source field selected';
		}

		const field = this.rootMappingNode?.sourceFieldOptions?.find(option => option.path.toLowerCase() === normalizedPath.toLowerCase())
			?? this.sourceFieldOptions.find(option => option.path.toLowerCase() === normalizedPath.toLowerCase());
		return field?.label || normalizedPath;
	}

	private getTargetFieldDisplay(path: string | null | undefined): string {
		const normalizedPath = path?.trim();
		if (!normalizedPath) {
			return 'No target field selected';
		}

		const field = this.configuration?.targetSchema?.fields
			? this.findSchemaFieldByPath(this.configuration.targetSchema.fields, normalizedPath)
			: null;
		return field ? `${field.displayName || field.path} (${field.path})` : normalizedPath;
	}

	private getTargetFieldSimpleDisplay(path: string | null | undefined): string {
		const normalizedPath = path?.trim();
		if (!normalizedPath) {
			return 'No target field selected';
		}

		const field = this.configuration?.targetSchema?.fields
			? this.findSchemaFieldByPath(this.configuration.targetSchema.fields, normalizedPath)
			: null;
		return field?.displayName || normalizedPath;
	}

	get canManageTemplates(): boolean {
		return this.configuration?.canManageTemplates === true;
	}

	get templateScopeSummary(): string {
		if (!this.configuration) {
			return 'Templates are scoped to this mapper context.';
		}

		return `${this.configuration.sourceSchema.systemName} ${this.configuration.sourceSchema.entityName} to ${this.configuration.targetSchema.systemName} ${this.configuration.targetSchema.entityName}`;
	}

	get templateScopeTechnicalSummary(): string {
		if (!this.configuration) {
			return '';
		}

		return 'Only templates made for this exact source and target link type are shown here.';
	}

	get templateNameMatchesExistingTemplate(): boolean {
		const trimmedName = this.templateName.trim();
		return !!trimmedName && this.availableTemplates.some(template => template.name === trimmedName);
	}

	get saveTemplateButtonLabel(): string {
		return this.templateNameMatchesExistingTemplate ? 'Update template' : 'Save as template';
	}

  get childSourceEntityOptions(): MappingEntityDescriptor[] {
    return this.availableSourceEntities.filter(entity => entity.supportsSourceMapping);
  }

  get childTargetEntityOptions(): MappingEntityDescriptor[] {
    return this.availableTargetEntities.filter(entity => entity.supportsTargetMapping);
  }

  toggleProfileMenu(): void {
    const willOpen = !this.profileMenuOpen;

		if (willOpen) {
			this.testRunMenuOpen = false;
			this.closeAdminRunMenu();
		}

		this.profileMenuOpen = willOpen;
  }

  reloadMapperMetadata(): void {
    this.loadConfiguration(true);
  }

  toggleMetadataCaching(enabled: boolean): void {
    this.metadataCachingEnabled = enabled;
    this.writeMetadataCachingPreference(enabled);

		if (enabled) {
			this.cacheCurrentConfiguration();
		} else {
      this.api.clearMetadataCache();
    }

    this.lastActionMessage = enabled
			? 'Mapper metadata caching enabled for 30 minutes. Reload bypasses and refreshes the cache.'
      : 'Mapper metadata caching disabled and cleared.';
  }

  clearMetadataCache(): void {
    this.api.clearMetadataCache();
    this.lastActionMessage = 'Mapper metadata cache cleared.';
  }

	async selectProfile(profileName: string): Promise<void> {
		const nextProfileName = profileName?.trim();
		if (!nextProfileName || !this.configuration) {
			return;
		}

		if (this.hasPendingMappingChanges) {
			const confirmation = await Swal.fire({
				icon: 'warning',
				title: nextProfileName === this.selectedProfileName.trim() ? 'Reload profile?' : 'Switch profile?',
				text: `Unsaved mapper changes will be discarded before loading profile '${nextProfileName}'.`,
				showCancelButton: true,
				confirmButtonText: nextProfileName === this.selectedProfileName.trim() ? 'Reload profile' : 'Switch profile',
				cancelButtonText: 'Keep editing',
				confirmButtonColor: '#d33',
				focusCancel: true
			});

			if (!confirmation.isConfirmed) {
				return;
			}
		}

		this.selectedProfileName = nextProfileName;
		this.loadSelectedProfile();
  }

	async applyTemplate(templateName: string): Promise<void> {
		if (!this.configuration || !templateName.trim()) {
			return;
		}

		if (this.hasPendingMappingChanges) {
			const confirmation = await Swal.fire({
				icon: 'warning',
				title: 'Apply template?',
				text: 'This will replace the current mapper draft with the selected template. Unsaved changes in the editor will be lost unless you save them first.',
				showCancelButton: true,
				confirmButtonText: 'Apply template',
				cancelButtonText: 'Cancel'
			});

			if (!confirmation.isConfirmed) {
				return;
			}
		}

		this.isPageLoading = true;
		this.pageLoadingMessage = 'Applying template...';
		this.pageLoadingDetail = 'Loading the reusable mapper template into this profile draft.';

		this.api.getMappingTemplate(
			this.configuration.linkId,
			this.configuration.sourceEntity,
			this.configuration.targetEntity,
			templateName.trim()
		).subscribe({
			next: async mapping => {
				const trimmedTemplateName = templateName.trim();
				this.rebuildRootNodeFromConfiguration();
				await this.applySavedMappingDefinition(mapping);
				this.applyRowFilters();
				this.refreshSelectedRow();
				this.profileMenuOpen = false;
				this.profileName = this.selectedProfileName.trim() || mapping.name || trimmedTemplateName;
				this.templateName = trimmedTemplateName;
				this.lastActionMessage = `Template '${trimmedTemplateName}' applied. Save the profile to keep these mapper changes for this link.`;
				this.isPageLoading = false;

				if (!this.hasRequiredSampleInputs()) {
					this.previewMappings();
				}
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to apply mapper template.', 'Mapper template could not be applied');
				this.isPageLoading = false;
			}
		});
	}

	async saveCurrentMappingAsTemplate(): Promise<void> {
		if (!this.configuration || !this.canManageTemplates) {
			return;
		}

		const trimmedTemplateName = this.templateName.trim();
		if (!trimmedTemplateName) {
			this.lastActionMessage = 'Enter a template name before saving.';
			return;
		}

		const confirmation = await Swal.fire({
			icon: 'question',
			title: `${this.templateNameMatchesExistingTemplate ? 'Update' : 'Save'} template '${trimmedTemplateName}'?`,
			text: `This admin template will only be available for ${this.templateScopeSummary}.`,
			showCancelButton: true,
			confirmButtonText: this.saveTemplateButtonLabel,
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.isPageLoading = true;
		this.pageLoadingMessage = 'Saving template...';
		this.pageLoadingDetail = 'Persisting the current mapper draft as a reusable template for this link type.';

		const mapping = {
			...this.buildMappingDefinition(),
			activeRuleSet: null,
			name: trimmedTemplateName
		};

		this.api.saveMappingTemplate(mapping).subscribe({
			next: () => {
				this.refreshTemplatesAfterSave(trimmedTemplateName);
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to save mapper template.', 'Mapper template could not be saved');
				this.isPageLoading = false;
			}
		});
	}

	private refreshTemplatesAfterSave(templateName: string): void {
		if (!this.configuration) {
			this.lastActionMessage = `Mapper template '${templateName}' saved.`;
			this.isPageLoading = false;
			return;
		}

		this.api.getMappingTemplates(
			this.configuration.linkId,
			this.configuration.sourceEntity,
			this.configuration.targetEntity
		).subscribe({
			next: templates => {
				if (this.configuration) {
					this.configuration = {
						...this.configuration,
						availableTemplates: templates
					};
					this.cacheCurrentConfiguration();
				}

				this.selectedTemplateName = templateName;
				this.templateName = templateName;
				this.profileMenuOpen = false;
				this.lastActionMessage = `Mapper template '${templateName}' saved.`;
				this.isPageLoading = false;
			},
			error: () => {
				this.profileMenuOpen = false;
				this.selectedTemplateName = templateName;
				this.lastActionMessage = `Mapper template '${templateName}' saved. Reload the mapper to refresh template details.`;
				this.isPageLoading = false;
			}
		});
	}

	setSelectedProfileActive(): void {
		const profileName = this.selectedProfileName.trim();
		if (!this.configuration || !profileName || this.selectedProfileIsActive) {
			return;
		}

		this.isPageLoading = true;
		this.pageLoadingMessage = 'Setting active profile...';
		this.pageLoadingDetail = 'Updating the runtime profile used by the next mapper execution.';
		this.api.setActiveMappingProfile({
			linkId: this.configuration.linkId,
			sourceEntity: this.configuration.sourceEntity,
			targetEntity: this.configuration.targetEntity,
			profileName
		}).subscribe({
			next: () => {
				if (this.configuration) {
					this.configuration = {
						...this.configuration,
						activeProfileName: profileName,
						availableProfiles: (this.configuration.availableProfiles ?? []).map(profile => ({
							...profile,
							isActive: profile.name === profileName
						}))
					};
					this.cacheCurrentConfiguration();
				}

				this.lastActionMessage = `Profile '${profileName}' set active for runtime.`;
				this.isPageLoading = false;
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to set active profile.', 'Active mapper profile could not be changed');
				this.isPageLoading = false;
			}
		});
	}

	private buildInputValues(definitions: MapperSampleInputDefinition[], currentValues: Record<string, string>): Record<string, string> {
		return definitions.reduce<Record<string, string>>((result, definition) => {
			result[definition.key] = currentValues[definition.key] ?? '';
			return result;
		}, {});
	}

	private buildSamplePayloadInputs(): Record<string, string | null> {
		return this.buildInputRequest(this.sampleInputDefinitions, this.sampleInputValues);
	}

	private buildInputRequest(definitions: MapperSampleInputDefinition[], values: Record<string, string>): Record<string, string | null> {
		return definitions.reduce<Record<string, string | null>>((result, definition) => {
			const value = (values[definition.key] ?? '').trim();
			result[definition.key] = value.length > 0 ? value : null;
			return result;
		}, {});
	}

	private buildTransferByKeyInputs(value: string): Record<string, string | null> {
		if (!this.transferByKeySetting) {
			return {};
		}

		return {
			[this.transferByKeySetting.inputKey]: value.trim() || null
		};
	}

  private getCurrentSamplePayloadInputSignature(): string {
    return JSON.stringify(this.buildSamplePayloadInputs());
  }

  private hasLoadedCurrentSamplePayload(): boolean {
    if (!this.configuration || this.configuration.sampleSourcePayload == null) {
      return false;
    }

    if (!this.hasSampleInputs) {
      return true;
    }

    if (!this.configuration.samplePayloadRetrieved) {
      return false;
    }

    return this.loadedSamplePayloadInputSignature === this.getCurrentSamplePayloadInputSignature();
  }

	private hasRequiredSampleInputs(): boolean {
		return this.hasMissingRequiredInputValues(this.sampleInputDefinitions, this.sampleInputValues);
	}

	private hasMissingRequiredInputValues(definitions: MapperSampleInputDefinition[], values: Record<string, string>): boolean {
		return definitions.some(definition => definition.required && !(values[definition.key] ?? '').trim());
	}

	private getTargetSchemaEnrichmentInputDefinitions(enrichment: { inputDefinitions?: MapperSampleInputDefinition[] | null; } | null | undefined): MapperSampleInputDefinition[] {
		return enrichment?.inputDefinitions ?? [];
	}

  private ensureCurrentSamplePayload(forceReload = false) {
    if (!this.configuration || this.hasRequiredSampleInputs()) {
      return of(false);
    }

    if (!forceReload && this.hasLoadedCurrentSamplePayload()) {
      return of(true);
    }

    return this.api.getSampleSourcePayload(
      this.configuration.linkId,
      this.configuration.sourceEntity,
			{
				...this.buildSamplePayloadInputs(),
				deferReferenceChildren: 'true'
			}
		).pipe(
			switchMap(response => from(this.applySamplePayloadResponse(response)).pipe(
				map(() => response?.samplePayloadRetrieved === true)
			))
		);
	}

	private async applySamplePayloadResponse(response: GetSamplePayloadResponse): Promise<void> {
		if (!this.configuration) {
			return;
		}

		const currentMapping = this.buildMappingDefinition();
		const existingTransferByKeySetting = this.configuration.transferByKeySetting ?? null;
		const nextTransferByKeySetting = response?.transferByKeySetting
			? {
				...response.transferByKeySetting,
				appliedValue: response.transferByKeySetting.appliedValue ?? existingTransferByKeySetting?.appliedValue ?? null
			}
			: existingTransferByKeySetting;

		this.configuration = {
			...this.configuration,
			sampleSourcePayload: response?.samplePayload ?? null,
			samplePayloadRetrieved: response?.samplePayloadRetrieved === true,
			samplePayloadStatusMessage: response?.statusMessage ?? null,
			sampleInputDefinitions: response?.inputDefinitions ?? this.configuration.sampleInputDefinitions ?? [],
			transferByKeySetting: nextTransferByKeySetting
		};
		this.cacheCurrentConfiguration();
		this.loadedSamplePayloadInputSignature = (!this.hasSampleInputs || response?.samplePayloadRetrieved === true)
			? this.getCurrentSamplePayloadInputSignature()
			: null;

		this.rebuildRootNodeFromConfiguration();
		await this.applySavedMappingDefinition(currentMapping);
		this.enforceSingleGlobalLinkKey();
		this.applyRowFilters();
		this.refreshSelectedRow();

		this.previewSourceJson = this.configuration.sampleSourcePayload ?? {};
		this.lastActionMessage = this.configuration.samplePayloadStatusMessage
			|| (this.configuration.samplePayloadRetrieved ? 'Sample source payload loaded.' : 'Sample source payload was not retrieved.');
	}

	private loadTransferByKeySettingState(linkId: number, sourceEntity: string): void {
		if (!this.configuration?.transferByKeySetting) {
			return;
		}

		this.api.getTransferByKeySettingState(linkId, sourceEntity)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: transferByKeySetting => {
					if (!this.configuration) {
						return;
					}

					if (
						this.configuration.linkId !== linkId
						|| this.configuration.sourceEntity !== sourceEntity
					) {
						return;
					}

					this.configuration = {
						...this.configuration,
						transferByKeySetting: transferByKeySetting
					};
					this.cacheCurrentConfiguration();

					if (!this.transferByIdDraftValue.trim()) {
						this.transferByIdDraftValue = (transferByKeySetting?.appliedValue ?? '').trim();
					}
				},
				error: () => {
					// Keep the loaded mapper state if the live transfer-by-key refresh fails.
				}
			});
	}

	private rebuildArtifactTraceItems(): void {
		const result = this.latestTestRunResultValue;
		this.artifactTraceItems = (result?.artifacts ?? []).map((artifact, index) => ({
			key: `${result?.sessionId ?? 'session'}:${artifact?.sourceId ?? 'record'}:${index}`,
			sourceId: artifact?.sourceId ?? 'record',
			summary: this.formatMapperArtifactSummary(artifact),
			actionLabel: artifact?.targetResult?.actionPerformed || 'No target action recorded yet',
			statusLabel: this.formatMapperArtifactStatus(artifact),
			statusTone: this.getMapperArtifactTone(artifact),
			errorMessage: artifact?.validationMessage || artifact?.targetResult?.errorMessage || artifact?.writeback?.errorMessage || '',
			panels: this.getArtifactTracePanels(artifact)
		}));
	}

	private formatMapperArtifactSummary(artifact: MapperTestRunArtifact): string {
		const linkKeyValue = artifact?.linkKeyValue?.trim();
		if (linkKeyValue) {
			return `Source ${artifact.sourceId} via ${linkKeyValue}`;
		}

		return `Source ${artifact?.sourceId || 'record'}`;
	}

	private getMapperArtifactTone(artifact: MapperTestRunArtifact): 'success' | 'danger' | 'warning' | 'muted' | 'info' {
		const status = (artifact?.status || '').toLowerCase();
		if (status.includes('failed')) {
			return 'danger';
		}

		if (status === 'created' || status === 'updated') {
			return 'success';
		}

		if (status.includes('ready') || status === 'pending') {
			return 'info';
		}

		if (status.includes('validation')) {
			return 'warning';
		}

		return 'muted';
	}

	private formatMapperArtifactStatus(artifact: MapperTestRunArtifact): string {
		switch (artifact?.status) {
			case 'ValidationFailed':
				return 'Validation failed';
			case 'UpsertFailed':
				return 'Upsert failed';
			case 'WritebackFailed':
				return 'Writeback failed';
			case 'UpdateReady':
				return 'Ready to update';
			case 'CreateReady':
				return 'Ready to create';
			default:
				return artifact?.status || 'Unknown';
		}
	}

	formatMapperArtifactJson(value: any): string {
		if (value === null || value === undefined) {
			return '';
		}

		try {
			return JSON.stringify(value, null, 2);
		} catch {
			return String(value);
		}
	}

	private getArtifactTracePanels(artifact: MapperTestRunArtifact): MapperArtifactTracePanel[] {
		const traceIndex = this.buildArtifactTraceIndex(artifact);
		const panels: MapperArtifactTracePanel[] = [
			this.buildArtifactPayloadTracePanel(
				'source',
				'Retrieved source',
				'Original record',
				'Source artifact was not captured for this run.',
				artifact?.retrievedSourceRecord,
				traceIndex),
			this.buildArtifactRuleTracePanel(traceIndex),
			this.buildArtifactPayloadTracePanel(
				'mapped',
				'Mapped payload',
				'Preview result',
				'Mapped payload was not captured for this run.',
				artifact?.transformedPayload,
				traceIndex),
			this.buildArtifactPayloadTracePanel(
				'posted',
				'Posted target payload',
				'Upsert request',
				'Posted payload was not captured for this run.',
				artifact?.targetResult?.postedPayload,
				traceIndex),
			this.buildArtifactPayloadTracePanel(
				'result',
				'Target result',
				'Upsert response',
				'Target result was not captured for this run.',
				artifact?.targetResult?.targetRecord,
				traceIndex),
			this.buildArtifactPayloadTracePanel(
				'writeback',
				'Writeback request',
				'Source update',
				'No writeback request was captured for this run.',
				artifact?.writeback?.requestValues,
				traceIndex)
		];

		return panels.filter(panel => panel.rows.length > 0 || panel.stage === 'rule');
	}

	toggleUnmappedArtifactFields(): void {
		this.showUnmappedArtifactFields = !this.showUnmappedArtifactFields;
		this.writeShowUnmappedArtifactFieldsPreference(this.showUnmappedArtifactFields);
	}

	private buildArtifactPayloadTracePanel(
		stage: MapperArtifactTraceStage,
		title: string,
		subtitle: string,
		emptyText: string,
		payload: any,
		traceIndex: MapperArtifactTraceIndex): MapperArtifactTracePanel {
		const rows = prioritizeAndLimitArtifactTraceRows(
			this.flattenArtifactPayload(payload)
				.map(item => this.buildArtifactPayloadTraceRow(stage, item.path, item.value, traceIndex)),
			80);

		return {
			stage,
			title,
			subtitle,
			emptyText,
			rows,
			mappedRows: rows.filter(row => !!row.traceId)
		};
	}

	private buildArtifactRuleTracePanel(traceIndex: MapperArtifactTraceIndex): MapperArtifactTracePanel {
		const rows: MapperArtifactTraceRow[] = traceIndex.rules.map(rule => ({
			stage: 'rule',
			path: rule.targetPath,
			label: rule.label,
			value: rule.description,
			traceId: rule.traceId,
			traceLabel: rule.label,
			description: rule.description,
			tone: rule.tone
		}));

		return {
			stage: 'rule',
			title: 'Mapping rule',
			subtitle: 'Trace source to target',
			emptyText: 'No current mapping rules could be matched to this artifact.',
			rows,
			mappedRows: rows
		};
	}

	private buildArtifactPayloadTraceRow(
		stage: MapperArtifactTraceStage,
		path: string,
		value: any,
		traceIndex: MapperArtifactTraceIndex): MapperArtifactTraceRow {
		const normalizedPath = this.normalizeArtifactTracePath(path);
		const traceId = this.resolveArtifactTraceId(stage, normalizedPath, traceIndex);
		const fallbackLabel = path;

		return {
			stage,
			path,
			label: this.formatArtifactTracePathLabel(path),
			value: this.formatArtifactTraceValue(value),
			traceId,
			traceLabel: traceId ? traceIndex.traceLabels.get(traceId) ?? fallbackLabel : fallbackLabel,
			description: traceId ? traceIndex.traceDescriptions.get(traceId) ?? 'Path appears in the captured mapper artifact data.' : 'No mapping trace was found for this path.',
			tone: traceId ? traceIndex.traceTones.get(traceId) ?? 'direct' : 'muted'
		};
	}

	private resolveArtifactTraceId(
		stage: MapperArtifactTraceStage,
		normalizedPath: string,
		traceIndex: MapperArtifactTraceIndex): string | null {
		if (!normalizedPath) {
			return null;
		}

		if (stage === 'source') {
			return traceIndex.sourcePathTraceIds.get(normalizedPath) ?? null;
		}

		if (stage === 'writeback') {
			return traceIndex.writebackSourcePathTraceIds.get(normalizedPath) ?? null;
		}

		return traceIndex.targetPathTraceIds.get(normalizedPath) ?? null;
	}

	private buildArtifactTraceIndex(artifact: MapperTestRunArtifact): MapperArtifactTraceIndex {
		const artifactKey = this.buildArtifactTraceKey(artifact);
		const index: MapperArtifactTraceIndex = {
			sourcePathTraceIds: new Map<string, string>(),
			targetPathTraceIds: new Map<string, string>(),
			writebackSourcePathTraceIds: new Map<string, string>(),
			traceLabels: new Map<string, string>(),
			traceDescriptions: new Map<string, string>(),
			traceTones: new Map<string, MapperArtifactTraceTone>(),
			rules: []
		};

		this.getArtifactMappingRows(artifact)
			.filter(row => row.mode !== 'Ignore' && !!row.targetPath?.trim())
			.forEach(row => {
				const targetPath = row.targetPath.trim();
				const sourcePaths = this.getArtifactRuleSourcePaths(row);
				const traceId = `${artifactKey}:target:${this.normalizeArtifactTracePath(targetPath)}`;
				const tone = this.getArtifactRuleTone(row);
				const label = row.targetDisplayName || targetPath;
				const description = this.describeArtifactMappingRule(row, sourcePaths);

				index.targetPathTraceIds.set(this.normalizeArtifactTracePath(targetPath), traceId);
				sourcePaths.forEach(sourcePath => index.sourcePathTraceIds.set(this.normalizeArtifactTracePath(sourcePath), traceId));
				index.traceLabels.set(traceId, label);
				index.traceDescriptions.set(traceId, description);
				index.traceTones.set(traceId, tone);
				index.rules.push({
					traceId,
					targetPath,
					sourcePath: sourcePaths[0] ?? null,
					label,
					description,
					tone
				});
			});

		const artifactAction = `${artifact?.targetResult?.actionPerformed ?? artifact?.status ?? ''}`.toLowerCase();
		const artifactWritebacks = artifactAction.includes('create')
			? this.createWritebacks
			: artifactAction.includes('update')
				? this.updateWritebacks
				: [...this.createWritebacks, ...this.updateWritebacks];

		artifactWritebacks
			.filter(writeback => !!writeback.sourceFieldPath?.trim())
			.forEach(writeback => {
				const sourceFieldPath = writeback.sourceFieldPath.trim();
				const targetFieldPath = writeback.targetFieldPath?.trim() ?? '';
				const traceId = `${artifactKey}:writeback:${this.normalizeArtifactTracePath(sourceFieldPath)}:${this.normalizeArtifactTracePath(targetFieldPath)}`;
				const label = `Writeback ${sourceFieldPath}`;
				const description = this.describeArtifactWritebackRule(writeback);

				index.writebackSourcePathTraceIds.set(this.normalizeArtifactTracePath(sourceFieldPath), traceId);
				if (targetFieldPath) {
					index.targetPathTraceIds.set(this.normalizeArtifactTracePath(targetFieldPath), traceId);
				}

				index.traceLabels.set(traceId, label);
				index.traceDescriptions.set(traceId, description);
				index.traceTones.set(traceId, 'writeback');
				index.rules.push({
					traceId,
					targetPath: sourceFieldPath,
					sourcePath: targetFieldPath || null,
					label,
					description,
					tone: 'writeback'
				});
			});

		return index;
	}

	private getArtifactMappingRows(artifact: MapperTestRunArtifact): MappingGridRow[] {
		const rootNode = this.rootMappingNode;
		if (!rootNode) {
			return [];
		}

		const action = `${artifact?.targetResult?.actionPerformed ?? artifact?.status ?? ''}`.toLowerCase();
		if (action.includes('update')) {
			return rootNode.updateMappingRows ?? [];
		}

		if (action.includes('create')) {
			return rootNode.createMappingRows ?? [];
		}

		return rootNode.createMappingRows?.length ? rootNode.createMappingRows : rootNode.updateMappingRows ?? [];
	}

	private getArtifactRuleSourcePaths(row: MappingGridRow): string[] {
		const paths = new Set<string>();
		const addPath = (path: string | null | undefined) => {
			const trimmed = path?.trim();
			if (trimmed) {
				paths.add(trimmed);
			}
		};

		addPath(row.sourceFieldPath);
		addPath(row.picklistMapping?.sourceFieldPath);
		this.extractExpressionSourcePaths(row.value).forEach(addPath);
		(row.lookup?.criteria ?? []).forEach(criterion => {
			addPath(criterion.sourceFieldPath);
			this.extractExpressionSourcePaths(criterion.value).forEach(addPath);
		});

		return Array.from(paths);
	}

	private getArtifactRuleTone(row: MappingGridRow): MapperArtifactTraceTone {
		if (row.mode === 'SourceField' && !row.normalization && !row.picklistMapping) {
			return 'direct';
		}

		return 'derived';
	}

	private describeArtifactMappingRule(row: MappingGridRow, sourcePaths: string[]): string {
		const targetPath = row.targetPath || row.targetDisplayName;
		const sourceText = sourcePaths.length ? sourcePaths.join(', ') : 'no source field';

		if (row.mode === 'SourceField') {
			const transforms: string[] = [];
			if (row.normalization) {
				transforms.push(...this.getNormalizationLabels(row.normalization));
			}

			if (row.picklistMapping) {
				transforms.push('picklist map');
			}

			return transforms.length
				? `${sourceText} -> ${targetPath} using ${transforms.join(' + ')}.`
				: `${sourceText} -> ${targetPath}.`;
		}

		if (row.mode === 'Expression') {
			return sourcePaths.length
				? `Expression using ${sourceText} -> ${targetPath}: ${row.value || 'no expression text captured in this row'}.`
				: `Expression -> ${targetPath}: ${row.value || 'no expression text captured in this row'}.`;
		}

		if (row.mode === 'Constant') {
			return `Constant value -> ${targetPath}.`;
		}

		if (row.mode === 'Lookup') {
			return `Lookup ${row.lookup?.referenceEntityName || 'record'} -> ${targetPath} using ${sourceText}.`;
		}

		return `${row.mode} rule -> ${targetPath}.`;
	}

	private extractExpressionSourcePaths(expression: string | null | undefined): string[] {
		const sourcePaths = new Set<string>();
		const text = expression?.trim();
		if (!text) {
			return [];
		}

		const addPath = (path: string | null | undefined) => {
			const trimmed = path?.trim();
			if (trimmed) {
				sourcePaths.add(this.resolveKnownSourceFieldPath(trimmed));
			}
		};

		const bracketPattern = /\[([^\]]+)\]/g;
		let bracketMatch: RegExpExecArray | null;
		while ((bracketMatch = bracketPattern.exec(text)) !== null) {
			addPath(bracketMatch[1]);
		}

		const formPathPattern = /\b(?:Form|Source)\.([A-Za-z_][A-Za-z0-9_]*(?:\[[^\]]+\])?(?:\.[A-Za-z_][A-Za-z0-9_]*(?:\[[^\]]+\])?)*)/g;
		let formMatch: RegExpExecArray | null;
		while ((formMatch = formPathPattern.exec(text)) !== null) {
			addPath(formMatch[1]);
		}

		return Array.from(sourcePaths);
	}

	private resolveKnownSourceFieldPath(path: string): string {
		const sourceOptions = this.rootMappingNode?.sourceFieldOptions ?? [];
		const normalizedPath = this.normalizeArtifactTracePath(path);
		const exactMatch = sourceOptions.find(option => this.normalizeArtifactTracePath(option.path) === normalizedPath);
		if (exactMatch?.path) {
			return exactMatch.path;
		}

		const prefixMatch = sourceOptions
			.filter(option => {
				const optionPath = this.normalizeArtifactTracePath(option.path);
				return !!optionPath && normalizedPath.startsWith(`${optionPath}.`);
			})
			.sort((left, right) => right.path.length - left.path.length)[0];

		return prefixMatch?.path ?? path;
	}

	private getNormalizationLabels(normalization: MappingRuleNormalization): string[] {
		const labels: string[] = [];
		const rawNormalization = normalization as MappingRuleNormalization & { omitWhenNull?: boolean | null };

		if (normalization.trimWhitespace) {
			labels.push('Trim');
		}

		if (normalization.emptyStringToNull) {
			labels.push('Empty -> null');
		}

		if (rawNormalization.omitWhenNull) {
			labels.push('Omit null');
		}

		if (normalization.truncateToMaxLength) {
			labels.push(normalization.maxLength ? `Truncate ${normalization.maxLength}` : 'Truncate');
		}

		if (normalization.padLeft) {
			labels.push(normalization.padLeftToLength ? `Pad left ${normalization.padLeftToLength}` : 'Pad left');
		}

		if (normalization.newlineMode) {
			labels.push(`Newlines ${normalization.newlineMode}`);
		}

		if (normalization.numberFormat) {
			labels.push(`Number ${normalization.numberFormat}`);
		}

		if (normalization.dateFormat) {
			labels.push(`Date ${normalization.dateFormat}`);
		}

		return labels.length ? labels : ['Normalization'];
	}

	private describeArtifactWritebackRule(writeback: MappingWriteback): string {
		if (writeback.mode === 'TargetValue') {
			return `${writeback.targetFieldPath || 'target field'} -> source ${writeback.sourceFieldPath}.`;
		}

		if (writeback.mode === 'CurrentDate') {
			return `Current date -> source ${writeback.sourceFieldPath}.`;
		}

		return `Expression -> source ${writeback.sourceFieldPath}.`;
	}

	private flattenArtifactPayload(value: any, parentPath = '', depth = 0): Array<{ path: string; value: any }> {
		if (value === null || value === undefined) {
			return [];
		}

		if (depth > 5 || !this.isArtifactTraceObject(value)) {
			return parentPath ? [{ path: parentPath, value }] : [];
		}

		if (Array.isArray(value)) {
			const results: Array<{ path: string; value: any }> = [];
			value.forEach((item, index) => {
				results.push(...this.flattenArtifactPayload(item, `${parentPath}[${index}]`, depth + 1));
			});
			return results;
		}

		const results: Array<{ path: string; value: any }> = [];
		Object.keys(value).forEach(key => {
			const nextPath = parentPath ? `${parentPath}.${key}` : key;
			const item = value[key];
			if (item === null || item === undefined || !this.isArtifactTraceObject(item)) {
				results.push({ path: nextPath, value: item });
				return;
			}

			results.push(...this.flattenArtifactPayload(item, nextPath, depth + 1));
		});

		return results;
	}

	private isArtifactTraceObject(value: any): boolean {
		return typeof value === 'object' && value !== null;
	}

	private formatArtifactTracePathLabel(path: string): string {
		const parts = path.split('.');
		return parts[parts.length - 1] || path;
	}

	private formatArtifactTraceValue(value: any): string {
		if (value === null) {
			return 'null';
		}

		if (value === undefined) {
			return 'undefined';
		}

		if (typeof value === 'string') {
			return `"${value}"`;
		}

		if (typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}

		try {
			return JSON.stringify(value);
		} catch {
			return String(value);
		}
	}

	private normalizeArtifactTracePath(path: string | null | undefined): string {
		return (path ?? '')
			.replace(/\[\d+\]/g, '')
			.trim()
			.toLowerCase();
	}

	private buildArtifactTraceKey(artifact: MapperTestRunArtifact): string {
		return `artifact:${artifact?.sourceId || 'record'}`;
	}

	getMapperExpectationComparisonTone(status: string | null | undefined): 'success' | 'danger' | 'warning' | 'muted' {
		switch ((status || '').trim().toLowerCase()) {
			case 'matched':
				return 'success';
			case 'mismatch':
				return 'danger';
			case 'not-evaluated':
				return 'warning';
			default:
				return 'muted';
		}
	}

	getSavedRunDiffTone(type: 'same' | 'added' | 'removed' | 'changed'): 'success' | 'danger' | 'warning' | 'muted' | 'info' {
		switch (type) {
			case 'added':
				return 'success';
			case 'removed':
				return 'danger';
			case 'changed':
				return 'warning';
			case 'same':
				return 'muted';
			default:
				return 'info';
		}
	}

	async saveLatestRunAsBaseline(): Promise<void> {
		const linkId = this.linkId ?? 0;
		const result = this.latestTestRunResult;
		const sessionId = (result?.sessionId || this.activeTestRunSessionId).trim();
		const sourceEntity = this.configuration?.sourceEntity?.trim() ?? '';
		const targetEntity = this.configuration?.targetEntity?.trim() ?? '';
		if (!this.canSaveLatestRunAsBaseline || linkId <= 0 || !result || !sessionId || !sourceEntity || !targetEntity) {
			return;
		}

		const profileName = (this.selectedProfileName || this.profileName || this.configuration?.activeProfileName || '').trim() || null;
		const transferByKeyValue = (this.transferByKeyAppliedValue || '').trim() || null;
		const confirmation = await Swal.fire({
			icon: 'question',
			title: this.savedBaselineTestRun ? 'Replace saved test run?' : 'Save test run?',
			text: this.savedBaselineTestRun
				? `This will replace the current saved baseline with session '${sessionId}'.`
				: `This will save session '${sessionId}' as the baseline test run for this mapper.`,
			showCancelButton: true,
			confirmButtonText: this.savedBaselineTestRun ? 'Replace saved run' : 'Save test run',
			cancelButtonText: 'Cancel'
		});

		if (!confirmation.isConfirmed) {
			return;
		}

		this.savedBaselineTestRun = {
			linkId,
			sourceEntity,
			targetEntity,
			sessionId,
			savedAt: new Date().toISOString(),
			profileName,
			transferByKeyValue,
			result: this.cloneMapperRunResult(result)
		};
		this.comparisonSessionId = '';
		this.comparisonRunResult = null;
		this.hasExplicitSavedRunComparison = false;
		this.persistSavedBaselineTestRun();
		this.lastActionMessage = `Saved mapper test run baseline from session '${sessionId}'.`;
		notify({
			message: this.lastActionMessage,
			type: 'success',
			displayTime: 2500,
		});
	}

	toggleCaptureArtifactsForSession(): void {
		if (!this.canUseAdminRunTools) {
			return;
		}

		this.captureArtifactsForSession = !this.captureArtifactsForSession;
		this.writeArtifactCaptureSessionPreference(this.captureArtifactsForSession);
		this.lastActionMessage = this.captureArtifactsForSession
			? 'Artifact capture enabled in this browser.'
			: 'Artifact capture disabled in this browser.';
	}

	clearSavedBaseline(): void {
		if (!this.savedBaselineTestRun) {
			return;
		}

		this.savedBaselineTestRun = null;
		this.comparisonRunResult = null;
		this.comparisonSessionId = '';
		this.hasExplicitSavedRunComparison = false;
		this.removeSavedBaselineTestRun();
		this.lastActionMessage = 'Cleared the saved mapper test run baseline.';
	}

	compareRunAgainstSavedBaseline(): void {
		const linkId = this.linkId ?? 0;
		const sourceEntity = this.configuration?.sourceEntity ?? '';
		const targetEntity = this.configuration?.targetEntity ?? '';
		const sessionId = this.comparisonSessionId.trim();
		if (this.isComparingSavedBaselineToSameSession(sessionId)) {
			this.lastActionMessage = 'Select a different session id. The saved baseline cannot be compared against itself.';
			return;
		}

		if (!this.canCompareAnotherRunAgainstSavedBaseline || linkId <= 0 || !sourceEntity || !targetEntity || !sessionId) {
			return;
		}

		this.isLoadingComparisonRun = true;
		this.api.getTestRunResults(linkId, sessionId, sourceEntity, targetEntity)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: result => {
					this.isLoadingComparisonRun = false;
					this.comparisonRunResult = result;
					this.hasExplicitSavedRunComparison = true;
					this.lastActionMessage = `Loaded session '${result?.sessionId || sessionId}' for comparison.`;
				},
				error: error => {
					this.isLoadingComparisonRun = false;
					this.comparisonRunResult = null;
					this.hasExplicitSavedRunComparison = false;
					this.showMapperApiError(error, 'Failed to load the selected mapper run for comparison.', 'Mapper comparison run could not be loaded');
				}
			});
	}

  onLinkKeyChanged(changedRow: MappingGridRow): void {
    const node = this.selectedMappingNode;
    if (!node) {
      return;
    }

    if (!changedRow.isLinkKey) {
			this.clearSharedLinkKey(node, changedRow.targetFieldId);
			this.lastActionMessage = `Record link key cleared for ${changedRow.targetDisplayName}.`;
			this.applyRowFilters();
			this.refreshSelectedRow();
      return;
    }

		this.synchronizeSharedLinkKeyFromRow(changedRow);

		this.lastActionMessage = `Record link key set to ${changedRow.targetDisplayName} for create and update.`;
		this.applyRowFilters();
		this.refreshSelectedRow();
  }

  private cacheCurrentConfiguration(): void {
    if (!this.metadataCachingEnabled || !this.configuration) {
      return;
    }

    this.api.cacheConfiguration(
      this.configuration.linkId,
      this.configuration.sourceEntity,
      this.configuration.targetEntity,
      this.configuration
    );
  }

	private refreshRuntimeMessagePropertyExamples(): void {
		const sourceSchema = this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema;
		const targetSchema = this.configuration?.targetSchema;

		this.runtimeMessageSourcePropertyExamples = this.flattenSchemaToFieldOptions(
			sourceSchema?.fields ?? [],
			'',
			'',
			field => this.isLeafField(field) && field.canRead
		).map(field => `{{source.${field.path}}}`);
		this.runtimeMessageSourceTreeItems = this.mapSchemaToTreeNodes(sourceSchema?.fields ?? []);

		this.runtimeMessageTargetPropertyExamples = this.flattenSchemaToFieldOptions(
			targetSchema?.fields ?? [],
			'',
			'',
			field => this.isLeafField(field) && (field.canRead || field.canCreate || field.canUpdate)
		).map(field => `{{target.${field.path}}}`);
		this.runtimeMessageTargetTreeItems = this.mapSchemaToTreeNodes(targetSchema?.fields ?? []);
	}

	private initializeLinkRunStatusTracking(): void {
		this.linkStatusService.startService()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				if ((this.companyId ?? 0) > 0) {
					this.linkStatusService.joinCompany(this.companyId);
				}

				this.queueService.isLinkRunning(this.linkId ?? 0)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: isRunning => {
							this.isLinkRunning = isRunning;
							if (isRunning) {
								this.testRunStatusText = 'Link is running. Mapper results will appear here when the run finishes.';
							}
						},
						error: () => {
							// Ignore initial status probe failures in the mapper shell.
						}
					});
			});

		this.linkStatusService.getLinkButtonObservable()
			.pipe(
				takeUntil(this.destroy$),
				filter(status => status.linkID === (this.linkId ?? 0))
			)
			.subscribe(status => {
				const wasRunning = this.isLinkRunning;
				this.isLinkRunning = status.isLinkRunning;

				if (status.isLinkRunning) {
					this.isTestRunStarting = false;
					this.testRunStatusText = 'Link is running. Mapper results will appear here when the run finishes.';
					return;
				}

				if (wasRunning) {
					this.hasRecentTestRun = true;
					this.testRunStatusText = this.activeTestRunSessionId
						? 'Link run finished. Waiting for mapper session completion signal...'
						: 'Link run finished.';
				}
			});

		this.linkStatusService.getMapperRunCompletedObservable()
			.pipe(
				takeUntil(this.destroy$),
				filter(update => update.linkId === (this.linkId ?? 0)),
				filter(update => !!update.sessionId && update.sessionId === this.activeTestRunSessionId)
			)
			.subscribe(update => {
				this.hasRecentTestRun = true;
				this.mapperRunProgressStage = 'Completed';
				this.mapperRunProgressProcessedCount = this.mapperRunProgressTotalCount;
				this.mapperRunProgressMessage = 'Mapper session completed. Loading final results...';
				this.loadLatestTestRunResult(update.sessionId);
			});

		this.linkStatusService.getMapperRunProgressObservable()
			.pipe(
				takeUntil(this.destroy$),
				filter(update => update.linkId === (this.linkId ?? 0)),
				filter(update => !!update.sessionId && update.sessionId === this.activeTestRunSessionId)
			)
			.subscribe(update => {
				this.mapperRunProgressStage = update.stage || '';
				this.mapperRunProgressProcessedCount = update.processedCount ?? 0;
				this.mapperRunProgressTotalCount = update.totalCount ?? 0;
				this.mapperRunProgressMessage = update.message || '';
				if (update.message?.trim()) {
					this.testRunStatusText = update.message.trim();
				}
			});
	}

	private loadLatestTestRunResult(sessionId: string): void {
		const linkId = this.linkId ?? 0;
		const sourceEntity = this.configuration?.sourceEntity ?? '';
		const targetEntity = this.configuration?.targetEntity ?? '';
		if (linkId <= 0 || !sessionId.trim() || !sourceEntity || !targetEntity) {
			return;
		}

		this.isLoadingLatestTestRunResult = true;
		this.api.getTestRunResults(linkId, sessionId, sourceEntity, targetEntity)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: result => {
					this.isLoadingLatestTestRunResult = false;
					this.latestTestRunResult = result;
					this.activeTestRunSessionId = result?.sessionId || sessionId;
					this.hasRecentTestRun = !!result?.isCompleted;
					if (this.comparisonRunResult?.sessionId === (result?.sessionId || sessionId)) {
						this.comparisonRunResult = result;
					}
					this.testRunStatusText = this.buildTestRunStatusText(result);
				},
				error: () => {
					this.isLoadingLatestTestRunResult = false;
					this.testRunStatusText = 'Link run finished, but mapper results could not be loaded yet.';
				}
			});
	}

	private buildTestRunStatusText(result: MapperTestRunResult | null): string {
		if (!result) {
			return 'Link run finished.';
		}

		if (!result.isCompleted) {
			return 'Link enqueued, waiting for execution to finish.';
		}

		if (this.hasTerminalTestRunErrors(result)) {
			const latestMessage = result.messages?.length
				? result.messages[result.messages.length - 1]?.message?.trim()
				: '';
			return latestMessage || 'Run failed. Review the mapper messages for details.';
		}

		const successCount = (result.createdCount ?? 0) + (result.updatedCount ?? 0);
		const issueCount = (result.validationFailures ?? 0) + (result.upsertFailures ?? 0) + (result.writebackFailures ?? 0);
		return `Run completed with ${successCount} successful change${successCount === 1 ? '' : 's'} and ${issueCount} issue${issueCount === 1 ? '' : 's'}.`;
	}

	private get hasLatestTestRunFailure(): boolean {
		return this.hasTerminalTestRunErrors(this.latestTestRunResult);
	}

	private hasTerminalTestRunErrors(result: MapperTestRunResult | null | undefined): boolean {
		if (!result?.isCompleted) {
			return false;
		}

		return (result.messages ?? []).some(message => {
			const typeName = message?.jobLogTypeName?.trim().toLowerCase();
			return typeName === 'error' || typeName === 'fail';
		});
	}

	private isComparingSavedBaselineToSameSession(sessionId: string): boolean {
		const baselineSessionId = this.savedBaselineTestRun?.sessionId?.trim();
		return !!baselineSessionId && baselineSessionId === sessionId.trim();
	}

	private buildSavedRunComparisonLog(saved: MapperTestRunResult | null, current: MapperTestRunResult | null): MapperSavedRunComparisonItem[] {
		if (!saved || !current) {
			return [];
		}

		if (this.isComparingSavedBaselineToSameSession(current.sessionId || '')) {
			return [
				{
					label: 'Comparison unavailable',
					saved: saved.sessionId || 'n/a',
					current: current.sessionId || 'n/a',
					status: 'Not-evaluated',
					type: 'guard',
					notes: 'Load a different session id to compare actual run differences against the saved baseline.'
				}
			];
		}

		const items: MapperSavedRunComparisonItem[] = [];
		const addNumberComparison = (label: string, savedValue: number | null | undefined, currentValue: number | null | undefined, notes?: string | null) => {
			const baselineValue = savedValue ?? 0;
			const currentRunValue = currentValue ?? 0;
			items.push({
				label,
				saved: String(baselineValue),
				current: String(currentRunValue),
				status: baselineValue === currentRunValue ? 'Matched' : 'Mismatch',
				type: 'summary',
				notes: notes ?? null
			});
		};

		const addTextComparison = (label: string, savedValue: string | null | undefined, currentValue: string | null | undefined, notes?: string | null) => {
			const baselineValue = (savedValue ?? '').trim() || 'n/a';
			const currentRunValue = (currentValue ?? '').trim() || 'n/a';
			items.push({
				label,
				saved: baselineValue,
				current: currentRunValue,
				status: baselineValue.toLowerCase() === currentRunValue.toLowerCase() ? 'Matched' : 'Mismatch',
				type: 'summary',
				notes: notes ?? null
			});
		};

		addNumberComparison('Created count', saved.createdCount, current.createdCount);
		addNumberComparison('Updated count', saved.updatedCount, current.updatedCount);
		addNumberComparison('Validation failures', saved.validationFailures, current.validationFailures);
		addNumberComparison('Upsert failures', saved.upsertFailures, current.upsertFailures);
		addNumberComparison('Writeback failures', saved.writebackFailures, current.writebackFailures);
		addNumberComparison('Artifact count', saved.artifacts?.length ?? 0, current.artifacts?.length ?? 0, 'Compares the number of captured per-record artifacts.');
		addTextComparison('Result state', this.buildSavedRunOutcome(saved), this.buildSavedRunOutcome(current));
		items.push(...this.buildSavedRunArtifactComparisons(saved, current));

		return items;
	}

	private buildSavedRunArtifactComparisons(saved: MapperTestRunResult, current: MapperTestRunResult): MapperSavedRunComparisonItem[] {
		const savedArtifact = saved.artifacts?.[0] ?? null;
		const currentArtifact = current.artifacts?.[0] ?? null;
		if (!savedArtifact || !currentArtifact) {
			return [
				{
					label: 'Artifact payload comparison',
					saved: savedArtifact ? 'available' : 'missing',
					current: currentArtifact ? 'available' : 'missing',
					status: savedArtifact && currentArtifact ? 'Matched' : 'Mismatch',
					type: 'artifact',
					notes: 'Both runs need a captured artifact to compare the actual transfer-single data.'
				}
			];
		}

		const comparisons: MapperSavedRunComparisonItem[] = [];
		const addJsonComparison = (label: string, savedValue: unknown, currentValue: unknown, notes?: string | null) => {
			const savedJson = this.formatMapperArtifactJson(savedValue);
			const currentJson = this.formatMapperArtifactJson(currentValue);
			const diffEntries = this.buildSavedRunSemanticDiffEntries(savedValue, currentValue);
			comparisons.push({
				label,
				saved: savedJson,
				current: currentJson,
				status: savedJson === currentJson ? 'Matched' : 'Mismatch',
				type: 'artifact',
				diffEntries,
				notes: notes ?? null
			});
		};

		addJsonComparison('Artifact source id', savedArtifact.sourceId, currentArtifact.sourceId);
		addJsonComparison('Retrieved source record', savedArtifact.retrievedSourceRecord, currentArtifact.retrievedSourceRecord, 'Compares the actual source data returned for the transfer-single run.');
		addJsonComparison('Mapped payload', savedArtifact.transformedPayload, currentArtifact.transformedPayload, 'Compares the transformed payload sent toward the target.');
		addJsonComparison('Posted target payload', savedArtifact.targetResult?.postedPayload, currentArtifact.targetResult?.postedPayload, 'Compares the payload actually posted to the target runtime.');
		addJsonComparison('Target result record', savedArtifact.targetResult?.targetRecord, currentArtifact.targetResult?.targetRecord, 'Compares the returned target-side record/result data.');
		addJsonComparison('Writeback request', savedArtifact.writeback?.requestValues, currentArtifact.writeback?.requestValues, 'Compares the writeback values sent back to the source side.');

		return comparisons;
	}

	private buildSavedRunSemanticDiffEntries(savedValue: unknown, currentValue: unknown): MapperSavedRunDiffEntry[] {
		const entries: MapperSavedRunDiffEntry[] = [];
		this.collectSavedRunSemanticDiffEntries(savedValue, currentValue, '$', entries);
		return entries;
	}

	private collectSavedRunSemanticDiffEntries(savedValue: unknown, currentValue: unknown, path: string, entries: MapperSavedRunDiffEntry[]): void {
		if (this.areSemanticValuesEqual(savedValue, currentValue)) {
			return;
		}

		if (savedValue === undefined) {
			entries.push({
				path,
				savedValue: '(missing)',
				currentValue: this.formatSemanticDiffValue(currentValue),
				status: 'added'
			});
			return;
		}

		if (currentValue === undefined) {
			entries.push({
				path,
				savedValue: this.formatSemanticDiffValue(savedValue),
				currentValue: '(missing)',
				status: 'removed'
			});
			return;
		}

		if (this.isPlainObject(savedValue) && this.isPlainObject(currentValue)) {
			const keys = Array.from(new Set([
				...Object.keys(savedValue as Record<string, unknown>),
				...Object.keys(currentValue as Record<string, unknown>)
			])).sort((left, right) => left.localeCompare(right));

			for (const key of keys) {
				this.collectSavedRunSemanticDiffEntries(
					(savedValue as Record<string, unknown>)[key],
					(currentValue as Record<string, unknown>)[key],
					`${path}.${key}`,
					entries
				);
			}
			return;
		}

		if (Array.isArray(savedValue) && Array.isArray(currentValue)) {
			const maxLength = Math.max(savedValue.length, currentValue.length);
			for (let index = 0; index < maxLength; index++) {
				this.collectSavedRunSemanticDiffEntries(
					savedValue[index],
					currentValue[index],
					`${path}[${index}]`,
					entries
				);
			}
			return;
		}

		entries.push({
			path,
			savedValue: this.formatSemanticDiffValue(savedValue),
			currentValue: this.formatSemanticDiffValue(currentValue),
			status: 'changed'
		});
	}

	private areSemanticValuesEqual(left: unknown, right: unknown): boolean {
		if (left === right) {
			return true;
		}

		if (Array.isArray(left) && Array.isArray(right)) {
			if (left.length !== right.length) {
				return false;
			}

			return left.every((item, index) => this.areSemanticValuesEqual(item, right[index]));
		}

		if (this.isPlainObject(left) && this.isPlainObject(right)) {
			const leftKeys = Object.keys(left as Record<string, unknown>).sort();
			const rightKeys = Object.keys(right as Record<string, unknown>).sort();
			if (leftKeys.length !== rightKeys.length) {
				return false;
			}

			return leftKeys.every((key, index) => key === rightKeys[index] && this.areSemanticValuesEqual(
				(left as Record<string, unknown>)[key],
				(right as Record<string, unknown>)[key]
			));
		}

		return this.formatSemanticDiffValue(left) === this.formatSemanticDiffValue(right);
	}

	private isPlainObject(value: unknown): boolean {
		return !!value && typeof value === 'object' && !Array.isArray(value);
	}

	private formatSemanticDiffValue(value: unknown): string {
		if (value === undefined) {
			return '(missing)';
		}

		if (value === null) {
			return 'null';
		}

		if (typeof value === 'string') {
			return value;
		}

		if (typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}

		return this.formatMapperArtifactJson(value);
	}

	private buildSavedRunOutcome(result: MapperTestRunResult | null | undefined): string {
		if (!result?.isCompleted) {
			return 'not-completed';
		}

		if (this.hasTerminalTestRunErrors(result)) {
			return 'failed';
		}

		if ((result.validationFailures ?? 0) > 0) {
			return 'validation-failed';
		}

		if ((result.upsertFailures ?? 0) > 0) {
			return 'upsert-failed';
		}

		if ((result.writebackFailures ?? 0) > 0) {
			return 'writeback-failed';
		}

		if ((result.createdCount ?? 0) > 0 && (result.updatedCount ?? 0) === 0) {
			return 'created';
		}

		if ((result.updatedCount ?? 0) > 0 && (result.createdCount ?? 0) === 0) {
			return 'updated';
		}

		if ((result.createdCount ?? 0) > 0 && (result.updatedCount ?? 0) > 0) {
			return 'mixed';
		}

		return 'completed';
	}

	private cloneMapperRunResult(result: MapperTestRunResult): MapperTestRunResult {
		return JSON.parse(JSON.stringify(result)) as MapperTestRunResult;
	}

	private buildSavedBaselineStorageKey(linkId: number, sourceEntity: string, targetEntity: string): string {
		return `${LinkGenericMapperComponent.savedBaselineStorageKeyPrefix}::${linkId}::${sourceEntity}::${targetEntity}`;
	}

	private buildSampleInputStorageKey(linkId: number, sourceEntity: string, targetEntity: string): string {
		return `${LinkGenericMapperComponent.sampleInputStorageKeyPrefix}::${linkId}::${sourceEntity}::${targetEntity}`;
	}

	private readStoredSampleInputs(linkId: number, sourceEntity: string, targetEntity: string): { enabled: boolean; values: Record<string, string> | null } {
		try {
			if (typeof localStorage === 'undefined') {
				return { enabled: false, values: null };
			}

			const raw = localStorage.getItem(this.buildSampleInputStorageKey(linkId, sourceEntity, targetEntity));
			if (!raw) {
				return { enabled: false, values: null };
			}

			const parsed = JSON.parse(raw) as Record<string, string>;
			return { enabled: true, values: parsed ?? {} };
		} catch {
			return { enabled: false, values: null };
		}
	}

	private writeStoredSampleInputs(): void {
		const linkId = this.configuration?.linkId ?? this.linkId ?? 0;
		const sourceEntity = this.configuration?.sourceEntity ?? this.sourceEntity ?? '';
		const targetEntity = this.configuration?.targetEntity ?? this.targetEntity ?? '';
		if (!linkId || !sourceEntity || !targetEntity) {
			return;
		}

		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.setItem(
				this.buildSampleInputStorageKey(linkId, sourceEntity, targetEntity),
				JSON.stringify(this.sampleInputValues ?? {})
			);
		} catch {
			// Ignore storage write failures and keep the in-memory sample inputs.
		}
	}

	private removeStoredSampleInputs(): void {
		const linkId = this.configuration?.linkId ?? this.linkId ?? 0;
		const sourceEntity = this.configuration?.sourceEntity ?? this.sourceEntity ?? '';
		const targetEntity = this.configuration?.targetEntity ?? this.targetEntity ?? '';
		if (!linkId || !sourceEntity || !targetEntity) {
			return;
		}

		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.removeItem(this.buildSampleInputStorageKey(linkId, sourceEntity, targetEntity));
		} catch {
			// Ignore storage removal failures.
		}
	}

	private persistSavedBaselineTestRun(): void {
		const baseline = this.savedBaselineTestRun;
		if (!baseline) {
			return;
		}

		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.setItem(
				this.buildSavedBaselineStorageKey(baseline.linkId, baseline.sourceEntity, baseline.targetEntity),
				JSON.stringify(baseline)
			);
		} catch {
			// Ignore storage write failures and keep the in-memory baseline.
		}
	}

	private readSavedBaselineTestRun(linkId: number, sourceEntity: string, targetEntity: string): SavedMapperTestRun | null {
		try {
			if (typeof localStorage === 'undefined') {
				return null;
			}

			const raw = localStorage.getItem(this.buildSavedBaselineStorageKey(linkId, sourceEntity, targetEntity));
			if (!raw) {
				return null;
			}

			return JSON.parse(raw) as SavedMapperTestRun;
		} catch {
			return null;
		}
	}

	private removeSavedBaselineTestRun(): void {
		const linkId = this.configuration?.linkId ?? this.linkId ?? 0;
		const sourceEntity = this.configuration?.sourceEntity ?? this.sourceEntity ?? '';
		const targetEntity = this.configuration?.targetEntity ?? this.targetEntity ?? '';
		if (!linkId || !sourceEntity || !targetEntity) {
			return;
		}

		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.removeItem(this.buildSavedBaselineStorageKey(linkId, sourceEntity, targetEntity));
		} catch {
			// Ignore storage removal failures.
		}
	}

	private readArtifactCaptureSessionPreference(): boolean {
		try {
			if (typeof localStorage !== 'undefined') {
				const value = localStorage.getItem(LinkGenericMapperComponent.artifactCaptureStorageKey);
				if (value !== null) {
					return value === 'true';
				}
			}

			if (typeof sessionStorage !== 'undefined') {
				return sessionStorage.getItem(LinkGenericMapperComponent.legacyArtifactCaptureSessionStorageKey) === 'true';
			}

			return false;
		} catch {
			return false;
		}
	}

	private writeArtifactCaptureSessionPreference(enabled: boolean): void {
		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.setItem(LinkGenericMapperComponent.artifactCaptureStorageKey, String(enabled));
		} catch {
			// Ignore storage write failures and keep the in-memory preference.
		}
	}

	private readShowUnmappedArtifactFieldsPreference(): boolean {
		try {
			if (typeof localStorage === 'undefined') {
				return true;
			}

			const value = localStorage.getItem(LinkGenericMapperComponent.showUnmappedArtifactFieldsStorageKey);
			return value === null ? true : value === 'true';
		} catch {
			return true;
		}
	}

	private writeShowUnmappedArtifactFieldsPreference(enabled: boolean): void {
		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			localStorage.setItem(LinkGenericMapperComponent.showUnmappedArtifactFieldsStorageKey, String(enabled));
		} catch {
			// Ignore storage write failures and keep the in-memory preference.
		}
	}

  private readMetadataCachingPreference(): boolean {
    try {
      if (typeof localStorage === 'undefined') {
        return false;
      }

      const value = localStorage.getItem(LinkGenericMapperComponent.metadataCachingStorageKey);
      return value === 'true';
    } catch {
      return false;
    }
  }

  private writeMetadataCachingPreference(enabled: boolean): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }

      localStorage.setItem(LinkGenericMapperComponent.metadataCachingStorageKey, String(enabled));
    } catch {
      // Ignore storage write failures and keep the in-memory preference.
    }
  }

	onMappingModeChanged(row: MappingGridRow): void {
		row.previewValue = undefined;
		row.isValid = undefined;
		row.validationMessage = null;
		if (row.matchOnlyLinkKey) {
			row.isLinkKey = row.mode !== 'Ignore';
		}

    if (row.mode === 'Ignore') {
      row.isLinkKey = false;
      row.sourceFieldPath = null;
      row.value = null;
      row.lookup = null;
			row.picklistMapping = null;
		} else if (row.mode === 'SourceField') {
			row.value = null;
			row.lookup = null;
			row.picklistMapping = null;
		} else if (row.mode === 'Constant' || row.mode === 'Expression') {
      row.sourceFieldPath = null;
      row.lookup = null;
			row.picklistMapping = null;
      row.value ??= '';
    } else if (row.mode === 'Lookup') {
      row.sourceFieldPath = null;
      row.value = null;
			row.picklistMapping = null;

      row.lookup = row.lookup ?? {
        referenceEntityName:
          row.referenceEntityName ||
          row.reference?.targetEntity ||
          '',
        returnFieldPath:
          row.reference?.defaultReturnFieldPath || 'ID',
        matchMode: 'All',
        criteria: []
      };

      if (!row.lookup.criteria.length) {
        row.lookup.criteria.push({
          targetFieldPath: '',
          mode: 'SourceField',
          sourceFieldPath: null,
          value: null
        });
      }
    }

		if (row.isLinkKey) {
			this.synchronizeSharedLinkKeyFromRow(row);
		} else {
			this.enforceSingleGlobalLinkKey();
		}

		if (!this.isRowUnmapped(row)) {
			this.requestPreview(row);
		}
		this.applyRowFilters();
	}

  onMappingRowSearchChanged(value: string): void {
    this.mappingRowSearch = value ?? '';
		this.filterMappingRows();
  }

  onShowOnlyRequiredChanged(value: boolean): void {
    this.showOnlyRequired = !!value;
		this.filterMappingRows();
  }

  onShowOnlyUnmappedChanged(value: boolean): void {
    this.showOnlyUnmapped = !!value;
		this.filterMappingRows();
  }

  onShowOnlyIssuesChanged(value: boolean): void {
    this.showOnlyIssues = !!value;
		this.filterMappingRows();
  }

  isRowUnmapped(row: MappingGridRow): boolean {
    if (this.isChildMappingRow(row)) {
      return (row.childMappingCount ?? 0) === 0;
    }

	if (row.readOnly && !row.matchOnlyLinkKey) {
	  return false;
	}

    switch (row.mode) {
      case 'Ignore':
        return row.required;
      case 'SourceField':
        return !row.sourceFieldPath;
      case 'Constant':
      case 'Expression':
        return !row.value?.trim();
      case 'Lookup':
        return !row.lookup?.criteria?.length || !row.lookup.returnFieldPath;
      default:
        return true;
    }
  }

  private syncRowLinkKeyValidity(row: MappingGridRow): void {
    if (!row.isLinkKey) {
      return;
    }

		if ((row.readOnly && !row.matchOnlyLinkKey) || !this.canUseAsSharedLinkKeySource(row) || this.isRowUnmapped(row)) {
			row.isLinkKey = false;
		}
  }

  getRowState(row: MappingGridRow): 'issue' | 'ready' | 'ignored' | 'readonly' | 'pending' | 'loading' {
    if (row.isResolving) {
      return 'loading';
    }

    if (this.isChildMappingRow(row)) {
      return (row.childMappingCount ?? 0) > 0 ? 'ready' : 'pending';
    }

	if (row.readOnly && (!row.matchOnlyLinkKey || row.mode === 'Ignore')) {
	  return 'readonly';
	}

    if (this.isRowValidationIssue(row)) {
      return 'issue';
    }

    if (row.mode === 'Ignore') {
      return row.required ? 'pending' : 'ignored';
    }

    if (this.isRowUnmapped(row)) {
      return 'pending';
    }

    if (row.isValid === true) {
      return 'ready';
    }

    return 'pending';
  }

	private isRowValidationIssue(row: MappingGridRow): boolean {
		if (row.isValid === false) {
			return true;
		}

		if (row.isResolving || row.isValid === true) {
			return false;
		}

		return this.isConversionValidationMessage(row.validationMessage);
	}

	private isConversionValidationMessage(message: string | null | undefined): boolean {
		return /could not be converted to/i.test(message ?? '');
	}

  getModeDescription(row: MappingGridRow): string {
    const scope =
      row.canCreate && row.canUpdate
        ? 'Available for create and update.'
        : row.canCreate
          ? 'Available for create only.'
          : row.canUpdate
            ? 'Available for update only.'
            : 'Not writable.';

    switch (row.mode) {
      case 'Ignore':
        return row.required
          ? `This required field still needs a value. ${scope}`
          : `This field will not be sent to the target system. ${scope}`;
      case 'SourceField':
        return `Copy a value directly from one source field. ${scope}`;
      case 'Constant':
        return `Always send the same fixed value. ${scope}`;
      case 'Expression':
        return `Build a value using fields, functions, and logic. ${scope}`;
      case 'Lookup':
        return `Find a related record in another entity and return one field from it. ${scope}`;
      default:
        return scope;
    }
  }

  openExpressionEditor(row: MappingGridRow): void {
    this.guideExpressionEditingRow = null;
		this.expressionEditorUsesGuideFields = false;
    this.expressionEditingTargetFieldId = row.targetFieldId;
    this.expressionFieldSearch = '';
		this.rebuildExpressionSourceTreeItems();
		this.resetExpressionTestResult();
    this.expressionTipsExpanded = false;
    this.expressionDraft = this.normalizeExpressionForEditor(row.value);
    this.selectedExpressionSourceField = null;
    this.expressionPopupVisible = true;
    this.closeExpressionSuggestions();
  }

  closeExpressionEditor(): void {
    this.closeExpressionSuggestions();
    this.expressionEditingTargetFieldId = null;
    this.lookupExpressionCriterionIndex = null;
    this.guideExpressionEditingRow = null;
		this.expressionEditorUsesGuideFields = false;
    this.writebackExpressionEditIndex = null;
    this.writebackExpressionEditTarget = null;
    this.expressionPopupVisible = false;
    this.expressionDraft = '';
    this.expressionFieldSearch = '';
		this.expressionTargetFieldSearch = '';
		this.expressionSourceTreeItems = [];
		this.expressionTargetTreeItems = [];
		this.resetExpressionTestResult();
    this.expressionTipsExpanded = false;
    this.selectedExpressionSourceField = null;
    this.expressionTextAreaElement = null;
  }

  applyExpression(): void {
    const draft = this.expressionDraft?.trim() ?? '';

    if (this.writebackExpressionEditIndex !== null && this.writebackExpressionEditTarget) {
      const writeback = this.writebacks[this.writebackExpressionEditIndex];
      if (!writeback) {
        return;
      }

      if (this.writebackExpressionEditTarget === 'condition') {
        writeback.conditionExpression = draft || null;
      } else {
        writeback.mode = 'Expression';
        writeback.expression = draft;
        writeback.targetFieldPath = null;
        writeback.dateFormat = null;
      }

			this.refreshSaveMappingReviewState();
      this.closeExpressionEditor();
      return;
    }

    if (
		this.lookupEditingDraftRow &&
      this.lookupExpressionCriterionIndex !== null &&
		this.lookupEditingDraftRow.lookup?.criteria?.[this.lookupExpressionCriterionIndex]
    ) {
		const criterion = this.lookupEditingDraftRow.lookup.criteria[this.lookupExpressionCriterionIndex];
      criterion.mode = 'Expression';
      criterion.value = draft;
		this.resetLookupTestState();
      this.closeExpressionEditor();
      return;
    }

		if (this.guideExpressionEditingRow) {
			const row = this.guideExpressionEditingRow;
			row.mode = 'Expression';
			row.value = draft;
			this.closeExpressionEditor();
			this.onMapperGuidePreviewRequested(row);
			return;
		}

    if (!this.expressionEditingTargetFieldId) {
      return;
    }

    const row = this.mappingRows.find(x => x.targetFieldId === this.expressionEditingTargetFieldId);
    if (!row) {
      return;
    }

		row.mode = 'Expression';
		row.value = draft;
		if (row.isLinkKey) {
			this.synchronizeSharedLinkKeyFromRow(row);
		}
		this.closeExpressionEditor();
		this.requestPreview(row);
	}

	onExpressionDraftChanged(value: string): void {
		this.expressionDraft = value ?? '';
		this.resetExpressionTestResult();
	}

  insertFieldIntoExpression(field: FieldOption): void {
    this.applyExpressionInsertion(`[${field.path}]`, true);
  }

  insertExpressionTool(tool: ExpressionTool): void {
    this.applyExpressionInsertion(tool.template, false);
  }

  applyExpressionSuggestion(item: ExpressionSuggestionItem): void {
    if (item.kind === 'field' && item.field) {
      this.selectedExpressionSourceField = item.field;
      this.applyExpressionInsertion(item.insertText, true);
      return;
    }

		if (item.kind === 'picklist-option' && item.picklistOption) {
			this.applyExpressionInsertion(item.insertText, false);
			return;
		}

    this.applyExpressionInsertion(item.insertText, false);
  }

	insertExpressionPicklistValue(option: { value: string }): void {
		this.applyExpressionInsertion(this.formatExpressionLiteral(option.value), false);
	}

	insertExpressionPicklistComparison(option: { value: string }): void {
		if (!this.selectedExpressionSourceField?.path) {
			return;
		}

		this.applyExpressionInsertion(`[${this.selectedExpressionSourceField.path}] == ${this.formatExpressionLiteral(option.value)}`, false);
	}

  onSourceTreeItemClick(e: any): void {
    const item = e?.itemData as TreeNode | undefined;
    if (!item || !item.isMappable || !item.path) {
      return;
    }

    if (this.expressionPopupVisible) {
      const match = this.sourceFieldOptions.find(x => x.path === item.path);
      if (match) {
        this.insertFieldIntoExpression(match);
      }
    }
  }

  onExpressionEditorInitialized(e: any): void {
    this.expressionTextAreaElement = e?.element?.querySelector('textarea') ?? null;
  }

  onExpressionClick(): void {
    this.captureExpressionSelection();
    if (this.expressionSuggestionsVisible) {
      this.updateExpressionSuggestions();
    }
  }

  onExpressionInput(): void {
    const textarea = this.resolveExpressionTextArea();
    if (textarea) {
			const nextValue = textarea.value ?? '';
			if (nextValue !== this.expressionDraft) {
				this.expressionDraft = nextValue;
				this.resetExpressionTestResult();
			}
    }

    this.captureExpressionSelection();

    if (this.expressionSuggestionsVisible) {
      this.updateExpressionSuggestions();
    }
  }

  onExpressionDxKeyDown(e: any): void {
    const domEvent = e?.event as KeyboardEvent | undefined;
    if (!domEvent) {
      return;
    }

    this.onExpressionKeyDown(domEvent);
  }

	private buildMappingDefinition(previewRow: MappingGridRow | null = null, previewTab: RuleTab = this.currentRuleTab): MappingDefinition {
		if (!this.configuration || !this.rootMappingNode) {
			throw new Error('Configuration not loaded.');
		}

		const rootNode = this.rootMappingNode;
    const previewRows = this.selectedMappingNode?.id === rootNode.id
      ? this.getRowsForPreview(previewRow, previewTab)
      : null;
    const createRows = previewTab === 'create' && previewRows ? previewRows : rootNode.createMappingRows;
    const updateRows = previewTab === 'update' && previewRows ? previewRows : rootNode.updateMappingRows;
    const inlineCreateRules = this.buildInlineChildRules(rootNode, 'create');
    const inlineUpdateRules = this.buildInlineChildRules(rootNode, 'update');

    return {
      linkId: this.configuration.linkId,
			fromLinkTemplateId: this.configuration.fromLinkTemplateId ?? null,
			toLinkTemplateId: this.configuration.toLinkTemplateId ?? null,
      sourceSchemaId: rootNode.sourceSchema.id,
      targetSchemaId: rootNode.targetSchema.id,
      sourceEntity: rootNode.sourceEntity,
      targetEntity: rootNode.targetEntity,
		sourceIdPath: this.resolveSourceIdPath(rootNode.sourceSchema, rootNode.sourceIdPath),
      name: rootNode.name,
      activeRuleSet: previewTab,
      createRules: this.extractRulesFromRows(createRows).concat(inlineCreateRules),
      updateRules: this.extractRulesFromRows(updateRows).concat(inlineUpdateRules),
		createWritebacks: this.serializeWritebacks(this.createWritebacks),
		updateWritebacks: this.serializeWritebacks(this.updateWritebacks),
      runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates({ runtimeMessageTemplates: rootNode.runtimeMessageTemplates }),
      rules: this.extractRulesFromRows(createRows).concat(inlineCreateRules),
		children: rootNode.children.map(child => this.buildChildMappingDefinition(child))
	};
  }

	private serializeWritebacks(writebacks: MappingWriteback[]): MappingWriteback[] {
		return writebacks
			.filter(writeback => !!writeback.sourceFieldPath)
			.map(writeback => ({
				sourceFieldPath: writeback.sourceFieldPath,
				mode: writeback.mode,
				targetFieldPath: writeback.mode === 'TargetValue' ? (writeback.targetFieldPath ?? null) : null,
				dateFormat: writeback.mode === 'CurrentDate' ? (writeback.dateFormat ?? 'yyyy-MM-dd') : null,
				expression: writeback.mode === 'Expression' ? (writeback.expression ?? '') : null,
				conditionExpression: writeback.conditionExpression?.trim() || null
			}));
	}

	private cloneWritebacks(writebacks: MappingWriteback[]): MappingWriteback[] {
		return writebacks.map(writeback => ({
			sourceFieldPath: writeback.sourceFieldPath,
			mode: writeback.mode,
			targetFieldPath: writeback.targetFieldPath ?? null,
			dateFormat: writeback.dateFormat ?? 'yyyy-MM-dd',
			expression: writeback.expression ?? null,
			conditionExpression: writeback.conditionExpression ?? null
		}));
	}

  private getRowsForPreview(previewRow: MappingGridRow | null, tab: 'create' | 'update'): MappingGridRow[] {
    const rows = this.getRowsForTab(tab);

    if (!previewRow) {
      return rows;
    }

    const rowIndex = rows.findIndex(
      row => row.targetFieldId === previewRow.targetFieldId
    );

    if (rowIndex < 0) {
      return rows;
    }

    return rows.slice(0, rowIndex + 1);
  }

  private async applySavedMappingDefinition(savedMapping: MappingDefinition | null): Promise<void> {
    if (!savedMapping || !this.rootMappingNode) {
      return;
    }

		await this.hydrateSavedSourcePaths(this.rootMappingNode, savedMapping);

		const hasSplitWritebacks = savedMapping.createWritebacks != null || savedMapping.updateWritebacks != null;
		const legacyWritebacks = savedMapping.writebacks ?? [];
		this.createWritebacks = this.cloneWritebacks(hasSplitWritebacks ? savedMapping.createWritebacks ?? [] : legacyWritebacks);
		this.updateWritebacks = this.cloneWritebacks(hasSplitWritebacks ? savedMapping.updateWritebacks ?? [] : legacyWritebacks);
		this.runtimeMessageTemplates = this.normalizeRuntimeMessageTemplates(savedMapping);

		this.writebackSourceTreeItemsByIndex = {};
		this.writebackTargetTreeItemsByIndex = {};
		this.writebackSourceFieldSearchByIndex = {};
		this.writebackTargetFieldSearchByIndex = {};
		this.writebackSourceDropdownOpenedByIndex = {};
		this.writebackTargetDropdownOpenedByIndex = {};
		this.writebacks.forEach((writeback, index) => {
			this.rebuildWritebackSourceTree(index, writeback);
			this.rebuildWritebackTargetTree(index, writeback);
		});

    const createRules = savedMapping.createRules?.length
      ? savedMapping.createRules
      : (savedMapping.rules ?? []);
    const updateRules = savedMapping.updateRules?.length
      ? savedMapping.updateRules
      : (savedMapping.rules ?? []);

		this.rootMappingNode.name = savedMapping.name?.trim() || this.rootMappingNode.name;
		this.rootMappingNode.sourceIdPath = this.resolveSourceIdPath(
			this.rootMappingNode.sourceSchema,
			savedMapping.sourceIdPath
		);
		this.rebuildSourceIdPathTree();
    this.applyRulesToRows(this.rootMappingNode.createMappingRows, createRules);
    this.applyRulesToRows(this.rootMappingNode.updateMappingRows, updateRules);
		this.rootMappingNode.children = await Promise.all(
			(savedMapping.children ?? []).map(child => this.createEditorNodeFromSavedDefinition(child, this.rootMappingNode!.id))
		);
		this.applyInlineChildRules(this.rootMappingNode, createRules, 'create');
		this.applyInlineChildRules(this.rootMappingNode, updateRules, 'update');
		this.syncChildMappingCounts(this.rootMappingNode);
		this.enforceSingleGlobalLinkKey();
		this.applyFiltersToNodeTree(this.rootMappingNode);
		this.rebuildArtifactTraceItems();
  }

	private isRowMapped(row: MappingGridRow): boolean {
		if (this.isChildMappingRow(row)) {
			return (row.childMappingCount ?? 0) > 0;
		}

		if ((row.readOnly && !row.matchOnlyLinkKey) || row.mode === 'Ignore') {
			return false;
		}

		return !this.isRowUnmapped(row);
	}

  private buildChildMappingDefinition(node: MapperEditorNode): ChildMappingDefinition {
    return {
      id: node.mappingId ?? null,
      ownerTargetFieldId: node.ownerTargetFieldId ?? null,
      ownerTargetPath: node.ownerTargetPath ?? null,
      inlineChildObject: node.inlineChildObject === true,
      name: node.name,
      sourceCollectionPath: node.sourceCollectionPath,
      targetRuntimeRoute: node.targetRuntimeRoute,
      sourceSchemaId: node.sourceSchema.id,
      targetSchemaId: node.targetSchema.id,
      sourceEntity: node.sourceEntity,
      targetEntity: node.targetEntity,
      activeRuleSet: this.selectedMappingNode?.id === node.id ? this.currentRuleTab : null,
      executionMode: node.executionMode,
      continueOnError: node.continueOnError,
      createRules: this.extractRulesFromRows(node.createMappingRows),
      updateRules: this.extractRulesFromRows(node.updateMappingRows),
      rules: this.extractRulesFromRows(node.createMappingRows),
      contextBindings: (node.contextBindings ?? []).map(binding => ({
        targetPath: binding.targetPath,
        mode: binding.mode,
        sourcePath: binding.sourcePath ?? null
      })),
      runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates({ runtimeMessageTemplates: node.runtimeMessageTemplates }),
      children: node.children.map(child => this.buildChildMappingDefinition(child))
    };
  }

  private async createEditorNodeFromSavedDefinition(savedChild: ChildMappingDefinition, parentId: string): Promise<MapperEditorNode> {
    const linkId = this.linkId ?? this.configuration?.linkId ?? 0;
    const isInlineChildObject = savedChild.inlineChildObject === true;
    const [sourceSchema, targetSchema] = isInlineChildObject
      ? [
          this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema!,
          this.buildInlineChildTargetSchema(
            this.configuration?.targetSchema!,
            savedChild.ownerTargetPath ?? '',
            savedChild.name?.trim() || savedChild.ownerTargetPath || savedChild.targetEntity
          )
        ]
      : await Promise.all([
          this.resolveSavedNodeSourceSchema(savedChild, linkId),
          this.resolveSavedNodeTargetSchema(savedChild, linkId)
        ]);
    const node = this.createEditorNode({
      kind: 'child',
      mappingId: savedChild.id ?? null,
      parentId,
      ownerTargetFieldId: savedChild.ownerTargetFieldId ?? null,
      ownerTargetPath: savedChild.ownerTargetPath ?? savedChild.sourceCollectionPath ?? null,
      inlineChildObject: isInlineChildObject,
      name: savedChild.name?.trim() || `${savedChild.sourceEntity} -> ${savedChild.targetEntity}`,
      sourceEntity: savedChild.sourceEntity,
      targetEntity: savedChild.targetEntity,
      sourceIdPath: 'id',
      sourceSchema,
      targetSchema,
      sourceCollectionPath: savedChild.sourceCollectionPath ?? '',
			targetRuntimeRoute: savedChild.targetRuntimeRoute?.trim() || this.getDefaultTargetRuntimeRoute(savedChild.targetEntity),
      contextBindings: savedChild.contextBindings ?? [],
      continueOnError: savedChild.continueOnError !== false,
      executionMode: savedChild.executionMode?.trim() || 'AfterParentUpsert',
      runtimeMessageTemplates: savedChild.runtimeMessageTemplates ?? null
    });

    const createRules = savedChild.createRules?.length
      ? savedChild.createRules
      : (savedChild.rules ?? []);
    const updateRules = savedChild.updateRules?.length
      ? savedChild.updateRules
      : (savedChild.rules ?? []);

    this.applyRulesToRows(node.createMappingRows, createRules);
    this.applyRulesToRows(node.updateMappingRows, updateRules);
    node.children = await Promise.all((savedChild.children ?? []).map(child => this.createEditorNodeFromSavedDefinition(child, node.id)));
    this.syncChildMappingCounts(node);
    this.applyRowFiltersToNode(node);
    return node;
  }

  private async resolveSavedNodeSourceSchema(savedChild: ChildMappingDefinition, linkId: number): Promise<MappingEntitySchema> {
    const rootSourceSchema = this.configuration?.effectiveSourceSchema ?? this.configuration?.sourceSchema;
    if (!rootSourceSchema) {
      throw new Error('Source schema not loaded.');
    }

    if (savedChild.sourceSchemaId === rootSourceSchema.id || savedChild.sourceEntity === rootSourceSchema.entityName) {
      return rootSourceSchema;
    }

    return await this.loadEntitySchemaAsync(linkId, savedChild.sourceEntity, 'source');
  }

  private async resolveSavedNodeTargetSchema(savedChild: ChildMappingDefinition, linkId: number): Promise<MappingEntitySchema> {
    const rootTargetSchema = this.configuration?.targetSchema;
    if (!rootTargetSchema) {
      throw new Error('Target schema not loaded.');
    }

    if (savedChild.targetSchemaId === rootTargetSchema.id || savedChild.targetEntity === rootTargetSchema.entityName) {
      return rootTargetSchema;
    }

    return await this.loadEntitySchemaAsync(linkId, savedChild.targetEntity, 'target');
  }

  private applyFiltersToNodeTree(node: MapperEditorNode): void {
    this.applyRowFiltersToNode(node);
    node.children.forEach(child => this.applyFiltersToNodeTree(child));
  }

	private getDefaultTargetRuntimeRoute(entityName: string | null | undefined): string {
		return this.getTargetRuntimeRouteOptions(entityName)[0] ?? '';
	}

	get selectedNodeTargetRuntimeRouteOptions(): string[] {
		const node = this.selectedMappingNode;
		if (!node || node.kind === 'root' || this.isInlineChildObjectNode(node)) {
			return [];
		}

		return this.getTargetRuntimeRouteOptions(node.targetEntity);
	}

	private getTargetRuntimeRouteOptions(entityName: string | null | undefined): string[] {
		const normalizedEntityName = (entityName ?? '').trim();
		if (!normalizedEntityName) {
			return [];
		}

		const descriptor = this.availableTargetEntities.find(entity =>
			entity.entityName.localeCompare(normalizedEntityName, undefined, { sensitivity: 'accent' }) === 0
		);

		const routes = [
			descriptor?.runtime?.upsertRoute?.trim()
		].filter((route): route is string => !!route);

		return Array.from(new Set(routes));
	}

  private rebuildRootNodeFromConfiguration(): void {
    if (!this.configuration) {
      return;
    }

    const effectiveSourceSchema = this.configuration.effectiveSourceSchema ?? this.configuration.sourceSchema;
    const previousSelection = this.selectedMappingNodeId;
    const previousRoot = this.rootMappingNode;

    this.rootMappingNode = this.createEditorNode({
      kind: 'root',
      mappingId: previousRoot?.mappingId ?? null,
      parentId: null,
      name: previousRoot?.name?.trim() || this.configuration.mappingName || `${this.configuration.sourceEntity} -> ${this.configuration.targetEntity}`,
      sourceEntity: this.configuration.sourceEntity,
      targetEntity: this.configuration.targetEntity,
	  sourceIdPath: this.resolveSourceIdPath(effectiveSourceSchema, previousRoot?.sourceIdPath),
      sourceSchema: effectiveSourceSchema,
      targetSchema: this.configuration.targetSchema,
      sourceCollectionPath: '',
      targetRuntimeRoute: '',
      contextBindings: [],
      continueOnError: true,
      executionMode: 'AfterParentUpsert',
      runtimeMessageTemplates: previousRoot?.runtimeMessageTemplates ?? this.normalizeRuntimeMessageTemplates(this.configuration as { runtimeMessageTemplates?: MapperRuntimeMessageTemplates | null; })
    });

    this.rebuildSourceIdPathTree();

    this.selectedMappingNodeId = previousSelection && previousRoot?.id === previousSelection
      ? this.rootMappingNode.id
      : (this.selectedMappingNodeId || this.rootMappingNode.id);
  }

	private resetMapperDraftFromConfiguration(): void {
		if (!this.configuration) {
			return;
		}

		const effectiveSourceSchema = this.configuration.effectiveSourceSchema ?? this.configuration.sourceSchema;
		this.rootMappingNode = this.createEditorNode({
			kind: 'root',
			mappingId: null,
			parentId: null,
			name: this.configuration.mappingName || `${this.configuration.sourceEntity} -> ${this.configuration.targetEntity}`,
			sourceEntity: this.configuration.sourceEntity,
			targetEntity: this.configuration.targetEntity,
			sourceIdPath: this.resolveSourceIdPath(effectiveSourceSchema),
			sourceSchema: effectiveSourceSchema,
			targetSchema: this.configuration.targetSchema,
			sourceCollectionPath: '',
			targetRuntimeRoute: '',
			contextBindings: [],
			continueOnError: true,
			executionMode: 'AfterParentUpsert',
			runtimeMessageTemplates: this.normalizeRuntimeMessageTemplates(this.configuration as { runtimeMessageTemplates?: MapperRuntimeMessageTemplates | null; })
		});

		this.selectedMappingNodeId = this.rootMappingNode.id;
		this.createWritebacks = [];
		this.updateWritebacks = [];
		this.activeWritebackRuleSet = 'create';
		this.writebackSourceTreeItemsByIndex = {};
		this.writebackTargetTreeItemsByIndex = {};
		this.writebackSourceFieldSearchByIndex = {};
		this.writebackTargetFieldSearchByIndex = {};
		this.writebackSourceDropdownOpenedByIndex = {};
		this.writebackTargetDropdownOpenedByIndex = {};
		this.rebuildSourceIdPathTree();
		this.refreshRuntimeMessagePropertyExamples();
		this.applyRowFilters();
		this.refreshSelectedRow();
		this.savedProfileRowSignatures = this.captureCurrentRowSignatures();
	}

  private applyRowFiltersToNode(node: MapperEditorNode): void {
    const previousSelectedNodeId = this.selectedMappingNodeId;
    this.selectedMappingNodeId = node.id;
    this.applyRowFilters();
    this.selectedMappingNodeId = previousSelectedNodeId;
		this.refreshMappingStats();
		this.refreshSaveMappingReviewState();
  }

  private extractRulesFromRows(rows: MappingGridRow[]): MappingRule[] {
    return rows.map(row => ({
      targetFieldId: row.targetFieldId,
      targetPath: row.targetPath,
      mode: row.mode,
      isLinkKey: !!row.isLinkKey,
      sourceFieldPath: row.mode === 'SourceField' ? (row.sourceFieldPath ?? null) : null,
      value: row.mode === 'Expression'
        ? this.normalizeExpressionForBackend(row.value)
        : (row.mode === 'Constant' ? (row.value ?? null) : null),
      lookup: row.mode === 'Lookup' && row.lookup
        ? {
          referenceEntityName: row.lookup.referenceEntityName,
			returnFieldPath: row.lookup.returnFieldPath,
          matchMode: row.lookup.matchMode,
          criteria: row.lookup.criteria.map(criterion => ({
            targetFieldPath: criterion.targetFieldPath,
            mode: criterion.mode,
            sourceFieldPath: criterion.mode === 'SourceField'
              ? (criterion.sourceFieldPath ?? null)
              : null,
            value: criterion.mode === 'Expression'
              ? this.normalizeExpressionForBackend(criterion.value)
              : (criterion.mode === 'Constant' ? (criterion.value ?? null) : null)
          }))
        }
        : null,
      valueMap: this.buildValueMap(row),
			normalization: this.buildNormalizationConfiguration(row)
    }));
  }

  private applyRulesToRows(rows: MappingGridRow[], rules: MappingRule[]): void {
    if (!rules?.length) {
      return;
    }

    const rowsByTargetFieldId = new Map(rows.map(row => [row.targetFieldId, row]));

    for (const rule of rules) {
      const row = rowsByTargetFieldId.get(rule.targetFieldId);
		if (!row || (row.readOnly && !row.matchOnlyLinkKey)) {
			continue;
		}

      row.mode = rule.mode;
		row.isLinkKey = row.matchOnlyLinkKey && rule.mode !== 'Ignore'
			? true
			: !!rule.isLinkKey;
      row.sourceFieldPath = rule.sourceFieldPath ?? null;
      row.value = rule.mode === 'Expression'
        ? this.normalizeExpressionForEditor(rule.value)
        : (rule.value ?? null);
      row.lookup = rule.lookup
        ? {
          referenceEntityName: rule.lookup.referenceEntityName,
					returnFieldPath: rule.lookup.returnFieldPath || row.reference?.defaultReturnFieldPath || 'ID',
          matchMode: rule.lookup.matchMode,
          criteria: (rule.lookup.criteria ?? []).map(criterion => ({
            targetFieldPath: criterion.targetFieldPath,
            mode: criterion.mode,
            sourceFieldPath: criterion.sourceFieldPath ?? null,
            value: criterion.mode === 'Expression'
              ? this.normalizeExpressionForEditor(criterion.value)
              : (criterion.value ?? null)
          }))
        }
        : null;

      row.picklistMapping = this.buildPicklistMappingFromValueMap(row, rule.valueMap ?? null);
			row.normalization = this.cloneNormalizationConfiguration(rule.normalization ?? null);
      row.previewValue = undefined;
      row.isValid = undefined;
      row.validationMessage = null;
      this.syncRowLinkKeyValidity(row);
    }
  }

	onTargetSchemaEnrichmentInputChanged(key: string, value: string): void {
		this.targetSchemaEnrichmentInputValues = {
			...this.targetSchemaEnrichmentInputValues,
			[key]: value ?? ''
		};
	}

	get targetSchemaEnrichmentConfig() {
		return this.configuration?.targetSchema?.targetEnrichment ?? null;
	}

	get hasTargetSchemaEnrichment(): boolean {
		return this.selectedMappingNode?.kind === 'root' && this.targetSchemaEnrichmentConfig?.enabled === true;
	}

	refreshTargetSchemaEnrichment(): void {
		const configuration = this.configuration;
		const node = this.selectedMappingNode;
		const linkId = this.linkId ?? 0;
		if (!configuration || !node || node.kind !== 'root' || linkId <= 0) {
			return;
		}

		if (this.hasMissingRequiredInputValues(this.targetSchemaEnrichmentInputDefinitions, this.targetSchemaEnrichmentInputValues)) {
			this.lastActionMessage = 'Required target enrichment input values are missing.';
			return;
		}

		const currentMapping = this.buildMappingDefinition();
		const targetSchemaEnrichmentInputs = this.buildInputRequest(this.targetSchemaEnrichmentInputDefinitions, this.targetSchemaEnrichmentInputValues);
		const hasTargetSchemaEnrichmentInput = this.targetSchemaEnrichmentInputDefinitions.some(definition =>
			!!targetSchemaEnrichmentInputs[definition.key]
		);
		this.isTargetSchemaEnrichmentLoading = true;

			this.api.getEntitySchema(
				linkId,
				configuration.targetEntity,
				'target',
				targetSchemaEnrichmentInputs
			).subscribe({
				next: targetSchema => {
				if (!this.configuration) {
					this.isTargetSchemaEnrichmentLoading = false;
					return;
				}

				this.configuration = {
					...this.configuration,
					targetSchema
				};
				this.targetSchemaEnrichmentInputValues = this.buildInputValues(this.getTargetSchemaEnrichmentInputDefinitions(targetSchema.targetEnrichment), this.targetSchemaEnrichmentInputValues);
				this.cacheCurrentConfiguration();
				this.rebuildRootNodeFromConfiguration();
				void this.applySavedMappingDefinition(currentMapping).then(() => {
					this.enforceSingleGlobalLinkKey();
					this.applyRowFilters();
					this.refreshSelectedRow();
				});
				this.lastActionMessage = hasTargetSchemaEnrichmentInput
					? (targetSchema.targetEnrichment?.successMessageWithInput ?? 'Target model enriched.')
					: (targetSchema.targetEnrichment?.successMessageWithoutInput ?? 'Target model reloaded.');
				this.isTargetSchemaEnrichmentLoading = false;
			},
			error: error => {
				this.showMapperApiError(error, 'Failed to refresh target model enrichment.', 'Target model enrichment could not be refreshed');
				this.isTargetSchemaEnrichmentLoading = false;
			}
		});
	}

  private applyInlineChildRules(parentNode: MapperEditorNode, rules: MappingRule[] | null | undefined, tab: 'create' | 'update'): void {
    if (!rules?.length) {
      return;
    }

    for (const childNode of parentNode.children.filter(child => this.isInlineChildObjectNode(child) && !!child.ownerTargetPath)) {
      const childRules = rules.filter(rule => this.isInlineChildObjectRulePath(childNode, rule.targetPath));
      if (!childRules.length) {
        continue;
      }

      const normalizedRules = childRules.map(rule => ({
        ...rule,
        targetFieldId: this.removeInlineChildPrefix(childNode.ownerTargetPath!, rule.targetFieldId),
        targetPath: this.removeInlineChildPrefix(childNode.ownerTargetPath!, rule.targetPath)
      }));

      this.applyRulesToRows(this.getInlineChildNodeRows(childNode, tab), normalizedRules);
      this.applyInlineChildRules(childNode, normalizedRules, tab);
    }
  }

	private getInlineChildNodeRows(node: MapperEditorNode, tab: 'create' | 'update'): MappingGridRow[] {
		const rows = tab === 'update' ? node.updateMappingRows : node.createMappingRows;
		if (!this.isInlineChildObjectNode(node)) {
			return rows;
		}

		return rows;
	}

  private buildInlineChildRules(parentNode: MapperEditorNode, tab: 'create' | 'update'): MappingRule[] {
    const result: MappingRule[] = [];

    parentNode.children
      .filter(child => this.isInlineChildObjectNode(child) && !!child.ownerTargetPath)
      .forEach(child => {
        const ownRules = this.extractRulesFromRows(this.getInlineChildNodeRows(child, tab)).map(rule => ({
          ...rule,
          targetFieldId: this.prefixInlineChildPath(child.ownerTargetPath!, rule.targetFieldId),
          targetPath: this.prefixInlineChildPath(child.ownerTargetPath!, rule.targetPath)
        }));
        const nestedRules = this.buildInlineChildRules(child, tab).map(rule => ({
          ...rule,
          targetFieldId: this.prefixInlineChildPath(child.ownerTargetPath!, rule.targetFieldId),
          targetPath: this.prefixInlineChildPath(child.ownerTargetPath!, rule.targetPath)
        }));

        result.push(...ownRules, ...nestedRules);
      });

    return result;
  }

  private prefixInlineChildPath(prefix: string, path: string | null | undefined): string {
    const normalizedPrefix = prefix?.trim();
    const normalizedPath = path?.trim();

    if (!normalizedPrefix) {
      return normalizedPath ?? '';
    }

    if (!normalizedPath) {
      return normalizedPrefix;
    }

    return normalizedPath.startsWith(normalizedPrefix + '.') || normalizedPath === normalizedPrefix
      ? normalizedPath
      : `${normalizedPrefix}.${normalizedPath}`;
  }

  private removeInlineChildPrefix(prefix: string, path: string | null | undefined): string {
    const normalizedPrefix = prefix?.trim();
    const normalizedPath = path?.trim();

    if (!normalizedPrefix || !normalizedPath) {
      return normalizedPath ?? '';
    }

    if (normalizedPath.startsWith(normalizedPrefix + '.')) {
      return normalizedPath.slice(normalizedPrefix.length + 1);
    }

    return normalizedPath;
  }

	private getRowConfigurationSignature(row: MappingGridRow): string {
		return JSON.stringify({
			mode: row.mode,
			isLinkKey: !!row.isLinkKey,
			sourceFieldPath: row.sourceFieldPath ?? null,
			value: row.value ?? null,
			lookup: row.lookup ?? null,
			picklistMapping: row.picklistMapping ?? null,
			normalization: this.buildNormalizationConfiguration(row)
		});
	}

	private isIgnoredRowConfigurationSignature(signature: string | null | undefined): boolean {
		if (!signature) {
			return false;
		}

		try {
			return JSON.parse(signature)?.mode === 'Ignore';
		} catch {
			return false;
		}
	}

	private formatRowConfigurationForReview(row: MappingGridRow): string {
		const parts: string[] = [row.mode || 'Ignore'];

		if (row.isLinkKey) {
			parts.push('link key');
		}

		switch (row.mode) {
			case 'SourceField':
				parts.push(row.sourceFieldPath || 'no source field');
				break;
			case 'Constant':
			case 'Expression':
				parts.push(row.value || 'empty value');
				break;
			case 'Lookup':
				parts.push(this.formatLookupConfigurationForReview(row));
				break;
		}

		if (row.picklistMapping?.items?.length) {
			parts.push(`${row.picklistMapping.items.length} picklist value${row.picklistMapping.items.length === 1 ? '' : 's'}`);
		}

		const normalization = this.buildNormalizationConfiguration(row);
		if (normalization) {
			parts.push('normalization');
		}

		return parts.join(' | ');
	}

	private formatLookupConfigurationForReview(row: MappingGridRow): string {
		const lookup = row.lookup;
		if (!lookup) {
			return 'lookup not configured';
		}

		const criteriaCount = lookup.criteria?.length ?? 0;
		return `${lookup.referenceEntityName || 'related entity'} -> ${lookup.returnFieldPath || 'return field'} (${criteriaCount} match rule${criteriaCount === 1 ? '' : 's'})`;
	}

	private async confirmSaveMappingChanges(profileName: string): Promise<boolean> {
		const reviewItems = this.buildSaveMappingReviewItems();

		if (!reviewItems.length) {
			return true;
		}

		const visibleItems = reviewItems.slice(0, 40);
		const rows = this.buildMappingReviewRows(visibleItems, 'save');
		const hiddenCount = Math.max(0, reviewItems.length - visibleItems.length);

		const confirmation = await Swal.fire({
			icon: 'question',
			title: `Save mapper profile '${profileName}'?`,
			width: 760,
				html: `
				<p>This will update ${reviewItems.length} mapper profile change${reviewItems.length === 1 ? '' : 's'}.</p>
				<div style="max-height: 460px; overflow: auto; text-align: left; padding-right: 4px;">
					${rows}
				</div>
				${hiddenCount > 0 ? `<p>${hiddenCount} more change${hiddenCount === 1 ? '' : 's'} not shown.</p>` : ''}
			`,
			showCancelButton: true,
			confirmButtonText: 'Save profile',
			cancelButtonText: 'Cancel'
		});

		return confirmation.isConfirmed;
	}

	private buildMappingReviewRows(reviewItems: SaveMappingReviewItem[], mode: 'save' | 'discard'): string {
		const isDiscard = mode === 'discard';
		return reviewItems.map(item => {
			const leftLabel = isDiscard ? 'Unsaved value' : 'Current saved';
			const leftValue = isDiscard ? item.after : item.before;
			const rightLabel = isDiscard ? 'Will restore' : 'New value';
			const rightValue = isDiscard ? item.before : item.after;
			const leftStyle = isDiscard
				? 'background: #fff6f6; border: 1px solid #f1caca;'
				: 'background: #f7f9fc;';

			return `
				<div style="border: 1px solid #dbe4ef; border-radius: 10px; padding: 12px; margin-bottom: 10px; background: #fff;">
					<div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 10px; color: #42526e; font-size: 12px;">
						<div><strong style="color: #172b4d;">${this.escapeHtml(item.field)}</strong></div>
						<div>${this.escapeHtml(item.mapping)} / ${this.escapeHtml(item.tab)}</div>
					</div>
					<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; text-align: left;">
						<div>
							<div style="font-size: 11px; font-weight: 700; color: #6b778c; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;">${leftLabel}</div>
							<div style="font-family: Consolas, 'Courier New', monospace; font-size: 12px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; ${leftStyle} border-radius: 6px; padding: 8px;">${this.escapeHtml(leftValue)}</div>
						</div>
						<div>
							<div style="font-size: 11px; font-weight: 700; color: #6b778c; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px;">${rightLabel}</div>
							<div style="font-family: Consolas, 'Courier New', monospace; font-size: 12px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; background: #f2fff7; border: 1px solid #c6f6d5; border-radius: 6px; padding: 8px;">${this.escapeHtml(rightValue)}</div>
						</div>
					</div>
				</div>`;
		}).join('');
	}

	private buildSaveMappingReviewItems(): SaveMappingReviewItem[] {
		const baseline = this.savedProfileRowSignatures ?? new Map<string, string>();
		const reviewItems: SaveMappingReviewItem[] = [];

		this.forEachMapperNode(node => {
			this.collectSaveMappingReviewItemsForRows(reviewItems, baseline, node, 'create', node.createMappingRows);
			this.collectSaveMappingReviewItemsForRows(reviewItems, baseline, node, 'update', node.updateMappingRows);
			this.collectSaveMappingReviewItemsForRuntimeMessages(reviewItems, baseline, node);
		});

		this.collectSaveMappingReviewItemsForWritebacks(reviewItems, baseline);
		this.collectSaveMappingReviewItemsForWritebackSet(reviewItems, baseline);

		return reviewItems;
	}

	private refreshSaveMappingReviewState(): void {
		const baseline = this.savedProfileRowSignatures ?? new Map<string, string>();
		let hasChanges = false;

		this.forEachMapperNode(node => {
			if (hasChanges) {
				return;
			}

			hasChanges = node.createMappingRows.some(row =>
				this.isRowConfigurationChanged(row, baseline.get(this.getRowReviewKey(node, 'create', row))))
				|| node.updateMappingRows.some(row =>
					this.isRowConfigurationChanged(row, baseline.get(this.getRowReviewKey(node, 'update', row))));
			if (hasChanges) {
				return;
			}

			const currentRuntimeMessageSignature = this.getRuntimeMessageConfigurationSignature(node.runtimeMessageTemplates);
			const savedRuntimeMessageSignature = baseline.get(this.getRuntimeMessageReviewKey(node));
			hasChanges = savedRuntimeMessageSignature !== currentRuntimeMessageSignature
				&& !(savedRuntimeMessageSignature == null && this.isEmptyRuntimeMessageSignature(currentRuntimeMessageSignature));
		});

		if (!hasChanges) {
			const currentWritebackSignature = this.getWritebackSetConfigurationSignature();
			const savedWritebackSignature = baseline.get(this.getWritebackSetReviewKey());
			hasChanges = savedWritebackSignature !== currentWritebackSignature
				&& !(savedWritebackSignature == null && this.createWritebacks.length === 0 && this.updateWritebacks.length === 0);
		}

		this.hasPendingMappingChangesValue = hasChanges;
	}

	private collectSaveMappingReviewItemsForRows(
		reviewItems: SaveMappingReviewItem[],
		baseline: Map<string, string>,
		node: MapperEditorNode,
		tab: RuleTab,
		rows: MappingGridRow[]
	): void {
		for (const row of rows) {
			const key = this.getRowReviewKey(node, tab, row);
			const savedSignature = baseline.get(key);
			if (!this.isRowConfigurationChanged(row, savedSignature)) {
				continue;
			}

			reviewItems.push({
				mapping: node.name || `${node.sourceEntity} -> ${node.targetEntity}`,
				tab: tab === 'create' ? 'Create' : 'Update',
				field: row.targetDisplayName || row.targetPath || row.targetFieldId,
				before: savedSignature == null ? 'Not saved' : this.formatSavedRowSignatureForReview(savedSignature),
				after: this.formatRowConfigurationForReview(row)
			});
		}
	}

	private isRowConfigurationChanged(row: MappingGridRow, savedSignature: string | null | undefined): boolean {
		if (savedSignature === this.getRowConfigurationSignature(row)) {
			return false;
		}

		if (this.isRowUnmapped(row) && (savedSignature == null || this.isIgnoredRowConfigurationSignature(savedSignature))) {
			return false;
		}

		return true;
	}

	private collectSaveMappingReviewItemsForWritebacks(
		reviewItems: SaveMappingReviewItem[],
		baseline: Map<string, string>
	): void {
		for (const [ruleSet, writebacks] of [
			['create', this.createWritebacks] as const,
			['update', this.updateWritebacks] as const
		]) {
			writebacks.forEach((writeback, index) => {
				const key = this.getWritebackReviewKey(ruleSet, index);
				const currentSignature = this.getWritebackConfigurationSignature(writeback);
				const savedSignature = baseline.get(key);

				if (savedSignature === currentSignature) {
					return;
				}

				reviewItems.push({
					mapping: this.rootMappingNode?.name || `${this.sourceEntity ?? 'Source'} -> ${this.targetEntity ?? 'Target'}`,
					tab: ruleSet === 'create' ? 'Writeback after create' : 'Writeback after update',
					field: `Writeback ${index + 1}`,
					before: savedSignature == null ? 'Not saved' : this.formatWritebackSignatureForReview(savedSignature),
					after: this.formatWritebackConfigurationForReview(writeback)
				});
			});
		}
	}

	private collectSaveMappingReviewItemsForRuntimeMessages(
		reviewItems: SaveMappingReviewItem[],
		baseline: Map<string, string>,
		node: MapperEditorNode
	): void {
		const key = this.getRuntimeMessageReviewKey(node);
		const currentSignature = this.getRuntimeMessageConfigurationSignature(node.runtimeMessageTemplates);
		const savedSignature = baseline.get(key);

		if (savedSignature === currentSignature) {
			return;
		}

		if (savedSignature == null && this.isEmptyRuntimeMessageSignature(currentSignature)) {
			return;
		}

		reviewItems.push({
			mapping: node.name || `${node.sourceEntity} -> ${node.targetEntity}`,
			tab: 'Messages',
			field: 'Runtime messages',
			before: savedSignature == null ? 'Not saved' : this.formatRuntimeMessageSignatureForReview(savedSignature),
			after: this.formatRuntimeMessageSignatureForReview(currentSignature)
		});
	}

	private collectSaveMappingReviewItemsForWritebackSet(
		reviewItems: SaveMappingReviewItem[],
		baseline: Map<string, string>
	): void {
		const key = this.getWritebackSetReviewKey();
		const currentSignature = this.getWritebackSetConfigurationSignature();
		const savedSignature = baseline.get(key);

		if (savedSignature === currentSignature) {
			return;
		}

		if (savedSignature == null && this.createWritebacks.length === 0 && this.updateWritebacks.length === 0) {
			return;
		}

		const alreadyHasWritebackReviewItem = reviewItems.some(item => item.tab.startsWith('Writeback'));
		if (alreadyHasWritebackReviewItem) {
			return;
		}

		reviewItems.push({
			mapping: this.rootMappingNode?.name || `${this.sourceEntity ?? 'Source'} -> ${this.targetEntity ?? 'Target'}`,
			tab: 'Writeback',
			field: 'Writeback configuration',
			before: savedSignature == null ? 'Not saved' : this.formatWritebackSetSignatureForReview(savedSignature),
			after: this.formatWritebackSetSignatureForReview(currentSignature)
		});
	}

	private getWritebackConfigurationSignature(writeback: MappingWriteback): string {
		return JSON.stringify({
			sourceFieldPath: writeback.sourceFieldPath ?? null,
			mode: writeback.mode,
			targetFieldPath: writeback.mode === 'TargetValue' ? (writeback.targetFieldPath ?? null) : null,
			dateFormat: writeback.mode === 'CurrentDate' ? (writeback.dateFormat ?? 'yyyy-MM-dd') : null,
			expression: writeback.mode === 'Expression' ? (writeback.expression ?? '') : null,
			conditionExpression: writeback.conditionExpression?.trim() || null
		});
	}

	private getWritebackSetConfigurationSignature(): string {
		return JSON.stringify({
			create: this.createWritebacks.map(writeback => JSON.parse(this.getWritebackConfigurationSignature(writeback))),
			update: this.updateWritebacks.map(writeback => JSON.parse(this.getWritebackConfigurationSignature(writeback)))
		});
	}

	private formatWritebackConfigurationForReview(writeback: MappingWriteback): string {
		const source = this.getWritebackSourceDisplayValue(writeback) || `No ${this.sourceSystemName} field`;
		const condition = writeback.conditionExpression?.trim()
			? ` when ${writeback.conditionExpression.trim()}`
			: '';

		if (writeback.mode === 'CurrentDate') {
			return `${source} = current date (${writeback.dateFormat || 'yyyy-MM-dd'})${condition}`;
		}

		if (writeback.mode === 'Expression') {
			return `${source} = ${writeback.expression?.trim() || 'No expression'}${condition}`;
		}

		return `${source} = ${this.getWritebackTargetDisplayValue(writeback) || `No ${this.targetSystemName} field`}${condition}`;
	}

	private formatWritebackSignatureForReview(signature: string): string {
		try {
			const parsed = JSON.parse(signature) as MappingWriteback;
			return this.formatWritebackConfigurationForReview(parsed);
		} catch {
			return signature;
		}
	}

	private formatWritebackSetSignatureForReview(signature: string): string {
		try {
			const parsed = JSON.parse(signature) as { create?: MappingWriteback[]; update?: MappingWriteback[] } | MappingWriteback[];
			if (Array.isArray(parsed)) {
				return parsed.length ? `${parsed.length} shared writeback${parsed.length === 1 ? '' : 's'}` : 'No writebacks';
			}

			return `${parsed.create?.length ?? 0} after create, ${parsed.update?.length ?? 0} after update`;
		} catch {
			return signature;
		}
	}

	private getRuntimeMessageConfigurationSignature(templates: MapperRuntimeMessageTemplates | null | undefined): string {
		return JSON.stringify(this.normalizeRuntimeMessageTemplates({ runtimeMessageTemplates: templates ?? null }));
	}

	private isEmptyRuntimeMessageSignature(signature: string): boolean {
		try {
			const parsed = JSON.parse(signature) as MapperRuntimeMessageTemplates;
			return this.runtimeMessageTemplateExamples.every(template => !String(parsed[template.key] ?? '').trim());
		} catch {
			return !signature.trim();
		}
	}

	private formatRuntimeMessageSignatureForReview(signature: string): string {
		try {
			const parsed = JSON.parse(signature) as MapperRuntimeMessageTemplates;
			const configured = this.runtimeMessageTemplateExamples
				.map(template => ({
					label: template.label,
					value: String(parsed[template.key] ?? '').trim()
				}))
				.filter(template => !!template.value);

			return configured.length
				? configured.map(template => `${template.label}: ${template.value}`).join('\n')
				: 'No custom runtime messages';
		} catch {
			return signature;
		}
	}

	private captureCurrentRowSignatures(): Map<string, string> {
		const result = new Map<string, string>();

		this.forEachMapperNode(node => {
			node.createMappingRows.forEach(row => result.set(this.getRowReviewKey(node, 'create', row), this.getRowConfigurationSignature(row)));
			node.updateMappingRows.forEach(row => result.set(this.getRowReviewKey(node, 'update', row), this.getRowConfigurationSignature(row)));
			result.set(this.getRuntimeMessageReviewKey(node), this.getRuntimeMessageConfigurationSignature(node.runtimeMessageTemplates));
		});

		this.createWritebacks.forEach((writeback, index) => {
			result.set(this.getWritebackReviewKey('create', index), this.getWritebackConfigurationSignature(writeback));
		});
		this.updateWritebacks.forEach((writeback, index) => {
			result.set(this.getWritebackReviewKey('update', index), this.getWritebackConfigurationSignature(writeback));
		});
		result.set(this.getWritebackSetReviewKey(), this.getWritebackSetConfigurationSignature());

		return result;
	}

	private forEachMapperNode(callback: (node: MapperEditorNode) => void, node: MapperEditorNode | null = this.rootMappingNode): void {
		if (!node) {
			return;
		}

		callback(node);
		node.children.forEach(child => this.forEachMapperNode(callback, child));
	}

	private getRowReviewKey(node: MapperEditorNode, tab: RuleTab, row: MappingGridRow): string {
		return `${this.getNodeReviewKey(node)}|${tab}|${row.targetFieldId || row.targetPath}`;
	}

	private getWritebackReviewKey(ruleSet: RuleTab, index: number): string {
		return `writeback|${ruleSet}|${index}`;
	}

	private getWritebackSetReviewKey(): string {
		return 'writeback|set';
	}

	private getRuntimeMessageReviewKey(node: MapperEditorNode): string {
		return `${this.getNodeReviewKey(node)}|messages`;
	}

	private getNodeReviewKey(node: MapperEditorNode): string {
		return [
			node.kind,
			node.mappingId ?? '',
			node.ownerTargetPath ?? '',
			node.sourceEntity,
			node.targetEntity,
			node.sourceCollectionPath ?? '',
			node.targetRuntimeRoute ?? ''
		].join('|').toLowerCase();
	}

	private formatSavedRowSignatureForReview(signature: string): string {
		try {
			const parsed = JSON.parse(signature) as {
				mode?: string;
				isLinkKey?: boolean;
				sourceFieldPath?: string | null;
				value?: string | null;
				lookup?: ManualLookupConfiguration | null;
				picklistMapping?: PicklistMappingConfiguration | null;
				normalization?: MappingRuleNormalization | null;
			};

			const parts: string[] = [parsed.mode || 'Ignore'];
			if (parsed.isLinkKey) {
				parts.push('link key');
			}

			switch (parsed.mode) {
				case 'SourceField':
					parts.push(parsed.sourceFieldPath || 'no source field');
					break;
				case 'Constant':
				case 'Expression':
					parts.push(parsed.value || 'empty value');
					break;
				case 'Lookup':
					parts.push(this.formatSavedLookupConfigurationForReview(parsed.lookup ?? null));
					break;
			}

			if (parsed.picklistMapping?.items?.length) {
				parts.push(`${parsed.picklistMapping.items.length} picklist value${parsed.picklistMapping.items.length === 1 ? '' : 's'}`);
			}

			if (parsed.normalization) {
				parts.push('normalization');
			}

			return parts.join(' | ');
		} catch {
			return signature;
		}
	}

	private formatSavedLookupConfigurationForReview(lookup: ManualLookupConfiguration | null): string {
		if (!lookup) {
			return 'lookup not configured';
		}

		const criteriaCount = lookup.criteria?.length ?? 0;
		return `${lookup.referenceEntityName || 'related entity'} -> ${lookup.returnFieldPath || 'return field'} (${criteriaCount} match rule${criteriaCount === 1 ? '' : 's'})`;
	}

	private escapeHtml(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

  private copyRowConfiguration(sourceRow: MappingGridRow, targetRow: MappingGridRow): void {
    targetRow.mode = sourceRow.mode;
    targetRow.isLinkKey = !!sourceRow.isLinkKey;
    targetRow.sourceFieldPath = sourceRow.sourceFieldPath ?? null;
    targetRow.value = sourceRow.value ?? null;
    targetRow.lookup = sourceRow.lookup
      ? {
        referenceEntityName: sourceRow.lookup.referenceEntityName,
        returnFieldPath: sourceRow.lookup.returnFieldPath,
        matchMode: sourceRow.lookup.matchMode,
        criteria: (sourceRow.lookup.criteria ?? []).map(criterion => ({
          targetFieldPath: criterion.targetFieldPath,
          mode: criterion.mode,
          sourceFieldPath: criterion.sourceFieldPath ?? null,
          value: criterion.value ?? null
        }))
      }
      : null;
    targetRow.picklistMapping = sourceRow.picklistMapping
      ? {
        sourceFieldPath: sourceRow.picklistMapping.sourceFieldPath,
        items: (sourceRow.picklistMapping.items ?? []).map(item => ({
          sourceValue: item.sourceValue,
          sourceLabel: item.sourceLabel,
          targetValue: item.targetValue ?? null
        }))
      }
      : null;
		targetRow.normalization = this.cloneNormalizationConfiguration(sourceRow.normalization ?? null);
    targetRow.previewValue = undefined;
    targetRow.isValid = undefined;
    targetRow.validationMessage = null;
    this.syncRowLinkKeyValidity(targetRow);
		this.enforceSingleGlobalLinkKey();
  }

	private enforceSingleGlobalLinkKey(): void {
		const node = this.selectedMappingNode;
		if (!node) {
			return;
		}

		for (const row of [...node.createMappingRows, ...node.updateMappingRows]) {
			if (row.matchOnlyLinkKey && row.mode !== 'Ignore') {
				row.isLinkKey = true;
			}
		}

		const keyedRows = [...node.createMappingRows, ...node.updateMappingRows].filter(row => row.isLinkKey);
		const sharedKeySource = keyedRows.find(row => this.canUseAsSharedLinkKeySource(row));

		if (sharedKeySource) {
			this.synchronizeSharedLinkKey(node, sharedKeySource);
			return;
		}

		if (keyedRows.length > 0) {
			this.clearSharedLinkKey(node);
			return;
		}

	}

	private enforceSharedLinkKeysForAllNodes(node: MapperEditorNode | null = this.rootMappingNode): void {
		if (!node) {
			return;
		}

		for (const row of [...node.createMappingRows, ...node.updateMappingRows]) {
			if (row.matchOnlyLinkKey && this.canUseAsSharedLinkKeySource(row)) {
				row.isLinkKey = true;
			}
		}

		const keyedRows = [...node.createMappingRows, ...node.updateMappingRows].filter(row => row.isLinkKey);
		const sharedKeySource = keyedRows.find(row => this.canUseAsSharedLinkKeySource(row));

		if (sharedKeySource) {
			this.synchronizeSharedLinkKey(node, sharedKeySource);
		} else if (keyedRows.length > 0) {
			this.clearSharedLinkKey(node);
		}

		node.children.forEach(child => this.enforceSharedLinkKeysForAllNodes(child));
	}

	private synchronizeSharedLinkKeyFromRow(sourceRow: MappingGridRow): void {
		const node = this.selectedMappingNode;
		if (!node) {
			return;
		}

		if (!this.canUseAsSharedLinkKeySource(sourceRow)) {
			this.clearSharedLinkKey(node, sourceRow.targetFieldId);
			return;
		}

		this.synchronizeSharedLinkKey(node, sourceRow);
	}

	private synchronizeSharedLinkKey(node: MapperEditorNode, sourceRow: MappingGridRow): void {
		for (const row of [...node.createMappingRows, ...node.updateMappingRows]) {
			if (row.targetFieldId !== sourceRow.targetFieldId) {
				row.isLinkKey = false;
				continue;
			}

			const nextSourceFieldPath = sourceRow.mode === 'SourceField' ? (sourceRow.sourceFieldPath ?? null) : null;
			const nextValue = sourceRow.mode === 'Expression' ? (sourceRow.value ?? null) : null;
			const mappingChanged = row.mode !== sourceRow.mode
				|| row.sourceFieldPath !== nextSourceFieldPath
				|| row.value !== nextValue
				|| row.lookup !== null
				|| row.picklistMapping !== null;

			row.mode = sourceRow.mode;
			row.isLinkKey = true;
			row.sourceFieldPath = nextSourceFieldPath;
			row.value = nextValue;
			row.lookup = null;
			row.picklistMapping = null;

			if (mappingChanged) {
				row.previewValue = undefined;
				row.isValid = undefined;
				row.validationMessage = null;
			}
		}
	}

	private clearSharedLinkKey(node: MapperEditorNode, targetFieldId: string | null = null): void {
		for (const row of [...node.createMappingRows, ...node.updateMappingRows]) {
			if (targetFieldId && row.targetFieldId !== targetFieldId) {
				continue;
			}

			row.isLinkKey = false;
		}
	}

	private canUseAsSharedLinkKeySource(row: MappingGridRow): boolean {
		return row.mode === 'SourceField'
			? !!row.sourceFieldPath
			: row.mode === 'Expression' && !!row.value?.trim();
	}

  private buildPicklistMappingFromValueMap(
    row: MappingGridRow,
    valueMap: MappingValueMapConfiguration | null | undefined
  ): PicklistMappingConfiguration | null {
    if (!valueMap?.items?.length || !row.sourceFieldPath) {
      return null;
    }

    const items = valueMap.items
      .filter(item => !!item.targetValue)
      .map(item => ({
        sourceValue: item.sourceValue ?? '',
        sourceLabel: item.sourceLabel ?? item.sourceValue ?? '',
        targetValue: item.targetValue
      }));

    if (!items.length) {
      return null;
    }

    return {
      sourceFieldPath: row.sourceFieldPath,
      items
    };
  }

	private buildValueMap(row: MappingGridRow) {
    if (!row.picklistMapping?.items?.length) {
      return null;
	}

    const mappedItems = row.picklistMapping.items
      .filter(item => !!item.targetValue)
      .map(item => ({
        sourceValue: item.sourceValue ?? null,
        sourceLabel: item.sourceLabel ?? null,
        targetValue: item.targetValue!,
        targetLabel: row.targetPicklistOptions?.find(option => option.value === item.targetValue)?.label ?? null,
        isDefault: false
      }));

    if (!mappedItems.length) {
      return null;
    }

    return {
      enabled: true,
      unmappedValueBehavior: 'Fail',
      defaultTargetValue: null,
      items: mappedItems
    };
  }

	private normalizeRuntimeMessageTemplates(
		mapping: { runtimeMessageTemplates?: MapperRuntimeMessageTemplates | null } | null | undefined
	): MapperRuntimeMessageTemplates {
		const templates = mapping?.runtimeMessageTemplates ?? {};

		return {
			createSuccess: templates.createSuccess?.trim() || null,
			updateSuccess: templates.updateSuccess?.trim() || null,
			validationFailure: templates.validationFailure?.trim() || null,
			upsertFailure: templates.upsertFailure?.trim() || null,
			writebackFailure: templates.writebackFailure?.trim() || null
		};
	}

  private normalizeExpressionForBackend(expression: string | null | undefined): string | null {
    if (!expression?.trim()) {
      return null;
    }

    return expression.replace(/\[([^\]]+)\]/g, (_, fieldPath: string) => `Form.${(fieldPath ?? '').trim()}`);
  }

  private flattenSchema(fields: MappingFieldSchema[]): MappingFieldSchema[] {
    const result: MappingFieldSchema[] = [];

    for (const field of fields ?? []) {
      result.push(field);

      if (field.children?.length) {
        result.push(...this.flattenSchema(field.children));
      }
    }

    return result;
  }

  private findSchemaFieldByPath(
		fields: MappingFieldSchema[],
		path: string | null | undefined,
		parentPath = ''
	): MappingFieldSchema | null {
    const normalizedPath = path?.trim();
    if (!normalizedPath) {
      return null;
    }

    for (const field of fields ?? []) {
			const fullPath = parentPath ? `${parentPath}.${field.path}` : field.path;
			if (fullPath === normalizedPath || (!parentPath && field.path === normalizedPath)) {
        return field;
      }

			const childMatch = this.findSchemaFieldByPath(field.children ?? [], normalizedPath, fullPath);
      if (childMatch) {
        return childMatch;
      }
    }

    return null;
  }

  private buildInlineChildTargetSchema(parentSchema: MappingEntitySchema, ownerTargetPath: string, displayName: string): MappingEntitySchema {
    const ownerField = this.findSchemaFieldByPath(parentSchema.fields, ownerTargetPath);
    const prefix = `${ownerTargetPath}.`;

    const cloneField = (field: MappingFieldSchema): MappingFieldSchema => ({
      ...field,
      id: field.id.startsWith(prefix) ? field.id.slice(prefix.length) : field.id,
      path: field.path.startsWith(prefix) ? field.path.slice(prefix.length) : field.path,
      children: (field.children ?? []).map(child => cloneField(child))
    });

    return {
      ...parentSchema,
      id: parentSchema.id,
      entityName: displayName,
      primaryKeyPath: null,
      uniqueKeyPaths: [],
      fields: (ownerField?.children ?? []).map(child => cloneField(child))
    };
  }

  private normalizeExpressionForEditor(expression: string | null | undefined): string {
    if (!expression?.trim()) {
      return '';
    }

    return expression.replace(/\bForm\.([A-Za-z_][A-Za-z0-9_.]*)\b/g, (_, fieldPath: string) => `[${fieldPath}]`);
  }

	setExpressionFieldSearch(value: string | null | undefined): void {
		this.expressionFieldSearch = value ?? '';
		if (this.expressionEditorUsesGuideFields) {
			this.expressionSourceTreeItems = this.filterTreeNodes(this.mapperGuideSourceTreeItems, this.expressionFieldSearch);
			return;
		}

		this.rebuildExpressionSourceTreeItems();
	}

	setExpressionTargetFieldSearch(value: string | null | undefined): void {
		this.expressionTargetFieldSearch = value ?? '';
		if (this.expressionEditorUsesGuideFields) {
			this.expressionTargetTreeItems = this.filterTreeNodes(this.mapperGuideTargetTreeItems, this.expressionTargetFieldSearch);
			return;
		}

		this.rebuildExpressionTargetTreeItems();
	}

	private rebuildExpressionSourceTreeItems(): void {
		this.expressionSourceTreeItems = this.filterTreeNodes(this.sourceTreeItems, this.expressionFieldSearch);
	}

	private rebuildExpressionTargetTreeItems(): void {
		this.expressionTargetTreeItems = this.filterTreeNodes(this.targetTreeItems, this.expressionTargetFieldSearch);
	}

  onExpressionTreeFieldSelected(item: TreeNode): void {
    if (!item || !item.isMappable || !item.path) {
      return;
    }

    const match = this.sourceFieldOptions.find(x => x.path === item.path);
    if (match) {
			this.selectedExpressionSourceField = match;
      this.insertFieldIntoExpression(match);
    }
  }

	onExpressionTargetTreeFieldSelected(item: TreeNode): void {
		if (!item || !item.isMappable || !item.path) {
			return;
		}

		this.applyExpressionInsertion(`Result.${item.path}`, false);
	}

	get canTestExpressionDraft(): boolean {
		if (this.expressionEditorUsesGuideFields) {
			return !!this.expressionDraft?.trim() && !this.expressionTestLoading;
		}

		return !!this.expressionEditingTargetFieldId && !!this.expressionDraft?.trim() && !this.expressionTestLoading;
	}

	get showExpressionTargetFields(): boolean {
		return this.writebackExpressionEditIndex !== null
			|| this.guideExpressionEditingRow?.targetFieldId === 'guide-writeback';
	}

	get expressionEditingRow(): MappingGridRow | null {
		if (this.guideExpressionEditingRow) {
			return this.guideExpressionEditingRow;
		}

		if (!this.expressionEditingTargetFieldId) {
			return this.lookupEditingDraftRow ?? null;
		}

		return this.mappingRows.find(x => x.targetFieldId === this.expressionEditingTargetFieldId) ?? null;
	}

	testExpressionDraft(): void {
		if (this.expressionEditorUsesGuideFields) {
			this.testMapperGuideExpressionDraft();
			return;
		}

		const row = this.mappingRows.find(x => x.targetFieldId === this.expressionEditingTargetFieldId);
		const draft = this.expressionDraft?.trim() ?? '';

		this.resetExpressionTestResult(false);

		if (!row || !draft) {
			this.expressionTestRan = true;
			this.expressionTestIsValid = false;
			this.expressionTestMessage = 'Select a target row and enter an expression first.';
			return;
		}

		if (!this.configuration) {
			this.expressionTestRan = true;
			this.expressionTestIsValid = false;
			this.expressionTestMessage = 'Mapper configuration is not loaded.';
			return;
		}

		if (this.hasRequiredSampleInputs()) {
			this.expressionTestRan = true;
			this.expressionTestIsValid = false;
			this.expressionTestMessage = 'Complete the required sample inputs before testing expressions.';
			return;
		}

		const originalMode = row.mode;
		const originalValue = row.value;
		const originalSourceFieldPath = row.sourceFieldPath;
		const originalLookup = row.lookup;
		const originalPicklistMapping = row.picklistMapping;

		row.mode = 'Expression';
		row.value = draft;
		row.sourceFieldPath = null;
		row.lookup = null;
		row.picklistMapping = null;

		this.expressionTestLoading = true;
		this.expressionTestRan = true;

		this.runPreviewRequest(row)
			.pipe(
				finalize(() => {
					row.mode = originalMode;
					row.value = originalValue;
					row.sourceFieldPath = originalSourceFieldPath;
					row.lookup = originalLookup;
					row.picklistMapping = originalPicklistMapping;
					this.expressionTestLoading = false;
				})
			)
			.subscribe({
				next: response => this.applyExpressionTestResponse(response, row, draft),
				error: error => {
					this.expressionTestIsValid = false;
					this.expressionTestMessage = this.showMapperApiError(error, 'Expression test failed.', 'Expression test failed');
				}
			});
	}

	private applyExpressionTestResponse(response: MappingPreviewResponse | null, row: MappingGridRow, expression: string): void {
		const fieldResult = response?.fieldResults?.find(x => x.targetFieldId === row.targetFieldId);
		const resolvedSourcePayload = response?.resolvedSourcePayload ?? response?.sourcePayload ?? this.configuration?.sampleSourcePayload ?? {};

		this.expressionTestIsValid = fieldResult?.isValid ?? false;
		this.expressionTestHasResult = !!fieldResult && Object.prototype.hasOwnProperty.call(fieldResult, 'previewValue');
		this.expressionTestResult = fieldResult?.previewValue ?? null;
		this.expressionTestMessage = fieldResult?.message ?? (fieldResult?.isValid ? 'Expression evaluated successfully.' : 'No expression result returned.');
		this.expressionTestUsedValues = this.extractReferencedExpressionFieldPaths(expression)
			.map(path => this.buildExpressionTestUsedValue(path, resolvedSourcePayload));
	}

	private testMapperGuideExpressionDraft(): void {
		const draft = this.expressionDraft?.trim() ?? '';
		const row = this.guideExpressionEditingRow ?? this.lookupEditingDraftRow;

		this.resetExpressionTestResult(false);
		this.expressionTestRan = true;

		if (!row || !draft) {
			this.expressionTestIsValid = false;
			this.expressionTestMessage = 'Select a demo row and enter an expression first.';
			return;
		}

		this.expressionTestIsValid = true;
		this.expressionTestHasResult = true;
		this.expressionTestResult = resolveMapperGuideExpressionPreview(draft);
		this.expressionTestMessage = 'Demo expression evaluated with guide sample values.';
		this.expressionTestUsedValues = this.extractReferencedExpressionFieldPaths(draft)
			.map(path => ({
				path,
				label: this.mapperGuideSourceFieldOptions.find(option => option.path.toLowerCase() === path.toLowerCase())?.label || path,
				value: getMapperGuideSourceValue(path)
			}));
	}

	private resetExpressionTestResult(clearRan = true): void {
		if (clearRan) {
			this.expressionTestRan = false;
		}

		this.expressionTestLoading = false;
		this.expressionTestIsValid = null;
		this.expressionTestHasResult = false;
		this.expressionTestResult = null;
		this.expressionTestMessage = null;
		this.expressionTestUsedValues = [];
	}

	private extractReferencedExpressionFieldPaths(expression: string): string[] {
		const paths: string[] = [];
		const seen = new Set<string>();
		const regex = /\[([^\]\r\n]+)]/g;
		let match: RegExpExecArray | null;

		while ((match = regex.exec(expression ?? '')) !== null) {
			const path = (match[1] ?? '').trim();
			const key = path.toLowerCase();
			if (!path || seen.has(key)) {
				continue;
			}

			seen.add(key);
			paths.push(path);
		}

		return paths;
	}

	private buildExpressionTestUsedValue(path: string, sourcePayload: any): ExpressionTestUsedValue {
		const field = this.sourceFieldOptions.find(option => option.path.toLowerCase() === path.toLowerCase());
		const value = this.readJsonPathCaseInsensitive(sourcePayload, path);

		return {
			path,
			label: field?.label || path,
			value: this.formatExpressionTestValue(value)
		};
	}

	private formatExpressionTestValue(value: any): string {
		if (value === undefined) {
			return 'undefined';
		}

		if (value === null) {
			return 'null';
		}

		if (typeof value === 'object') {
			try {
				return JSON.stringify(value);
			} catch {
				return '[object]';
			}
		}

		return String(value);
	}

	onExpressionPicklistFieldSelected(item: TreeNode): void {
		if (!item?.isPicklist || !item.path) {
			return;
		}

		const sourceOptions = this.expressionEditorUsesGuideFields ? this.mapperGuideSourceFieldOptions : this.sourceFieldOptions;
		const match = sourceOptions.find(x => x.path === item.path);
		if (match?.isPicklist) {
			this.selectedExpressionSourceField = match;
		}
	}

	private formatExpressionLiteral(value: string | null | undefined): string {
		const raw = value ?? '';
		if (/^-?\d+(\.\d+)?$/.test(raw)) {
			return raw;
		}

		return `'${raw.replace(/'/g, "''")}'`;
	}

  private filterTreeNodes(items: TreeNode[], query: string): TreeNode[] {
    const normalizedQuery = (query ?? '').trim().toLowerCase();

    if (!normalizedQuery) {
      return items;
    }

    const filterNode = (node: TreeNode): TreeNode | null => {
      const ownMatch =
        (node.displayName ?? '').toLowerCase().includes(normalizedQuery) ||
        (node.path ?? '').toLowerCase().includes(normalizedQuery) ||
        (node.description ?? '').toLowerCase().includes(normalizedQuery) ||
        (node.dataType ?? '').toLowerCase().includes(normalizedQuery);

      const filteredChildren = (node.items ?? [])
        .map(child => filterNode(child))
        .filter((child): child is TreeNode => !!child);

      if (ownMatch || filteredChildren.length > 0) {
        return {
          ...node,
          items: filteredChildren
        };
      }

      return null;
    };

    return (items ?? [])
      .map(node => filterNode(node))
      .filter((node): node is TreeNode => !!node);
  }

  private rebuildWritebackSourceTree(index: number, writeback: MappingWriteback | undefined): void {
    if (!writeback) {
      this.writebackSourceTreeItemsByIndex[index] = [];
      return;
    }

    const allowedPaths = new Set(this.sourceWritableFieldOptions.map(x => x.path));
    const query = (this.getWritebackSourceFieldSearchValue(index) || '').trim().toLowerCase();
    const selectedPath = writeback.sourceFieldPath ?? null;

    this.writebackSourceTreeItemsByIndex[index] = this.buildSelectableTree(
      this.sourceTreeItems,
      allowedPaths,
      query,
      selectedPath
    );
  }

  private rebuildWritebackTargetTree(index: number, writeback: MappingWriteback | undefined): void {
    if (!writeback) {
      this.writebackTargetTreeItemsByIndex[index] = [];
      return;
    }

    const allowedPaths = new Set(this.targetReadableFieldOptions.map(x => x.path));
    const query = (this.getWritebackTargetFieldSearchValue(index) || '').trim().toLowerCase();
    const selectedPath = writeback.targetFieldPath ?? null;

    this.writebackTargetTreeItemsByIndex[index] = this.buildSelectableTree(
      this.targetTreeItems,
      allowedPaths,
      query,
      selectedPath
    );
  }

	private rebuildSourceIdPathTree(): void {
    const rootNode = this.rootMappingNode;
    if (!rootNode) {
      this.sourceIdPathTreeItems = [];
      return;
    }

    const allowedPaths = new Set(rootNode.sourceFieldOptions.map(option => option.path));
    const query = (this.sourceIdPathFieldSearch || '').trim().toLowerCase();
    const selectedPath = rootNode.sourceIdPath?.trim() || null;

		this.sourceIdPathTreeItems = this.buildSelectableTree(
      rootNode.sourceTreeItems,
      allowedPaths,
      query,
      selectedPath
		);
	}

	private resolveSourceIdPath(sourceSchema: MappingEntitySchema, candidatePath?: string | null): string {
		const readableFields = this.flattenSchemaToFieldOptions(
			sourceSchema?.fields ?? [],
			'',
			'',
			field => this.isLeafField(field) && field.canRead
		);
		const availablePaths = new Set(readableFields.map(field => field.path));
		const candidate = candidatePath?.trim();
		if (candidate && availablePaths.has(candidate)) {
			return candidate;
		}

		const primaryKeyPath = sourceSchema?.primaryKeyPath?.trim();
		if (primaryKeyPath && availablePaths.has(primaryKeyPath)) {
			return primaryKeyPath;
		}

		const schemaKeyPath = readableFields.find(field => field.isKey)?.path;
		if (schemaKeyPath) {
			return schemaKeyPath;
		}

		return availablePaths.has('id') ? 'id' : (readableFields[0]?.path || primaryKeyPath || 'id');
	}

	private buildSelectableTree(
    items: TreeNode[],
    allowedPaths: Set<string>,
    query: string,
    selectedPath: string | null
  ): TreeNode[] {
    const mapNode = (node: TreeNode): TreeNode | null => {
      const originalChildren = node.items ?? [];

      const mappedChildren = originalChildren
        .map(child => mapNode(child))
        .filter((x): x is TreeNode => !!x);

      const hadChildrenOriginally = originalChildren.length > 0;
      const hasVisibleChildren = mappedChildren.length > 0;
		const isLeaf = !hadChildrenOriginally && !node.childrenDeferred;
      const isAllowedLeaf = !!node.isMappable && allowedPaths.has(node.path);
      const isSelectedLeaf = !!selectedPath && node.path === selectedPath;

      const haystack = `${node.displayName ?? ''} ${node.path ?? ''} ${node.dataType ?? ''}`.toLowerCase();
      const matchesSearch = !query || haystack.includes(query);

      const includeNode =
        matchesSearch ||
        hasVisibleChildren ||
        (!query && isAllowedLeaf) ||
        isSelectedLeaf;

      if (!includeNode) {
        return null;
      }

      return {
        ...node,
        items: mappedChildren,
        disabled: isLeaf ? !isAllowedLeaf : false
      };
    };

    return (items ?? [])
      .map(node => mapNode(node))
      .filter((node): node is TreeNode => !!node);
  }

  private flattenSchemaToFieldOptions(
    fields: MappingFieldSchema[],
    labelPrefix = '',
    pathPrefix = '',
    predicate: (field: MappingFieldSchema) => boolean = () => true
  ): FieldOption[] {
    const result: FieldOption[] = [];

    for (const field of fields ?? []) {
      const label = labelPrefix ? `${labelPrefix} / ${field.displayName}` : field.displayName;
      const fullPath = pathPrefix ? `${pathPrefix}.${field.path}` : field.path;

      if (this.isLeafField(field) && predicate(field)) {
        const qualifiers = [
          field.isUserDefinedField ? 'UDF' : null,
          field.isKey ? 'key' : null,
          field.isUniqueKey ? 'unique' : null,
          field.isReference ? 'reference' : null,
          field.referenceEntityName ? `ref:${field.referenceEntityName}` : null,
          field.isPicklist ? 'picklist' : null
        ]
          .filter(Boolean)
          .join(', ');

		result.push({
			id: fullPath,
			path: fullPath,
			label: `${label} [${fullPath}]${qualifiers ? ` (${qualifiers})` : ''}`,
          dataType: field.dataType,
          isUserDefinedField: field.isUserDefinedField,
          description: field.description ?? null,
          canRead: field.canRead,
          canCreate: field.canCreate,
          canUpdate: field.canUpdate,
          isReference: field.isReference,
          isKey: field.isKey,
          isUniqueKey: field.isUniqueKey,
          canUseInLookup: field.canUseInLookup,
          isPicklist: field.isPicklist,
          picklistValueMode: field.picklistValueMode ?? null,
				picklistOptions: field.picklistOptions ?? [],
				optionsDeferred: field.optionsDeferred === true,
				deferredOptionsEntityName: field.deferredOptionsEntityName ?? null,
				deferredOptionsFieldPath: field.deferredOptionsFieldPath ?? null
        });
      }

      if (field.children?.length) {
        result.push(
          ...this.flattenSchemaToFieldOptions(
            field.children,
            label,
            fullPath,
            predicate
          )
        );
      }
    }

    return result;
  }

  private mapSchemaToTreeNodes(
    fields: MappingFieldSchema[],
    parentPath = ''
  ): TreeNode[] {
    return (fields ?? []).map(field => {
      const fullPath = parentPath ? `${parentPath}.${field.path}` : field.path;
			const loadedChildren = this.mapSchemaToTreeNodes(field.children ?? [], fullPath);
			const deferredState = this.deferredFieldLoadStates.get(field);
			const deferredChildren: TreeNode[] = field.childrenDeferred && loadedChildren.length === 0
				? [{
					id: `${fullPath}.__deferred__`,
					displayName: deferredState?.state === 'loading'
						? 'Loading fields...'
						: deferredState?.state === 'error'
							? 'Could not load fields. Collapse and expand to retry.'
							: 'Fields load when expanded.',
					path: `${fullPath}.__deferred__`,
					isMappable: false,
					disabled: true,
					items: []
				}]
				: [];
			const children = loadedChildren.length > 0 ? loadedChildren : deferredChildren;
			const isLeaf = children.length === 0 && !field.isCollection && !field.isChildObject;

      return {
        id: fullPath,
        displayName: field.displayName,
        path: fullPath,
        dataType: field.dataType,
        description: field.description ?? null,
        isReadOnly: field.isReadOnly,
        isPicklist: field.isPicklist,
        isReference: field.isReference,
        isUserDefinedField: field.isUserDefinedField,
        isCollection: field.isCollection,
        isChildObject: field.isChildObject,
				childrenDeferred: field.childrenDeferred === true,
				referenceEntityName: field.referenceEntityName ?? field.reference?.targetEntity ?? null,
				deferredChildrenEntityName: field.deferredChildrenEntityName ?? null,
				deferredChildrenFieldPath: field.deferredChildrenFieldPath ?? null,
				deferredLoadState: deferredState?.state ?? (field.childrenDeferred ? 'unloaded' : 'loaded'),
				deferredLoadError: deferredState?.error ?? null,
				hasItems: children.length > 0 || field.childrenDeferred === true,
				expanded: deferredState?.state === 'loading' || deferredState?.state === 'loaded' || deferredState?.state === 'error',
        canRead: field.canRead,
        canCreate: field.canCreate,
        canUpdate: field.canUpdate,
        isKey: field.isKey,
        isUniqueKey: field.isUniqueKey,
        canUseInLookup: field.canUseInLookup,
        isMappable: isLeaf && !!field.canRead,
        disabled: false,
        items: children
      };
    });
  }

	onDeferredSourceNodeExpanded(treeNode: TreeNode): void {
		const node = this.selectedMappingNode;
		if (!node || !treeNode?.childrenDeferred) {
			return;
		}

		void this.loadDeferredSourceChildren(node, treeNode.path, true).catch(() => undefined);
	}

	private async loadDeferredSourceChildren(
		node: MapperEditorNode,
		fieldPath: string,
		notifyOnError: boolean
	): Promise<void> {
		const field = this.findSchemaFieldByPath(node.sourceSchema.fields, fieldPath);
		if (!field?.childrenDeferred || !field.deferredChildrenEntityName || !field.deferredChildrenFieldPath) {
			return;
		}

		const existingPromise = this.deferredFieldLoadPromises.get(field);
		if (existingPromise) {
			return existingPromise;
		}

		const loadPromise = this.performDeferredSourceChildrenLoad(node, field, fieldPath, notifyOnError);
		this.deferredFieldLoadPromises.set(field, loadPromise);

		try {
			await loadPromise;
		} finally {
			this.deferredFieldLoadPromises.delete(field);
		}
	}

	private async performDeferredSourceChildrenLoad(
		node: MapperEditorNode,
		field: MappingFieldSchema,
		fieldPath: string,
		notifyOnError: boolean
	): Promise<void> {
		const linkId = this.linkId ?? this.configuration?.linkId ?? 0;
		if (linkId <= 0 || !field.deferredChildrenEntityName || !field.deferredChildrenFieldPath) {
			return;
		}

		this.deferredFieldLoadStates.set(field, { state: 'loading' });
		this.rebuildSourceSchemaViews(node);

		try {
			const childSchema = await firstValueFrom(this.api.getEntitySchema(
				linkId,
				field.deferredChildrenEntityName,
				'source',
				{
					deferReferenceChildren: 'true',
					deferredChildrenFieldPath: field.deferredChildrenFieldPath
				}
			));

			field.children = childSchema.fields ?? [];
			field.childrenDeferred = false;
			field.isChildObject = true;
			field.referenceEntityName = childSchema.entityName;
			field.reference = {
				targetSystem: childSchema.systemName,
				targetEntity: childSchema.entityName,
				endpoint: field.reference?.endpoint ?? 'link-mapper/schema',
				defaultReturnFieldPath: field.reference?.defaultReturnFieldPath ?? childSchema.primaryKeyPath ?? 'ID'
			};
			this.deferredFieldLoadStates.set(field, { state: 'loaded' });
			this.rebuildSourceSchemaViews(node);
		} catch (error) {
			const message = this.getApiErrorMessage(error, `Could not load nested fields for ${field.displayName || fieldPath}.`);
			this.deferredFieldLoadStates.set(field, { state: 'error', error: message });
			this.rebuildSourceSchemaViews(node);

			if (notifyOnError) {
				notify({ message, type: 'error', displayTime: 6000 });
			}

			throw error;
		}
	}

	private rebuildSourceSchemaViews(node: MapperEditorNode): void {
		node.sourceFieldOptions = this.flattenSchemaToFieldOptions(
			node.sourceSchema.fields,
			'',
			'',
			field => this.isLeafField(field) && field.canRead
		);
		node.sourceTreeItems = this.mapSchemaToTreeNodes(node.sourceSchema.fields);

		if (this.selectedMappingNode?.id === node.id) {
			this.rebuildExpressionSourceTreeItems();
			this.rebuildSourceIdPathTree();
			this.refreshRuntimeMessagePropertyExamples();
		}
	}

	private async hydrateSavedSourcePaths(node: MapperEditorNode, mapping: MappingDefinition): Promise<void> {
		const paths = new Set<string>();
		const addPath = (path: string | null | undefined): void => {
			if (path?.trim()) {
				paths.add(path.trim());
			}
		};
		const addExpressionPaths = (expression: string | null | undefined): void => {
			const pattern = /\[([^\]]+)\]/g;
			let match: RegExpExecArray | null;
			while ((match = pattern.exec(expression ?? '')) !== null) {
				addPath(match[1] ?? null);
			}
		};

		addPath(mapping.sourceIdPath);
		for (const rule of [...(mapping.createRules ?? []), ...(mapping.updateRules ?? []), ...(mapping.rules ?? [])]) {
			addPath(rule.sourceFieldPath);
			addExpressionPaths(rule.value);
			for (const criterion of rule.lookup?.criteria ?? []) {
				addPath(criterion.sourceFieldPath);
				addExpressionPaths(criterion.value);
			}
		}

		const writebacks = mapping.createWritebacks != null || mapping.updateWritebacks != null
			? [...(mapping.createWritebacks ?? []), ...(mapping.updateWritebacks ?? [])]
			: mapping.writebacks ?? [];
		for (const writeback of writebacks) {
			addPath(writeback.sourceFieldPath);
			addExpressionPaths(writeback.expression);
			addExpressionPaths(writeback.conditionExpression);
		}

		for (const path of paths) {
			await this.hydrateDeferredSourcePath(node, path);
		}
	}

	private async hydrateDeferredSourcePath(node: MapperEditorNode, path: string): Promise<void> {
		while (true) {
			const deferred = this.findDeferredSourceFieldForPath(node.sourceSchema.fields, path);
			if (!deferred) {
				return;
			}

			try {
				await this.loadDeferredSourceChildren(node, deferred.path, false);
			} catch {
				return;
			}
		}
	}

	private findDeferredSourceFieldForPath(
		fields: MappingFieldSchema[],
		targetPath: string,
		parentPath = ''
	): { field: MappingFieldSchema; path: string } | null {
		for (const field of fields ?? []) {
			const fullPath = parentPath ? `${parentPath}.${field.path}` : field.path;
			if (field.childrenDeferred && (targetPath === fullPath || targetPath.startsWith(`${fullPath}.`))) {
				return { field, path: fullPath };
			}

			const nested = this.findDeferredSourceFieldForPath(field.children ?? [], targetPath, fullPath);
			if (nested) {
				return nested;
			}
		}

		return null;
	}


  private flattenSchemaToTreeNodes(
    fields: MappingFieldSchema[],
    pathPrefix = '',
    idPrefix = ''
  ): TreeNode[] {
    const result: TreeNode[] = [];

    for (const field of fields ?? []) {
      const nodeId = idPrefix ? `${idPrefix}:${field.id}` : field.id;
      const fullPath = pathPrefix ? `${pathPrefix}.${field.path}` : field.path;
      const hasChildren = !!field.children?.length;

      result.push({
        id: nodeId,
        displayName: field.displayName,
        path: fullPath,
        dataType: field.dataType,
        description: field.description ?? null,
        isReadOnly: field.isReadOnly,
        isPicklist: field.isPicklist,
        isReference: field.isReference,
        isUserDefinedField: field.isUserDefinedField,
        isCollection: field.isCollection,
        isChildObject: field.isChildObject,
        canRead: field.canRead,
        canCreate: field.canCreate,
        canUpdate: field.canUpdate,
        isKey: field.isKey,
        isUniqueKey: field.isUniqueKey,
        canUseInLookup: field.canUseInLookup,
        isMappable: this.isLeafField(field) && field.canRead,
        disabled: !(this.isLeafField(field) && field.canRead)
      });

      if (hasChildren) {
        result.push(
          ...this.flattenSchemaToTreeNodes(
            field.children!,
            nodeId,
            fullPath
          )
        );
      }
    }

    return result;
  }

  private getDefaultMode(
    field: MappingFieldSchema,
    allowedModes: MappingRuleMode[],
    operation: 'create' | 'update'
  ): MappingRuleMode {
    const canWriteForOperation = operation === 'create' ? field.canCreate : field.canUpdate;

    if (field.isReadOnly || !canWriteForOperation || field.isKey) {
      return 'Ignore';
    }

    const isRequiredForOperation = this.isFieldRequiredForOperation(field, operation);

    if (isRequiredForOperation && !field.isReference && allowedModes.includes('SourceField')) {
      return 'SourceField';
    }

    return allowedModes[0] ?? 'Ignore';
  }

  private isLeafField(field: MappingFieldSchema): boolean {
    return !field.isCollection && !field.isChildObject && !(field.children?.length);
  }

	private buildRowsFromTargetSchema(
		schema: MappingEntitySchema,
		operation: 'create' | 'update'
	): MappingGridRow[] {
		return this.flattenSchema(schema.fields)
			.filter(field => {
				if (field.isCollection || field.isChildObject) {
					return true;
				}

				return this.isLeafField(field) && !this.isNestedUnderChildMappingField(schema.fields, field.path);
			})
	  .map(field => {
		const matchOnlyLinkKey = field.isReadOnly
			&& field.canRead
			&& field.canUseInLookup
			&& this.isLeafField(field);
		const allowedModes: MappingRuleMode[] = matchOnlyLinkKey
			? ['Ignore', 'SourceField', 'Expression']
			: field.allowedModes?.length
				? field.allowedModes
				: this.getDefaultAllowedModes(field, operation);

        const defaultMode = this.getDefaultMode(field, allowedModes, operation);
        const isWritableForOperation = operation === 'create' ? field.canCreate : field.canUpdate;
        const isRequiredForOperation = this.isFieldRequiredForOperation(field, operation);

        return {
          targetFieldId: field.id,
          targetPath: field.path,
          targetDisplayName: field.displayName,
          targetDataType: field.dataType,
          isCollection: field.isCollection,
          childSourceSchemaId: field.referenceEntityName ?? null,

          required: isRequiredForOperation,
          requiredOnCreate: field.isRequiredOnCreate,
          requiredOnUpdate: field.isRequiredOnUpdate,

          canRead: field.canRead,
          canCreate: field.canCreate,
          canUpdate: field.canUpdate,

		  readOnly: (field.isCollection || field.isChildObject)
			? false
			: (field.isReadOnly || !isWritableForOperation),
		  matchOnlyLinkKey,
          isPicklist: field.isPicklist,
          isReference: field.isReference,
          isUserDefinedField: field.isUserDefinedField,
          isChildObject: field.isChildObject,

          isKey: field.isKey,
          isUniqueKey: field.isUniqueKey,
          canUseInLookup: field.canUseInLookup,

          description: field.description ?? null,
          format: field.format ?? null,
          maxLength: field.maxLength ?? null,
          minLength: field.minLength ?? null,

          referenceEntityName: field.referenceEntityName ?? null,
          reference: field.reference ?? null,
          allowedModes,

          targetPicklistValueMode: field.picklistValueMode ?? null,
          targetPicklistOptions: (field.picklistOptions ?? []).filter(x => x.isActive !== false),

          mode: allowedModes.includes(defaultMode) ? defaultMode : allowedModes[0] ?? 'Ignore',
          isLinkKey: false,
          sourceFieldPath: null,
          value: null,
          lookup: null,
					normalization: null,
          sourceFieldOptions: [],
          childMappingCount: 0
        };
      });
  }

	private buildNormalizationConfiguration(row: MappingGridRow): MappingRuleNormalization | null {
		const normalization = row.normalization;
		if (!normalization || row.mode === 'Ignore') {
			return null;
		}

		const result: MappingRuleNormalization = {};

		if (normalization.trimWhitespace === true) {
			result.trimWhitespace = true;
		}

		if (normalization.emptyStringToNull === true) {
			result.emptyStringToNull = true;
		}

		if (normalization.truncateToMaxLength === true) {
			result.truncateToMaxLength = true;
		}

		if (normalization.truncateToMaxLength === true && this.isPositiveInteger(normalization.maxLength)) {
			result.maxLength = Number(normalization.maxLength);
		}

		const truncateSuffix = normalization.truncateSuffix ?? '';
		if (normalization.truncateToMaxLength === true && truncateSuffix.length > 0) {
			result.truncateSuffix = truncateSuffix;
		}

		if (normalization.padLeft === true) {
			result.padLeft = true;
		}

		if (normalization.padLeft === true && this.isPositiveInteger(normalization.padLeftToLength)) {
			result.padLeftToLength = Number(normalization.padLeftToLength);
		}

		const padLeftChar = normalization.padLeftChar?.charAt(0) || ' ';
		if (normalization.padLeft === true && padLeftChar) {
			result.padLeftChar = padLeftChar;
		}

		const newlineMode = normalization.newlineMode?.trim();
		if (newlineMode && newlineMode !== 'Preserve') {
			result.newlineMode = newlineMode;
		}

		const numberFormat = normalization.numberFormat?.trim();
		if (numberFormat) {
			result.numberFormat = numberFormat;
		}

		const dateFormat = normalization.dateFormat?.trim();
		if (dateFormat) {
			result.dateFormat = dateFormat;
		}

		return Object.keys(result).length ? result : null;
	}

	private cloneNormalizationConfiguration(normalization: MappingRuleNormalization | null): MappingRuleNormalization | null {
		if (!normalization) {
			return null;
		}

		const result: MappingRuleNormalization = {
			trimWhitespace: normalization.trimWhitespace === true ? true : null,
			emptyStringToNull: normalization.emptyStringToNull === true ? true : null,
			truncateToMaxLength: normalization.truncateToMaxLength === true ? true : null,
			maxLength: this.isPositiveInteger(normalization.maxLength) ? Number(normalization.maxLength) : null,
			truncateSuffix: normalization.truncateSuffix ?? null,
			padLeft: normalization.padLeft === true ? true : null,
			padLeftToLength: this.isPositiveInteger(normalization.padLeftToLength) ? Number(normalization.padLeftToLength) : null,
			padLeftChar: normalization.padLeftChar?.charAt(0) || null,
			newlineMode: normalization.newlineMode?.trim() || null,
			numberFormat: normalization.numberFormat?.trim() || null,
			dateFormat: normalization.dateFormat?.trim() || null
		};

		return Object.values(result).some(value => value !== null && value !== false && value !== '')
			? result
			: null;
	}

	private isPositiveInteger(value: number | string | null | undefined): boolean {
		const numericValue = Number(value);
		return Number.isInteger(numericValue) && numericValue > 0;
	}

	private isNestedUnderChildMappingField(fields: MappingFieldSchema[], path: string | null | undefined): boolean {
		const normalizedPath = path?.trim();
		if (!normalizedPath) {
			return false;
		}

		for (const field of this.flattenSchema(fields)) {
			if ((!field.isChildObject && !field.isCollection) || !field.path) {
				continue;
			}

			const normalizedFieldPath = field.path.trim();
			if (!normalizedFieldPath) {
				continue;
			}

			if (field.isCollection) {
				if (normalizedPath.startsWith(`${normalizedFieldPath}[].`) || normalizedPath.startsWith(`${normalizedFieldPath}.`)) {
					return true;
				}

				continue;
			}

			if (normalizedPath.startsWith(normalizedFieldPath + '.')) {
				return true;
			}
		}

    return false;
  }

  private getDefaultAllowedModes(
    field: MappingFieldSchema,
    operation: 'create' | 'update'
  ): MappingRuleMode[] {
    if (field.isCollection || field.isChildObject || field.isKey) {
      return ['Ignore'];
    }

    const isWritableForOperation = operation === 'create' ? field.canCreate : field.canUpdate;

    if (field.isReadOnly || !isWritableForOperation) {
      return ['Ignore'];
    }

    if (field.isReference) {
      return ['Ignore', 'Lookup', 'Constant', 'Expression'];
    }

    return ['Ignore', 'SourceField', 'Constant', 'Expression'];
  }

  private isFieldRequiredForOperation(
    field: MappingFieldSchema,
    operation: 'create' | 'update'
  ): boolean {
    const hasExplicitOperationRequirements = field.isRequiredOnCreate || field.isRequiredOnUpdate;

    if (operation === 'create') {
      return hasExplicitOperationRequirements ? field.isRequiredOnCreate : field.isRequired;
    }

    return hasExplicitOperationRequirements ? field.isRequiredOnUpdate : false;
  }

  private getExpressionCategoryLabel(category: ExpressionToolCategory): string {
    switch (category) {
      case 'NullConditional':
        return 'Null / Conditional';
      case 'DateTime':
        return 'Date / Time';
      case 'CollectionAggregate':
        return 'Collection / Aggregate';
			case 'Custom':
				return 'Custom';
      default:
        return category;
    }
  }

  private captureExpressionSelection(): void {
    const textarea = this.resolveExpressionTextArea();
    if (!textarea) {
      return;
    }

    this.expressionSelectionStart = textarea.selectionStart ?? 0;
    this.expressionSelectionEnd = textarea.selectionEnd ?? this.expressionSelectionStart;
  }

  private resolveExpressionTextArea(): HTMLTextAreaElement | null {
    if (this.expressionTextAreaElement?.isConnected) {
      return this.expressionTextAreaElement;
    }

    const active = document.activeElement;
    if (active instanceof HTMLTextAreaElement) {
      this.expressionTextAreaElement = active;
      return active;
    }

    const popupTextArea = document.querySelector('.dx-popup-wrapper .dx-textarea textarea');
    if (popupTextArea instanceof HTMLTextAreaElement) {
      this.expressionTextAreaElement = popupTextArea;
      return popupTextArea;
    }

    return null;
  }

  private onExpressionKeyDown(e: KeyboardEvent): void {
    this.captureExpressionSelection();

    if (e.ctrlKey && e.key === '.') {
      e.preventDefault();
      e.stopPropagation();
      this.openExpressionSuggestions();
      return;
    }

    if (!this.expressionSuggestionsVisible) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.expressionSuggestions.length > 0) {
        this.expressionSuggestionIndex =
          (this.expressionSuggestionIndex + 1) % this.expressionSuggestions.length;
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.expressionSuggestions.length > 0) {
        this.expressionSuggestionIndex =
          (this.expressionSuggestionIndex - 1 + this.expressionSuggestions.length) % this.expressionSuggestions.length;
      }
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      if (this.expressionSuggestions.length > 0) {
        e.preventDefault();
        this.applyExpressionSuggestion(this.expressionSuggestions[this.expressionSuggestionIndex]);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeExpressionSuggestions();
      return;
    }

    setTimeout(() => {
      this.captureExpressionSelection();
      this.updateExpressionSuggestions();
    }, 0);
  }

  private openExpressionSuggestions(): void {
    this.updateExpressionSuggestions(true);
  }

  private closeExpressionSuggestions(): void {
    this.expressionSuggestionsVisible = false;
    this.expressionSuggestions = [];
    this.expressionSuggestionIndex = 0;
  }

  private getAutocompleteToken(value: string, cursorIndex: number): string | null {
    const left = value.substring(0, cursorIndex);

    const bracketMatch = left.match(/\[([A-Za-z0-9._]*)$/);
    if (bracketMatch) {
      return bracketMatch[1] ?? '';
    }

    const wordMatch = left.match(/([A-Za-z0-9._]+)$/);
    if (wordMatch) {
      return wordMatch[1] ?? '';
    }

    return null;
  }

  private updateExpressionSuggestions(forceOpen = false): void {
    const textarea = this.resolveExpressionTextArea();
    if (!textarea) {
      this.closeExpressionSuggestions();
      return;
    }

    const cursorIndex = textarea.selectionStart ?? 0;
    const value = textarea.value ?? this.expressionDraft ?? '';
    const token = this.getAutocompleteToken(value, cursorIndex);

    if (!forceOpen && !token) {
      this.closeExpressionSuggestions();
      return;
    }

    const query = (token ?? '').toLowerCase();

    const fieldSuggestions = this.sourceFieldOptions
      .map(field => ({
        score: this.getSuggestionScore(query, field.path, field.label),
        item: {
          kind: 'field' as const,
          label: `[${field.path}]`,
          subLabel: field.label,
          insertText: `[${field.path}]`,
          field
        }
      }))
      .filter(x => x.score < 999)
      .sort((a, b) => a.score - b.score || a.item.label.localeCompare(b.item.label))
      .slice(0, 6)
      .map(x => x.item);

    const toolSuggestions = this.expressionTools
      .map(tool => ({
        score: this.getSuggestionScore(query, tool.label, tool.description, tool.searchText, tool.template),
        item: {
          kind: 'tool' as const,
          label: tool.label,
          subLabel: tool.description,
          insertText: tool.template,
          tool
        }
      }))
      .filter(x => x.score < 999)
      .sort((a, b) => a.score - b.score || a.item.label.localeCompare(b.item.label))
      .slice(0, 6)
      .map(x => x.item);

    this.expressionSuggestions = [...fieldSuggestions, ...toolSuggestions].slice(0, 8);
    this.expressionSuggestionIndex = 0;
    this.expressionSuggestionsVisible = this.expressionSuggestions.length > 0;
  }

  private getSuggestionScore(query: string, ...values: string[]): number {
    if (!query) {
      return 100;
    }

    const q = query.toLowerCase();
    const normalized = values.map(x => (x ?? '').toLowerCase());

    if (normalized.some(x => x === q)) {
      return 0;
    }

    if (normalized.some(x => x.startsWith(q))) {
      return 1;
    }

    if (normalized.some(x => x.endsWith(`.${q}`))) {
      return 2;
    }

    if (normalized.some(x => x.includes(q))) {
      return 3;
    }

    return 999;
  }

  private applyExpressionInsertion(snippet: string, selectPlaceholder: boolean): void {
    const textarea = this.resolveExpressionTextArea();
    const currentValue = textarea?.value ?? this.expressionDraft ?? '';
    const selectionStart = textarea?.selectionStart ?? this.expressionSelectionStart ?? currentValue.length;
    const selectionEnd = textarea?.selectionEnd ?? this.expressionSelectionEnd ?? selectionStart;

    const nextValue =
      currentValue.slice(0, selectionStart) +
      snippet +
      currentValue.slice(selectionEnd);

    this.expressionDraft = nextValue;
		this.resetExpressionTestResult();

    setTimeout(() => {
      const resolved = this.resolveExpressionTextArea();
      if (!resolved) {
        return;
      }

      const placeholderIndex = snippet.indexOf('...');
      let caretStart = selectionStart + snippet.length;
      let caretEnd = caretStart;

      if (selectPlaceholder && placeholderIndex >= 0) {
        caretStart = selectionStart + placeholderIndex;
        caretEnd = caretStart + 3;
      }

      resolved.focus();
      resolved.setSelectionRange(caretStart, caretEnd);
      this.expressionSelectionStart = caretStart;
      this.expressionSelectionEnd = caretEnd;
      this.updateExpressionSuggestions();
    }, 0);
  }
}
